import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CheckCircle2, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  COMPOSITION_LENSES,
  PORTFOLIO_SCOPE_TOOLTIP,
  SETUP_CATEGORIES,
  SETUP_LABELS,
  STATUS_LABELS,
  STRENGTH_BAND_CHIP_BORDER,
  STRENGTH_BAND_ORDER,
  STRENGTH_BAND_STRIP_ORDER,
  accountContactIdsByOwnerBand,
  accountContactsByStrongestBand,
  accountEvidenceBands,
  accountMetrics,
  accountPairsByStrengthBand,
  accountStrengthBands,
  alignmentFunnel,
  buildOwnerPortfolioAccounts,
  buildOwnerStats,
  buildReadinessByAccount,
  buildGapItems,
  buildRiskItems,
  naOwnerMatch,
  compositionAccountsStatus,
  compositionNaView,
  compositionOpensStrength,
  compositionTargetView,
  compositionUnitTooltip,
  compositionValues,
  coverageKpis,
  coverageRecordFilterActive,
  coverageStatusPillClass,
  effectiveStatus,
  evidenceBandLabel,
  filteredAttention,
  filteredRows,
  formatMetricPct,
  gapRows,
  heatClass,
  heatRecordBadgeLabel,
  isEvidenceBandKey,
  KEY_MEDDPICC_ROLES,
  FAN_IN_THRESHOLD,
  nextOwnersSort,
  ownerBands,
  ownerPortfolioDefaultOpenIds,
  ownerPortfolioMatch,
  ownerPortfolioRowMatch,
  ownerPortfolioUsesRowFilter,
  OWNERS_PORTFOLIO_HIT_COL,
  PANEL_READINESS_CATEGORIES,
  pluralUnit,
  quadrantLabel,
  rowMatchesEvidenceBand,
  scopeRows,
  setupSummaryText,
  sortOwnerPortfolioAccounts,
  sortOwnerStats,
} from "./coverageModel";
import type {
  AccountReadiness,
  AttentionItem,
  CoverageAccount,
  CoverageFilters,
  CoverageRow,
  EvidenceBandKey,
  GradeCapacity,
  OwnerCapacity,
  OwnerPortfolioAccountStats,
  OwnerPortfolioSortKey,
  OwnersMode,
  OwnersPortfolioFilter,
  OwnersSortKey,
  OwnersSortState,
} from "./coverageModel";
import NeedsAttentionPane from "./NeedsAttentionPane";
import Badge from "../../Components/Badge";
import Button from "../../Components/Button";
import CarouselNavigation from "../../Components/CarouselNavigation";
import DropdownSelector from "../../Components/DropdownSelector";
import { MetricCard } from "../../Components/MetricCards";
import CommonModal from "../../Components/Modal";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { useToast } from "../../Components/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearCoverageFilter,
  clearCoverageFilters,
  clearCoverageFocus,
  drillAccountFocus,
  drillFromComposition,
  fetchPortfolioCoverage,
  openAccountsSorted,
  openNotSetUpAccounts,
  openOwnersSummary,
  openSingleThreaded,
  openUnassessedRelationships,
  restoreCoverageFocus,
  returnToComposition,
  setAccountDetail,
  setAccountModalSlide,
  setCapacitySettingsOpen,
  setCompositionLens,
  setCoverageAccountsMode,
  sortCoverageAccounts,
  setCoverageFilter,
  setCoverageOwnersGridSort,
  setCoverageOwnersMode,
  setCoverageOwnersSort,
  setCoverageReturnContext,
  setCoverageStatusView,
  setCoverageView,
  setLinkedCompositionKey,
  setMapFocus,
  setOwnerPortfolio,
  setOwnerPortfolioFilter,
  setRecordsPanel,
  setReadinessSegment,
  ownersViewInGrid,
  clearNaFocus,
  openNeedsAttention,
  setNaGaps,
  setNaRisks,
  setNaView,
  toggleOwnerPortfolioFilter,
} from "../../Store/Reducers/CoverageSlice";
import { invalidateRelationshipMatrixCache } from "../../Store/Reducers/RelationshipSlice";
import { applyDepartureImpact } from "../../Store/Reducers/TeamViewsSlice";
import { fetchVendorAdminGrades } from "../../Store/Reducers/VendorAdminSlice";
import {
  COVERAGE_OPS_PATH,
  RELATIONSHIP_ROUTE_PATH,
  ROLE_DISPLAY_NAMES,
  TEAM_VIEWS_ROUTE_PATH,
  getAccountTeamRoleDisplayLabel,
} from "../../Utils/Constants/RoleConstants";
import type { RoleType } from "../../Utils/Constants/RoleConstants";
import { orgPath } from "../../Utils/Helper/orgPath";
import { DEFAULT_FAN_OUT_CAP } from "../../Utils/Helper/rlFanCaps";
import RelationshipAccountsLoader from "../Relationship/RelationshipAccountsLoader";
import { useTeamViewsPrivilegedAccess } from "../Relationship/team-views/teamViewsAccess";

/* ------------------------------------------------------------------ */
/*  Type aliases                                                       */
/* ------------------------------------------------------------------ */

type CoverageView = "overview" | "accounts" | "owners" | "gaps" | "attention";

/* ------------------------------------------------------------------ */
/*  Static option lists                                                */
/* ------------------------------------------------------------------ */

const VIEW_TABS: { id: CoverageView; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "accounts", label: "Accounts" },
  { id: "owners", label: "Owners" },
  { id: "attention", label: "Needs Attention" },
];

/** Min plot height; grows with row stretch so the map fills beside Portfolio composition. */
const HEALTH_MAP_PLOT_MIN_PX = 320;

/** Inset account dots from plot edges so they do not cover quadrant titles. */
function healthMapX(completePct: number | null) {
  return 16 + (completePct ?? 0) * 0.68;
}

function healthMapY(strongPct: number | null) {
  return 20 + (100 - (strongPct ?? 0)) * 0.6;
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

function nameOf(accounts: { account_id: string; name: string }[], id: string) {
  return (
    accounts.find(function (a) {
      return a.account_id === id;
    })?.name || "Account"
  );
}

function uniqueFilterOptions(
  rows: CoverageRow[],
  idKey: "contact_role" | "owner_id",
  labelKey: "contact_role_label" | "owner_name"
): { value: string; label: string }[] {
  const m = new Map<string, string>();
  rows.forEach(function (row) {
    const id = row[idKey];
    if (!id) return;
    m.set(id, row[labelKey] || id);
  });
  return Array.from(m.entries())
    .map(function (entry) {
      return { value: entry[0], label: entry[1] };
    })
    .sort(function (a, b) {
      return a.label.localeCompare(b.label);
    });
}

/** Prototype subtitle: Exec GL5 when a grade is known, otherwise the role label. */
function formatOwnerGradeLine(
  role: string | null | undefined,
  grade: number | null | undefined,
  gradeName?: string | null
): string {
  if (grade != null && Number.isFinite(Number(grade)) && Number(grade) >= 1) {
    const name = String(gradeName || "").trim();
    return name ? "Exec GL" + grade + " (" + name + ")" : "Exec GL" + grade;
  }
  return formatCoverageOwnerRole(role);
}
function formatCoverageOwnerRole(role: string | null | undefined): string {
  const raw = String(role || "").trim();
  if (!raw) return "Team member";
  const normalized = raw.toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");
  const platformLabel = ROLE_DISPLAY_NAMES[normalized as RoleType];
  if (platformLabel) return platformLabel;
  const atmLabel = getAccountTeamRoleDisplayLabel(raw);
  if (atmLabel !== raw) return atmLabel;
  if (normalized === "c_level" || normalized === "clevel") return "C-Level";
  if (normalized === "sponsor" || normalized === "executive_sponsor") {
    return "Executive Sponsor";
  }
  if (normalized === "executive") return "Executive";
  return raw.replace(/_/g, " ").replace(/\b\w/g, function (ch) {
    return ch.toUpperCase();
  });
}

/* ------------------------------------------------------------------ */
/*  MetricInfo — tooltip pattern matching LevelMixStrip               */
/* ------------------------------------------------------------------ */

const metricInfoClass =
  "z-[9999] max-w-[280px] border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-normal normal-case tracking-normal leading-relaxed text-gray-700 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";

function MetricInfo({ label, text }: { label: string; text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-default items-center justify-center border-0 bg-transparent p-0 leading-none text-gray-400 transition-colors hover:text-purple-600 focus:outline-none focus-visible:text-purple-600 data-[state=open]:text-purple-600"
          style={{ width: "1em", height: "1em" }}
          aria-label={"About " + label}
          onClick={function (e) {
            e.stopPropagation();
          }}
        >
          <Info
            aria-hidden
            strokeWidth={2.25}
            className="block"
            style={{ width: "1em", height: "1em" }}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={6}
        className={metricInfoClass}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/* ------------------------------------------------------------------ */
/*  Coverage health map — cluster fan-out (prototype spider view)      */
/* ------------------------------------------------------------------ */

const MAP_DOT_COLORS = [
  "#974ae4",
  "#00A1E0",
  "#F5A623",
  "#81B53A",
  "#1A7F4B",
  "#7B61FF",
  "#F22F46",
  "#0061D5",
  "#FF7A59",
  "#635BFF",
  "#F06A6A",
  "#0EA5E9",
];

type MapPoint = {
  id: string;
  name: string;
  code: string;
  color: string;
  x: number;
  y: number;
  quadrant: string;
  gaps: number;
  pending: number;
  m: ReturnType<typeof accountMetrics>;
};

type MapCluster = {
  x: number;
  y: number;
  members: MapPoint[];
};

function accountMapCode(id: string, name: string, used: Set<string>) {
  const clean = (name || id).replace(/[^A-Za-z]/g, "").toUpperCase() || "AC";
  const candidates = [
    clean.slice(0, 2),
    clean.slice(0, 3),
    clean[0] + (clean[2] || clean[1] || "X"),
  ];
  for (let i = 0; i < candidates.length; i++) {
    if (candidates[i].length >= 2 && !used.has(candidates[i])) {
      used.add(candidates[i]);
      return candidates[i];
    }
  }
  let n = 1;
  while (used.has(clean.slice(0, 2) + String(n))) n += 1;
  const code = clean.slice(0, 2) + String(n);
  used.add(code);
  return code;
}

function colorForAccount(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return MAP_DOT_COLORS[hash % MAP_DOT_COLORS.length];
}

function buildMapClusters(points: MapPoint[]): MapCluster[] {
  const clusters: MapCluster[] = [];
  points.forEach(function (p) {
    const match = clusters.find(function (c) {
      return Math.abs(c.x - p.x) < 3.5 && Math.abs(c.y - p.y) < 5.5;
    });
    if (match) {
      match.members.push(p);
      match.x =
        match.members.reduce(function (s, q) {
          return s + q.x;
        }, 0) / match.members.length;
      match.y =
        match.members.reduce(function (s, q) {
          return s + q.y;
        }, 0) / match.members.length;
    } else {
      clusters.push({ x: p.x, y: p.y, members: [p] });
    }
  });
  return clusters;
}

function fanOffsets(
  n: number,
  hubLeftPx: number,
  hubTopPx: number,
  plotW: number,
  plotH: number
): { dx: number; dy: number }[] {
  const dot = 28;
  const r = Math.max(20, Math.ceil(((dot + 5) * n) / (2 * Math.PI)));
  const margin = r + dot / 2 + 3;
  const ax = hubLeftPx;
  const ay = hubTopPx;
  const cx = Math.min(Math.max(ax, margin), plotW - margin) - ax;
  const cy = Math.min(Math.max(ay, margin), plotH - margin) - ay;
  const start = n === 2 ? Math.PI : -Math.PI / 2;
  const pts: { dx: number; dy: number }[] = [];
  for (let i = 0; i < n; i++) {
    const a = start + (i * 2 * Math.PI) / n;
    pts.push({ dx: cx + r * Math.cos(a), dy: cy + r * Math.sin(a) });
  }
  return pts;
}

function MapDotButton(props: {
  point: MapPoint;
  style: React.CSSProperties;
  title?: string;
  highlighted?: boolean;
  dimmed?: boolean;
  onOpen: (id: string) => void;
  onHover?: (id: string | null) => void;
}) {
  const { point, style, title, highlighted, dimmed, onOpen, onHover } = props;
  return (
    <button
      type="button"
      className={
        "absolute z-[1] flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-white text-[8px] font-extrabold text-white shadow-[0_0_0_1px_#d1d5db] transition-transform hover:z-[3] hover:scale-110" +
        (highlighted
          ? " z-[4] scale-125 ring-2 ring-amber-400 ring-offset-1"
          : "") +
        (dimmed ? " opacity-25" : "") +
        (point.gaps
          ? " shadow-[0_0_0_2px_#ef4444]"
          : point.pending
            ? " shadow-[0_0_0_2px_#f59e0b]"
            : "")
      }
      style={{ ...style, background: point.color }}
      title={title}
      aria-label={title}
      onMouseEnter={function () {
        if (onHover) onHover(point.id);
      }}
      onMouseLeave={function () {
        if (onHover) onHover(null);
      }}
      onFocus={function () {
        if (onHover) onHover(point.id);
      }}
      onBlur={function () {
        if (onHover) onHover(null);
      }}
      onClick={function (e) {
        e.stopPropagation();
        onOpen(point.id);
      }}
    >
      {point.code}
    </button>
  );
}

function HealthMapPlot(props: {
  points: MapPoint[];
  onOpenAccount: (id: string) => void;
  focusAccountId?: string | null;
  foundAccountId?: string | null;
}) {
  const { points, onOpenAccount, focusAccountId, foundAccountId } = props;
  const plotRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [activeCluster, setActiveCluster] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);
  const [hoverAccountId, setHoverAccountId] = useState<string | null>(null);
  const [cardPos, setCardPos] = useState({ left: 0, top: 0 });
  const hubRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const clusters = useMemo(
    function () {
      return buildMapClusters(points);
    },
    [points]
  );

  useLayoutEffect(
    function () {
      const el = plotRef.current;
      if (!el) return;
      const measure = function () {
        setSize({ w: el.clientWidth, h: el.clientHeight });
      };
      measure();
      const ro =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(measure)
          : null;
      if (ro) ro.observe(el);
      window.addEventListener("resize", measure);
      return function () {
        if (ro) ro.disconnect();
        window.removeEventListener("resize", measure);
      };
    },
    [points.length]
  );

  useEffect(function () {
    const onKey = function (e: KeyboardEvent) {
      if (e.key === "Escape") {
        setActiveCluster(null);
        setPinned(false);
        setHoverAccountId(null);
      }
    };
    const onDocClick = function (e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        !t.closest("[data-cw-cluster]") &&
        !t.closest("[data-cw-cluster-card]")
      ) {
        setActiveCluster(null);
        setPinned(false);
        setHoverAccountId(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onDocClick);
    return function () {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onDocClick);
    };
  }, []);

  const showCluster = function (index: number, pin: boolean) {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setActiveCluster(index);
    if (pin) setPinned(true);
    const hub = hubRefs.current[index];
    if (hub) {
      const rect = hub.getBoundingClientRect();
      const cluster = clusters[index];
      const n = cluster.members.length;
      const reach =
        Math.max(20, Math.ceil(((28 + 5) * n) / (2 * Math.PI))) + 24;
      const cw = 290;
      const ch = Math.min(window.innerHeight * 0.7, 48 + n * 52);
      const hx = rect.left + rect.width / 2;
      const hy = rect.top + rect.height / 2;
      let left = hx + reach;
      if (left + cw > window.innerWidth - 8) left = hx - reach - cw;
      setCardPos({
        left: Math.max(8, left),
        top: Math.max(8, Math.min(hy - ch / 2, window.innerHeight - ch - 8)),
      });
    }
  };

  const hideSoon = function () {
    if (pinned) return;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(function () {
      setActiveCluster(null);
      setHoverAccountId(null);
    }, 300);
  };

  const activeMembers =
    activeCluster != null ? clusters[activeCluster]?.members || [] : [];
  const sharedSummary = (function () {
    if (!activeMembers.length) return "";
    const first = activeMembers[0];
    const same = activeMembers.every(function (p) {
      return p.m.complete === first.m.complete && p.m.strong === first.m.strong;
    });
    if (same) {
      return (
        "Contact coverage " +
        first.m.complete +
        "% · Strong RL4–5 " +
        first.m.strong +
        "% · " +
        first.quadrant
      );
    }
    return "Nearby positions — values differ slightly";
  })();

  return (
    <>
      <div
        ref={plotRef}
        className="relative h-full w-full overflow-hidden rounded-lg border border-gray-200"
        style={{
          minHeight: HEALTH_MAP_PLOT_MIN_PX,
          backgroundImage: [
            "linear-gradient(to right, rgba(254,242,242,0.56) 0 49.7%, transparent 49.7%)",
            "linear-gradient(to bottom, rgba(240,253,244,0.45) 0 49.7%, rgba(255,251,235,0.34) 49.7% 100%)",
            "linear-gradient(to right, transparent 49.7%, #e5e7eb 50%, transparent 50.3%)",
            "linear-gradient(to bottom, transparent 49.7%, #e5e7eb 50%, transparent 50.3%)",
          ].join(", "),
          backgroundColor: "#fff",
        }}
      >
        <span className="pointer-events-none absolute left-2 top-2 z-0 max-w-[32%] text-[7.5px] leading-[1.25] text-gray-600">
          <strong className="mb-0.5 block text-[8.5px] leading-tight text-gray-800">
            Strong but narrow
          </strong>
          Good relationships; incomplete contact footprint
        </span>
        <span className="pointer-events-none absolute right-2 top-2 z-0 max-w-[32%] text-right text-[7.5px] leading-[1.25] text-gray-600">
          <strong className="mb-0.5 block text-[8.5px] leading-tight text-gray-800">
            Healthy coverage
          </strong>
          Broad coverage with strong relationship evidence
        </span>
        <span className="pointer-events-none absolute bottom-2 left-2 z-0 max-w-[32%] text-[7.5px] leading-[1.25] text-gray-600">
          <strong className="mb-0.5 block text-[8.5px] leading-tight text-gray-800">
            Critical / exposed
          </strong>
          Incomplete footprint with weak relationship evidence
        </span>
        <span className="pointer-events-none absolute bottom-2 right-2 z-0 max-w-[32%] text-right text-[7.5px] leading-[1.25] text-gray-600">
          <strong className="mb-0.5 block text-[8.5px] leading-tight text-gray-800">
            Broad but weak
          </strong>
          Many contacts covered; relationship strength is limited
        </span>

        {clusters.map(function (cluster, index) {
          if (cluster.members.length === 1) {
            const p = cluster.members[0];
            const tip =
              p.name +
              " · " +
              p.quadrant +
              " · Contact coverage " +
              formatMetricPct(p.m.complete) +
              " (" +
              p.m.coveredCount +
              "/" +
              p.m.total +
              ") · Strong RL4–5 evidence " +
              formatMetricPct(p.m.strong) +
              " (" +
              p.m.strongCount +
              "/" +
              p.m.coveredCount +
              " covered contacts)" +
              (p.m.unassessedCount
                ? " · " +
                  p.m.unassessedCount +
                  " covered contact" +
                  (p.m.unassessedCount === 1 ? "" : "s") +
                  " not yet assessed"
                : "");
            return (
              <MapDotButton
                key={p.id}
                point={p}
                title={tip}
                highlighted={p.id === foundAccountId}
                dimmed={Boolean(focusAccountId && focusAccountId !== p.id)}
                style={{ left: p.x + "%", top: p.y + "%" }}
                onOpen={onOpenAccount}
              />
            );
          }

          const hubLeftPx = (cluster.x / 100) * size.w;
          const hubTopPx = (cluster.y / 100) * size.h;
          const offsets =
            size.w > 0
              ? fanOffsets(
                  cluster.members.length,
                  hubLeftPx,
                  hubTopPx,
                  size.w,
                  size.h
                )
              : cluster.members.map(function () {
                  return { dx: 0, dy: 0 };
                });

          return (
            <div
              key={"cluster-" + index}
              data-cw-cluster={index}
              className="absolute z-[2] h-0 w-0"
              style={{ left: cluster.x + "%", top: cluster.y + "%" }}
              onMouseEnter={function () {
                showCluster(index, false);
              }}
              onMouseLeave={hideSoon}
            >
              <svg
                className="pointer-events-none absolute left-0 top-0 overflow-visible"
                width="1"
                height="1"
                aria-hidden
              >
                {offsets.map(function (o, i) {
                  return (
                    <line
                      key={cluster.members[i].id}
                      x1="0"
                      y1="0"
                      x2={o.dx}
                      y2={o.dy}
                      stroke="#94a3b8"
                      strokeWidth="1"
                      strokeDasharray="2 2"
                    />
                  );
                })}
              </svg>
              <button
                type="button"
                ref={function (el) {
                  hubRefs.current[index] = el;
                }}
                className="absolute left-0 top-0 z-[1] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-slate-500 bg-white hover:scale-125 hover:border-[#974ae4]"
                aria-label={
                  cluster.members.length +
                  " accounts at this position — show list"
                }
                onClick={function (e) {
                  e.stopPropagation();
                  if (pinned && activeCluster === index) {
                    setPinned(false);
                    setActiveCluster(null);
                    return;
                  }
                  showCluster(index, true);
                }}
              />
              {cluster.members.map(function (p, i) {
                return (
                  <MapDotButton
                    key={p.id}
                    point={p}
                    highlighted={
                      hoverAccountId === p.id || p.id === foundAccountId
                    }
                    style={{
                      left: offsets[i].dx,
                      top: offsets[i].dy,
                    }}
                    onOpen={onOpenAccount}
                    onHover={setHoverAccountId}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {activeCluster != null && activeMembers.length > 1 ? (
        <div
          data-cw-cluster-card
          role="dialog"
          className="fixed z-[1000] max-h-[70vh] w-[290px] overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-[0_12px_30px_rgba(15,23,42,0.18)]"
          style={{ left: cardPos.left, top: cardPos.top }}
          onMouseEnter={function () {
            if (hideTimer.current) clearTimeout(hideTimer.current);
          }}
          onMouseLeave={hideSoon}
        >
          <div className="mb-1 border-b border-gray-100 px-1.5 pb-2">
            <strong className="block text-[11.5px] text-gray-900">
              {activeMembers.length} accounts at this spot
            </strong>
            <span className="mt-0.5 block text-[9.5px] text-gray-500">
              {sharedSummary}
            </span>
          </div>
          {activeMembers.map(function (p) {
            const highlighted = hoverAccountId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={
                  "grid w-full grid-cols-[30px_minmax(0,1fr)] items-center gap-x-2 rounded-md px-1.5 py-1.5 text-left " +
                  (highlighted ? "bg-indigo-50" : "hover:bg-indigo-50")
                }
                onMouseEnter={function () {
                  setHoverAccountId(p.id);
                }}
                onMouseLeave={function () {
                  setHoverAccountId(null);
                }}
                onClick={function () {
                  setActiveCluster(null);
                  setPinned(false);
                  onOpenAccount(p.id);
                }}
              >
                <span
                  className="row-span-2 flex h-[26px] w-[26px] items-center justify-center rounded-full text-[8px] font-extrabold text-white"
                  style={{ background: p.color }}
                >
                  {p.code}
                </span>
                <span className="text-[11px] font-bold text-gray-800">
                  {p.name}
                  {p.gaps ? (
                    <em className="ml-1.5 text-[9px] font-bold not-italic text-red-600">
                      lifecycle gap
                    </em>
                  ) : p.pending ? (
                    <em className="ml-1.5 text-[9px] font-bold not-italic text-amber-700">
                      pending handoff
                    </em>
                  ) : null}
                </span>
                <span className="text-[9.5px] text-gray-500">
                  {p.m.coveredCount}/{p.m.total} contacts covered ·{" "}
                  {p.m.strongCount}/{p.m.coveredCount} at RL4–5
                  {p.m.unassessedCount
                    ? " · " + p.m.unassessedCount + " not yet assessed"
                    : ""}
                </span>
              </button>
            );
          })}
          <div className="mt-1 border-t border-gray-100 px-1.5 pt-1.5 text-[9px] leading-snug text-gray-500">
            Dots fan out so every account is visible; the hub marks the shared
            position. Click an account to see its coverage details.
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Owner rollup (unchanged logic)                                     */
/* ------------------------------------------------------------------ */

type OwnerMatrixPerson = {
  id: string;
  name: string;
  role: string;
  onRoster: boolean;
  reactive: boolean;
  localCap: number | null;
  globalCap: number | null;
};

function isOpenDormant(row: CoverageRow) {
  return (
    row.lifecycle_state === "dormant" &&
    row.status !== "transferred" &&
    row.status !== "dismissed"
  );
}

function fanOutFor(
  ownerId: string,
  accountId: string,
  rows: CoverageRow[]
): number {
  let n = 0;
  rows.forEach(function (row) {
    if (
      row.lifecycle_state === "active" &&
      row.owner_id === ownerId &&
      row.account_id === accountId
    ) {
      n += 1;
    }
  });
  return n;
}

function globalFanOutFor(ownerId: string, rows: CoverageRow[]): number {
  let n = 0;
  rows.forEach(function (row) {
    if (row.lifecycle_state === "active" && row.owner_id === ownerId) {
      n += 1;
    }
  });
  return n;
}

function buildOwnerMatrixPeople(
  scopeRows: CoverageRow[],
  capacities: OwnerCapacity[],
  ownerFilter: string
): OwnerMatrixPerson[] {
  const capacityByOwner = new Map(
    capacities.map(function (item) {
      return [item.owner_id, item] as [string, OwnerCapacity];
    })
  );
  const people = new Map<string, OwnerMatrixPerson>();

  const ensure = function (
    ownerId: string,
    name: string | null | undefined,
    role: string | null | undefined,
    onRoster: boolean | undefined,
    reactive: boolean
  ) {
    const existing = people.get(ownerId);
    const capacity = capacityByOwner.get(ownerId);
    if (existing) {
      if (reactive) existing.reactive = true;
      if (onRoster) existing.onRoster = true;
      if (!existing.role && role) existing.role = role;
      if (existing.name === "Unnamed owner" && name) existing.name = name;
      return;
    }
    people.set(ownerId, {
      id: ownerId,
      name: name || capacity?.name || "Unnamed owner",
      role: role || capacity?.role || "",
      onRoster: Boolean(onRoster || capacity?.on_roster),
      reactive: reactive,
      localCap: capacity?.local_capacity ?? null,
      globalCap: capacity?.global_capacity ?? null,
    });
  };

  // Prototype: active owners in scope + reactive (open dormant) former owners.
  scopeRows.forEach(function (row) {
    if (!row.owner_id) return;
    if (row.lifecycle_state === "active") {
      ensure(
        row.owner_id,
        row.owner_name,
        row.owner_role,
        row.on_roster,
        false
      );
    } else if (isOpenDormant(row)) {
      ensure(row.owner_id, row.owner_name, row.owner_role, row.on_roster, true);
    }
  });

  // Also include approved account-team roster members even with zero mappings.
  capacities.forEach(function (cap) {
    if (ownerFilter !== "all" && cap.owner_id !== ownerFilter) return;
    if (people.has(cap.owner_id)) {
      const existing = people.get(cap.owner_id);
      if (!existing) return;
      if (cap.on_roster) existing.onRoster = true;
      if (!existing.role && cap.role) existing.role = cap.role;
      if (existing.name === "Unnamed owner" && cap.name)
        existing.name = cap.name;
      if (existing.localCap == null && cap.local_capacity != null) {
        existing.localCap = cap.local_capacity;
      }
      if (existing.globalCap == null && cap.global_capacity != null) {
        existing.globalCap = cap.global_capacity;
      }
      return;
    }
    if (!cap.on_roster) return;
    ensure(cap.owner_id, cap.name, cap.role, true, false);
  });

  if (ownerFilter !== "all") {
    Array.from(people.keys()).forEach(function (id) {
      if (id !== ownerFilter) people.delete(id);
    });
  }

  return Array.from(people.values()).sort(function (a, b) {
    const aLoad = globalFanOutFor(a.id, scopeRows);
    const bLoad = globalFanOutFor(b.id, scopeRows);
    if (bLoad !== aLoad) return bLoad - aLoad;
    return a.name.localeCompare(b.name);
  });
}

function scopedAccountColumns(
  scopeRows: CoverageRow[],
  accounts: CoverageAccount[]
): { id: string; name: string }[] {
  const ids = Array.from(
    new Set(
      scopeRows.map(function (row) {
        return row.account_id;
      })
    )
  );
  const nameById = new Map(
    accounts.map(function (account) {
      return [account.account_id, account.name] as [string, string];
    })
  );
  scopeRows.forEach(function (row) {
    if (!nameById.has(row.account_id)) {
      nameById.set(row.account_id, row.account_name);
    }
  });
  return ids
    .map(function (id) {
      return { id: id, name: nameById.get(id) || id };
    })
    .sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });
}

function capFor(ownerId: string, capacities: OwnerCapacity[]) {
  return capacities.find(function (c) {
    return c.owner_id === ownerId;
  });
}

function overloadCount(
  accountId: string,
  rows: CoverageRow[],
  capacities: OwnerCapacity[]
) {
  const ownerSet = new Set(
    rows
      .filter(function (row) {
        return (
          row.account_id === accountId &&
          row.lifecycle_state === "active" &&
          row.owner_id
        );
      })
      .map(function (row) {
        return row.owner_id as string;
      })
  );
  let count = 0;
  ownerSet.forEach(function (ownerId) {
    const cap = capFor(ownerId, capacities);
    const local = rows.filter(function (row) {
      return (
        row.owner_id === ownerId &&
        row.account_id === accountId &&
        row.lifecycle_state === "active"
      );
    }).length;
    if (cap && local > cap.local_capacity) count += 1;
  });
  return count;
}

/* ------------------------------------------------------------------ */
/*  CoverageWorkspace — main component                                */
/* ------------------------------------------------------------------ */

const FOCUS_VIEW_LABELS: Record<string, string> = {
  overview: "Overview",
  accounts: "Accounts",
  owners: "Owners",
  attention: "Needs Attention",
  gaps: "Needs Attention",
};

const CoverageWorkspace: React.FC = function CoverageWorkspace() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const coverage = useAppSelector(function (state) {
    return state.coverage;
  });
  const rows = coverage.rows;
  const accounts = coverage.accounts;
  const attentionItems = coverage.attentionItems;
  const ownerCapacities = coverage.ownerCapacities;
  const loading = coverage.loading;
  const error = coverage.error;
  const view = coverage.view;
  const filters = coverage.filters;
  const analysis = coverage.analysis;
  const compositionLens = coverage.compositionLens;
  const compositionReturn = coverage.compositionReturn;
  const accountDetailId = coverage.accountDetailId;
  const linkedCompositionKey = coverage.linkedCompositionKey;
  const capacityConfigured = coverage.capacityConfigured;
  const recordsPanel = coverage.recordsPanel;
  const mapFocus = coverage.mapFocus;
  const readinessSegment = coverage.readinessSegment;
  const ownerPortfolio = coverage.ownerPortfolio;
  const accountsMode = coverage.accountsMode;
  const accountsSort = coverage.accountsSort;
  const ownersFocus = coverage.ownersFocus;
  const ownersMode = coverage.ownersMode;
  const ownersSort = coverage.ownersSort;
  const ownersGridSort = coverage.ownersGridSort;
  const ownersHighlight = coverage.ownersHighlight;
  const capacityByGrade = coverage.capacityByGrade;
  const capacitySettingsOpen = coverage.capacitySettingsOpen;
  const naView = coverage.naView;
  const naGaps = coverage.naGaps;
  const naRisks = coverage.naRisks;
  const coverageFocus = coverage.coverageFocus;
  const activeFocus =
    coverageFocus && coverageFocus.view === view ? coverageFocus : null;

  const meddpiccOn = useAppSelector(function (state) {
    return state.vendorAdmin.rlSettingValue?.consider_meddpicc_role !== false;
  });

  const readinessByAccount = useMemo(
    function () {
      return buildReadinessByAccount(accounts, rows, {
        meddpiccOn: meddpiccOn,
        ownerCapacities: ownerCapacities,
      });
    },
    [accounts, rows, meddpiccOn, ownerCapacities]
  );

  useEffect(
    function () {
      dispatch(fetchPortfolioCoverage());
    },
    [dispatch]
  );

  /* derived data — unchanged logic */
  const scoped = useMemo(
    function () {
      return scopeRows(rows, filters);
    },
    [rows, filters]
  );
  const matching = useMemo(
    function () {
      return filteredRows(rows, filters);
    },
    [rows, filters]
  );
  const gaps = useMemo(
    function () {
      return gapRows(matching);
    },
    [matching]
  );
  const attention = useMemo(
    function () {
      return filteredAttention(attentionItems, filters, analysis);
    },
    [attentionItems, filters, analysis]
  );
  const owners = useMemo(
    function () {
      return buildOwnerMatrixPeople(scoped, ownerCapacities, filters.owner);
    },
    [scoped, ownerCapacities, filters.owner]
  );
  const ownerAccounts = useMemo(
    function () {
      return scopedAccountColumns(scoped, accounts);
    },
    [scoped, accounts]
  );

  /* navigation helpers */
  const openAccount = function (
    accountId: string,
    label: string,
    options?: {
      history?: boolean;
      contactId?: string;
      purpose?: "review" | "add-owner";
      ownerId?: string;
      highlightContactIds?: string[];
      relationshipView?: "map" | "matrix";
      vendorId?: string;
    }
  ) {
    dispatch(setCoverageReturnContext({ label: label }));
    const params = new URLSearchParams({
      tab: "account-relationships",
      accountId: accountId,
    });
    if (options?.history) params.set("openHistory", "1");
    if (options?.relationshipView)
      params.set("relationshipView", options.relationshipView);
    if (options?.vendorId) params.set("vendorId", options.vendorId);
    if (options?.contactId) {
      params.set("highlightContactId", options.contactId);
      params.set("highlightPurpose", options.purpose || "review");
    }
    if (options?.ownerId) params.set("highlightOwnerId", options.ownerId);
    if (options?.highlightContactIds?.length) {
      params.set("highlightContactIds", options.highlightContactIds.join(","));
    }
    navigate(orgPath(RELATIONSHIP_ROUTE_PATH) + "?" + params.toString());
  };

  /** Contacts + account team are managed on Account Workspace, not Relationships. */
  const openAccountWorkspace = function (accountId: string, label: string) {
    navigate(orgPath(`/account-workspace/${accountId}`), {
      state: {
        from: orgPath(COVERAGE_OPS_PATH),
        fromLabel: label || "Coverage",
      },
    });
  };

  const openTeamViews = function (name: string, label: string) {
    dispatch(setCoverageReturnContext({ label: label }));
    navigate(
      orgPath(TEAM_VIEWS_ROUTE_PATH) +
        "?q=" +
        encodeURIComponent(name) +
        "&view=by-person"
    );
  };

  const openAccountCoverageMap = function (
    accountId: string,
    ownerId: string,
    accountName: string,
    ownerName: string,
    portfolioFilter?: string | null
  ) {
    dispatch(
      setCoverageReturnContext({
        label: accountName + " coverage map · " + ownerName,
      })
    );
    const params = new URLSearchParams({
      tab: "account-relationships",
      accountId: accountId,
      relationshipView: "map",
      vendorId: ownerId,
      highlightOwnerId: ownerId,
      highlightName: ownerName,
    });
    if (portfolioFilter === "overCovered" || portfolioFilter === "only") {
      const ids = Array.from(
        new Set(
          rows
            .filter(function (r) {
              if (r.account_id !== accountId || r.owner_id !== ownerId)
                return false;
              if (r.lifecycle_state !== "active") return false;
              return portfolioFilter === "only"
                ? r.fan_in <= 1
                : r.fan_in >= FAN_IN_THRESHOLD;
            })
            .map(function (r) {
              return r.contact_id;
            })
        )
      );
      if (ids.length) params.set("highlightContactIds", ids.join(","));
      params.set(
        "highlightWhat",
        portfolioFilter === "only" ? "sole-owner" : "over-covered"
      );
    }
    navigate(orgPath(RELATIONSHIP_ROUTE_PATH) + "?" + params.toString());
  };

  const [departureImpact, setDepartureImpact] = useState<{
    ownerId: string;
    mode: "prospective" | "reactive";
  } | null>(null);

  const openOwnerPortfolio = function (
    ownerId: string,
    focusAccountId?: string | null,
    filter?: OwnersPortfolioFilter | null
  ) {
    const active = scoped.some(function (row) {
      return row.owner_id === ownerId && row.lifecycle_state === "active";
    });
    const dormant = scoped.some(function (row) {
      return row.owner_id === ownerId && isOpenDormant(row);
    });
    if (!active && !dormant) return;
    dispatch(
      setOwnerPortfolio({
        ownerId: ownerId,
        focusAccountId: focusAccountId ?? null,
        filter: filter ?? null,
      })
    );
  };

  const startDepartureImpact = function (
    ownerId: string,
    mode?: "prospective" | "reactive"
  ) {
    const hasReactive = rows.some(function (row) {
      return row.owner_id === ownerId && isOpenDormant(row);
    });
    const hasProspective = rows.some(function (row) {
      return row.owner_id === ownerId && row.lifecycle_state === "active";
    });
    const resolved =
      mode ||
      (hasReactive && !hasProspective
        ? "reactive"
        : hasProspective
          ? "prospective"
          : null);
    if (!resolved) return;
    dispatch(setOwnerPortfolio(null));
    setDepartureImpact({ ownerId: ownerId, mode: resolved });
  };

  const setFilter = function (key: keyof CoverageFilters, value: string) {
    dispatch(setCoverageFilter({ key: key, value: value }));
  };

  /* filter options */
  const accountOptions = accounts
    .map(function (a) {
      return { value: a.account_id, label: a.name };
    })
    .sort(function (a, b) {
      return a.label.localeCompare(b.label);
    });
  accountOptions.unshift({ value: "all", label: "All accounts" });

  const ownerOptions = uniqueFilterOptions(rows, "owner_id", "owner_name");
  ownerOptions.unshift({ value: "all", label: "All owners" });

  /* scope chips (dropdown filters + readiness segment from composition drills) */
  const chips: { key: keyof CoverageFilters | "readiness"; label: string }[] =
    [];
  if (filters.account !== "all")
    chips.push({ key: "account", label: "Account" });
  if (filters.status !== "all")
    chips.push({
      key: "status",
      label: isEvidenceBandKey(filters.status)
        ? "Relationship strength · " +
          (STATUS_LABELS[filters.status] || filters.status)
        : STATUS_LABELS[filters.status] || filters.status,
    });
  if (filters.role !== "all") chips.push({ key: "role", label: filters.role });
  if (filters.owner !== "all") chips.push({ key: "owner", label: "Owner" });
  if (readinessSegment && readinessSegment !== "all")
    chips.push({
      key: "readiness",
      label: STATUS_LABELS[readinessSegment] || readinessSegment,
    });

  const scopeAccounts = Array.from(
    new Set(
      scoped.map(function (r) {
        return r.account_id;
      })
    )
  ).length;
  const matchingAccounts = new Set(
    matching.map(function (row) {
      return row.account_id;
    })
  ).size;
  const filterNote = isEvidenceBandKey(filters.status)
    ? heatRecordBadgeLabel(filters.status, matching).replace(
        /^(\d+) /,
        "$1 matching "
      ) +
      " across " +
      matchingAccounts +
      " account" +
      (matchingAccounts === 1 ? "" : "s")
    : matching.length +
      " matching record" +
      (matching.length === 1 ? "" : "s") +
      " across " +
      matchingAccounts +
      " account" +
      (matchingAccounts === 1 ? "" : "s");

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        {/* --- Main tabs + clear --- */}
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <div className="min-w-0 w-fit shrink-0">
            <TabsComponent
              tabs={VIEW_TABS}
              activeTab={view}
              onTabChange={function (id: CoverageView) {
                dispatch(setCoverageView(id));
              }}
              variant="secondary-segmented"
              segmentedLayout="fit"
              className="mb-0 whitespace-nowrap rounded-full"
            />
          </div>
        </div>

        {/* --- Tab filters. Status still arrives from drills and shows on the blue bar. --- */}
        {view === "overview" ? null : (
          <div className="flex shrink-0 flex-wrap gap-2">
            {view === "owners" || view === "attention" || view === "gaps" ? (
              <div className="ml-2 w-[240px] shrink-0">
                <DropdownSelector
                  label="Owner"
                  selected={filters.owner}
                  options={ownerOptions}
                  onSelect={function (v) {
                    setFilter("owner", v);
                  }}
                  compact
                  fullWidth
                  searchable
                  searchPlaceholder="Find owner…"
                  enableScroll
                  maxHeight="240px"
                  triggerStyle="field"
                />
              </div>
            ) : null}
            {view === "accounts" || view === "attention" || view === "gaps" ? (
              <div className="ml-2 w-[240px] shrink-0">
                <DropdownSelector
                  label="Account"
                  selected={filters.account}
                  options={accountOptions}
                  onSelect={function (v) {
                    setFilter("account", v);
                  }}
                  compact
                  fullWidth
                  searchable
                  searchPlaceholder="Find account…"
                  enableScroll
                  maxHeight="240px"
                  triggerStyle="field"
                />
              </div>
            ) : null}
          </div>
        )}

        {/* --- Scope bar --- */}
        {view === "overview" ? (
          <p
            className="ml-2 shrink-0 text-xs text-gray-600"
            title="Contacts are counted once per account. Current relationships are owner–contact pairs with an active owner."
          >
            <strong>{accounts.length}</strong> accounts
            <span className="mx-1.5" aria-hidden>
              ·
            </span>
            <strong>
              {
                new Set(
                  rows.map(function (r) {
                    return r.account_id + ":" + r.contact_id;
                  })
                ).size
              }
            </strong>{" "}
            contacts
            <span className="mx-1.5" aria-hidden>
              ·
            </span>
            <strong>
              {
                rows.filter(function (r) {
                  return r.lifecycle_state === "active";
                }).length
              }
            </strong>{" "}
            current relationships
          </p>
        ) : activeFocus ? (
          <div className="ml-2 flex shrink-0 flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-950">
            <Button
              variant="outline-primary"
              className="!w-fit !text-xs"
              title={
                "Return to " +
                (FOCUS_VIEW_LABELS[activeFocus.fromView] || "Accounts") +
                " as you left it"
              }
              onClick={function () {
                dispatch(restoreCoverageFocus());
              }}
            >
              ← Back to {FOCUS_VIEW_LABELS[activeFocus.fromView] || "Accounts"}
            </Button>
            <strong>Focus</strong>
            <button
              type="button"
              title="Clear the focus and show this tab unfiltered"
              onClick={function () {
                dispatch(clearCoverageFocus());
              }}
            >
              <Badge
                type="primary-border"
                label={
                  "From " +
                  (FOCUS_VIEW_LABELS[activeFocus.fromView] || "Accounts") +
                  ": " +
                  activeFocus.label +
                  " ×"
                }
              />
            </button>
            <span className="text-slate-600">
              {view === "attention"
                ? (naView === "risks" ? attention.length : gaps.length) +
                  " matching " +
                  (naView === "risks" ? "risk item" : "gap item") +
                  ((naView === "risks" ? attention.length : gaps.length) === 1
                    ? ""
                    : "s") +
                  ". Clear all to show every account and owner again."
                : filterNote +
                  (view === "accounts"
                    ? ". The heatmap stays on this account after you close the list. Clear all to show every account again."
                    : ". Clear all to show this tab unfiltered.")}
            </span>
            <Button
              variant="clear"
              className="!ml-auto !w-fit !text-xs"
              onClick={function () {
                dispatch(clearCoverageFilters());
              }}
            >
              Clear all
            </Button>
          </div>
        ) : (
          <div
            className={
              "ml-2 flex shrink-0 flex-wrap items-center gap-2 text-xs " +
              (chips.length
                ? "rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-950"
                : "text-gray-600")
            }
          >
            <strong>{chips.length ? "Filtered by" : "Portfolio scope"}</strong>
            {chips.length ? null : (
              <span title={PORTFOLIO_SCOPE_TOOLTIP}>
                {scoped.length} coverage records across {scopeAccounts} accounts
                {view === "gaps" || view === "attention"
                  ? " · " +
                    (view === "attention" ? attention.length : gaps.length) +
                    " matching"
                  : ""}
              </span>
            )}
            {chips.map(function (chip) {
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={function () {
                    if (chip.key === "readiness") {
                      dispatch(setReadinessSegment("all"));
                      return;
                    }
                    dispatch(
                      clearCoverageFilter(chip.key as keyof CoverageFilters)
                    );
                  }}
                >
                  <Badge type="primary-border" label={chip.label + " ×"} />
                </button>
              );
            })}
            {chips.length ? (
              <span className="text-slate-600">{filterNote}</span>
            ) : null}
            {chips.length ? (
              <Button
                variant="clear"
                className="!ml-auto !w-fit !text-xs"
                onClick={function () {
                  dispatch(clearCoverageFilters());
                }}
              >
                Clear all
              </Button>
            ) : null}
          </div>
        )}

        {/* --- Composition return banner (white card + outline) --- */}
        {compositionReturn && view !== "overview" ? (
          <div className="flex shrink-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
            <span>
              From {compositionReturn.lensLabel} &middot;{" "}
              <strong>{compositionReturn.categoryLabel}</strong>
            </span>
            <Button
              variant="outline-primary"
              onClick={function () {
                dispatch(returnToComposition());
              }}
            >
              Back to composition
            </Button>
          </div>
        ) : null}

        {/* --- Content area --- */}
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-gray-200 bg-white p-4">
          {loading ? (
            <RelationshipAccountsLoader label="Loading portfolio coverage\u2026" />
          ) : null}
          {!loading && error ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-red-700">{error}</p>
              <Button
                variant="outline-primary"
                onClick={function () {
                  dispatch(fetchPortfolioCoverage());
                }}
              >
                Retry
              </Button>
            </div>
          ) : null}

          {!loading && !error && view === "overview" ? (
            <OverviewPane
              rows={rows}
              scoped={scoped}
              matching={matching}
              filters={filters}
              accounts={accounts}
              ownerCapacities={ownerCapacities}
              readinessByAccount={readinessByAccount}
              meddpiccOn={meddpiccOn}
              mapFocus={mapFocus}
              lensId={compositionLens}
              linkedKey={linkedCompositionKey}
              onStatus={function (status, next) {
                if (status === "open-orphaned") {
                  dispatch(
                    openNeedsAttention({
                      naView: "gaps",
                      gapType: "lifecycle,pending",
                      lifecycle: "orphaned",
                      focusLabel: "True orphans + pending handoffs",
                    })
                  );
                  return;
                }
                if (status === "single-owner") {
                  dispatch(openSingleThreaded());
                  return;
                }
                if (status === "coverage") {
                  dispatch(openAccountsSorted({ key: "complete", dir: "asc" }));
                  return;
                }
                dispatch(setCoverageStatusView({ status: status, view: next }));
              }}
              onOpenNotSetUp={function () {
                dispatch(openNotSetUpAccounts());
              }}
              onOpenOwnersView={function () {
                dispatch(openOwnersSummary());
              }}
              onOpenUnassessed={function () {
                dispatch(openUnassessedRelationships());
              }}
              onLens={function (id) {
                dispatch(setCompositionLens(id));
              }}
              onLink={function (key) {
                dispatch(setLinkedCompositionKey(key));
              }}
              onDrill={function (lens, categoryKey, categoryLabel) {
                const target = compositionTargetView(lens.id, categoryKey);
                const isReadiness = lens.id === "readiness";
                const compositionReturn = {
                  lensId: lens.id,
                  lensLabel: lens.label,
                  categoryKey: categoryKey,
                  categoryLabel: categoryLabel,
                };
                if (target === "attention") {
                  const na = compositionNaView(lens.id, categoryKey) || "gaps";
                  let gapType: string | undefined;
                  let lifecycle: "orphaned" | "covered" | undefined;
                  let riskType: string | undefined;
                  if (na === "gaps") {
                    if (
                      categoryKey === "not-set-up" ||
                      SETUP_CATEGORIES.includes(categoryKey as any)
                    ) {
                      gapType = "setup";
                    } else if (
                      categoryKey === "key-uncovered" ||
                      categoryKey === "other-unowned"
                    ) {
                      gapType = "unowned";
                    } else if (categoryKey === "baseline-unmapped") {
                      gapType = "unowned+orphaned";
                    } else if (categoryKey === "open-orphaned") {
                      gapType = "lifecycle";
                      lifecycle = "orphaned";
                    } else if (categoryKey === "open-covered") {
                      gapType = "lifecycle";
                      lifecycle = "covered";
                    } else if (categoryKey === "pending") {
                      gapType = "pending";
                    }
                  } else {
                    if (
                      categoryKey === "single-key" ||
                      categoryKey === "single-owner"
                    ) {
                      riskType = "single-owner";
                    } else if (categoryKey === "fan-in") {
                      riskType = "fan-in";
                    } else if (categoryKey === "strain") {
                      // Prototype readiness:strain opens capacity + fan-in; start on capacity.
                      riskType = "capacity";
                    }
                  }
                  dispatch(
                    openNeedsAttention({
                      naView: na,
                      gapType: gapType,
                      lifecycle: lifecycle,
                      riskType: riskType,
                      focusLabel: categoryLabel,
                      readinessSegment: isReadiness
                        ? categoryKey === "not-set-up" ||
                          SETUP_CATEGORIES.includes(categoryKey as any)
                          ? "not-set-up"
                          : categoryKey
                        : undefined,
                      compositionReturn: compositionReturn,
                    })
                  );
                  return;
                }
                // Continuity Current / Resilience 2+ / Evidence bands / readiness ready → Accounts.
                dispatch(
                  drillFromComposition({
                    lensId: lens.id,
                    lensLabel: lens.label,
                    categoryKey: categoryKey,
                    categoryLabel: categoryLabel,
                    view: target,
                    status: compositionAccountsStatus(lens.id, categoryKey),
                    readinessSegment:
                      isReadiness && categoryKey === "ready"
                        ? "ready"
                        : undefined,
                    accountsMode: compositionOpensStrength(lens.id)
                      ? "strength"
                      : "heatmap",
                  })
                );
              }}
              onOpenAccount={function (id) {
                dispatch(setAccountDetail(id));
              }}
              onOpenRelationships={function (id) {
                openAccount(id, nameOf(accounts, id));
              }}
              onClearMapFocus={function () {
                dispatch(setMapFocus(null));
              }}
            />
          ) : null}

          {!loading && !error && view === "accounts" ? (
            <AccountsPane
              accounts={accounts}
              rows={rows}
              scoped={scoped}
              matching={matching}
              filters={filters}
              ownerCapacities={ownerCapacities}
              readinessByAccount={readinessByAccount}
              readinessSegment={readinessSegment}
              meddpiccOn={meddpiccOn}
              recordsPanel={recordsPanel}
              scopeFocusLabel={activeFocus ? activeFocus.label : null}
              accountsMode={accountsMode}
              accountsSort={accountsSort}
              onAccountsMode={function (mode) {
                dispatch(setCoverageAccountsMode(mode));
              }}
              onSelect={function (id, slide) {
                dispatch(setAccountDetail(id));
                if (slide) dispatch(setAccountModalSlide(slide));
              }}
              onSetupCta={function (accountId, _category) {
                // All setup CTAs (Add contacts / Set up account team / Map relationships)
                // open Relationships Account Relationships — not Account Workspace.
                openAccount(
                  accountId,
                  nameOf(accounts, accountId) + " map relationships"
                );
              }}
              onOpenRelationships={function (accountId, contactId) {
                // Close band panel before leave so Back to Coverage matches View on Map.
                dispatch(setRecordsPanel(null));
                openAccount(
                  accountId,
                  nameOf(accounts, accountId) + " open matrix",
                  contactId
                    ? { contactId: contactId, purpose: "review" }
                    : undefined
                );
              }}
              onViewCoverageMap={function (accountId, contactId) {
                const accountName = nameOf(accounts, accountId);
                dispatch(setRecordsPanel(null));
                dispatch(
                  setCoverageReturnContext({
                    label: accountName + " coverage map",
                  })
                );
                const params = new URLSearchParams({
                  tab: "account-relationships",
                  accountId: accountId,
                  relationshipView: "map",
                });
                if (contactId) params.set("contactId", contactId);
                navigate(
                  orgPath(RELATIONSHIP_ROUTE_PATH) + "?" + params.toString()
                );
              }}
              onClearStatus={function () {
                dispatch(clearCoverageFilter("status"));
                dispatch(setReadinessSegment("all"));
                dispatch(setCoverageAccountsMode("heatmap"));
              }}
              onStrengthBand={function (band) {
                dispatch(setCoverageFilter({ key: "status", value: band }));
              }}
              onSortColumn={function (key) {
                dispatch(sortCoverageAccounts(key));
              }}
              onOpenAccountRisks={function (accountId, riskType) {
                dispatch(setRecordsPanel(null));
                dispatch(
                  openNeedsAttention({
                    naView: "risks",
                    riskType: riskType,
                    accountId: accountId,
                    focusLabel:
                      nameOf(accounts, accountId) +
                      " · " +
                      (riskType === "fan-in" ? "Fan-in" : "Single-owner"),
                  })
                );
              }}
              onOpenPanel={function (panel) {
                dispatch(setRecordsPanel(panel));
              }}
              onClosePanel={function () {
                dispatch(setRecordsPanel(null));
              }}
            />
          ) : null}

          {!loading && !error && view === "owners" ? (
            <OwnersPane
              owners={owners}
              accountColumns={ownerAccounts}
              rows={scoped}
              allRows={rows}
              ownerCapacities={ownerCapacities}
              accounts={accounts}
              scopeAccountId={
                filters.account !== "all" ? filters.account : null
              }
              ownersMode={ownersMode}
              ownersFocus={ownersFocus}
              ownersSort={ownersSort}
              ownersGridSort={ownersGridSort}
              ownersHighlight={ownersHighlight}
              capacityConfigured={capacityConfigured}
              capacityByGrade={capacityByGrade}
              capacitySettingsOpen={capacitySettingsOpen}
              onOpenCapacitySettings={function () {
                dispatch(setCapacitySettingsOpen(true));
              }}
              onCloseCapacitySettings={function () {
                dispatch(setCapacitySettingsOpen(false));
              }}
              onSetMode={function (mode) {
                dispatch(setCoverageOwnersMode(mode));
              }}
              onSort={function (key) {
                dispatch(
                  setCoverageOwnersSort(nextOwnersSort(ownersSort, key))
                );
              }}
              onGridSort={function (key) {
                dispatch(setCoverageOwnersGridSort(key));
              }}
              onViewInGrid={function (ownerId) {
                dispatch(ownersViewInGrid(ownerId));
              }}
              onOpenPortfolio={openOwnerPortfolio}
              onOpenTeamViews={function (owner) {
                openTeamViews(owner.name, owner.name + " owner coverage");
              }}
              onDepartureImpact={function (ownerId) {
                startDepartureImpact(ownerId, "reactive");
              }}
            />
          ) : null}

          {!loading && !error && view === "attention" ? (
            <NeedsAttentionPane
              attentionItems={attentionItems}
              accounts={accounts}
              rows={rows}
              filters={filters}
              readinessSegment={readinessSegment}
              readinessAccountIds={
                readinessSegment &&
                readinessSegment !== "all" &&
                readinessSegment !== "not-set-up"
                  ? new Set(
                      Object.entries(readinessByAccount)
                        .filter(function (entry) {
                          const rd = entry[1];
                          if (readinessSegment === "setup") return rd.setup;
                          return (
                            rd.category === readinessSegment ||
                            (readinessSegment === "not-set-up" && rd.setup)
                          );
                        })
                        .map(function (entry) {
                          return entry[0];
                        })
                    )
                  : readinessSegment === "not-set-up" ||
                      readinessSegment === "setup"
                    ? new Set(
                        Object.entries(readinessByAccount)
                          .filter(function (entry) {
                            return entry[1].setup;
                          })
                          .map(function (entry) {
                            return entry[0];
                          })
                      )
                    : null
              }
              naView={naView}
              naGaps={naGaps}
              naRisks={naRisks}
              naFocusLabel={null}
              meddpiccOn={meddpiccOn}
              onSetNaView={function (v) {
                dispatch(setNaView(v));
              }}
              onSetGaps={function (key, value) {
                dispatch(setNaGaps({ key: key, value: value }));
              }}
              onSetRisks={function (key, value) {
                dispatch(setNaRisks({ key: key, value: value }));
              }}
              onClearFocus={function () {
                dispatch(clearNaFocus());
                dispatch(setCoverageFilter({ key: "account", value: "all" }));
              }}
              onAccount={function (id) {
                openAccount(id, nameOf(accounts, id) + " account coverage");
              }}
              onSetup={function (accountId) {
                openAccountWorkspace(
                  accountId,
                  nameOf(accounts, accountId) + " account setup"
                );
              }}
              onSuccessor={function (ownerId) {
                startDepartureImpact(ownerId, "reactive");
              }}
              onConfirm={function (accountId) {
                openAccount(
                  accountId,
                  nameOf(accounts, accountId) + " confirm handoff"
                );
              }}
              onAddOwner={function (accountId, contactId) {
                openAccount(
                  accountId,
                  nameOf(accounts, accountId) + " add owner",
                  contactId
                    ? { contactId: contactId, purpose: "add-owner" }
                    : { purpose: "add-owner" }
                );
              }}
              onOpenPortfolio={function (ownerId, filter) {
                openOwnerPortfolio(ownerId, null, filter as any);
              }}
              onSimulate={function (ownerId) {
                startDepartureImpact(ownerId, "prospective");
              }}
              onFilterAccount={function (accountId) {
                dispatch(
                  setCoverageFilter({
                    key: "account",
                    value: accountId || "all",
                  })
                );
              }}
              onSeeOwnerRisks={function (ownerId) {
                dispatch(setCoverageFilter({ key: "owner", value: ownerId }));
                dispatch(setNaView("risks"));
                dispatch(setNaRisks({ key: "type", value: "capacity" }));
              }}
              onViewHistory={function (accountId) {
                openAccount(
                  accountId,
                  nameOf(accounts, accountId) + " lifecycle history",
                  { history: true }
                );
              }}
            />
          ) : null}
        </div>
      </div>

      <AccountCoverageModal
        accountId={accountDetailId}
        accounts={accounts}
        rows={rows}
        attentionItems={attentionItems}
        ownerCapacities={ownerCapacities}
        readinessByAccount={readinessByAccount}
        meddpiccOn={meddpiccOn}
        onClose={function () {
          dispatch(setAccountDetail(null));
        }}
        onMatrix={function (id) {
          dispatch(setAccountDetail(null));
          openAccount(id, nameOf(accounts, id) + " account coverage");
        }}
        onViewCoverageMap={function (id) {
          const accountName = nameOf(accounts, id);
          dispatch(setAccountDetail(null));
          dispatch(
            setCoverageReturnContext({
              label: accountName + " coverage map",
            })
          );
          const params = new URLSearchParams({
            tab: "account-relationships",
            accountId: id,
            relationshipView: "map",
          });
          navigate(orgPath(RELATIONSHIP_ROUTE_PATH) + "?" + params.toString());
        }}
        onOpenGaps={function (id) {
          dispatch(setAccountDetail(null));
          dispatch(
            openNeedsAttention({
              naView: "gaps",
              accountId: id,
              focusLabel: nameOf(accounts, id) + " gaps",
            })
          );
        }}
        onOpenRisks={function (id) {
          dispatch(setAccountDetail(null));
          dispatch(
            openNeedsAttention({
              naView: "risks",
              accountId: id,
              focusLabel: nameOf(accounts, id) + " risks",
            })
          );
        }}
        onOpenOwnerBand={function (id, key) {
          dispatch(setAccountDetail(null));
          if (key === "baseline-unmapped") {
            dispatch(
              openNeedsAttention({
                naView: "gaps",
                gapType: "unowned+orphaned",
                accountId: id,
                focusLabel: nameOf(accounts, id) + " · No owner",
              })
            );
            return;
          }
          if (
            key === "single-owner" ||
            key === "two-owners" ||
            key === "fan-in"
          ) {
            const bandLabel =
              key === "single-owner"
                ? "Single owner"
                : key === "two-owners"
                  ? "Two owners"
                  : "3+ owners";
            dispatch(
              drillAccountFocus({
                accountId: id,
                accountsMode: "heatmap",
                focusLabel: nameOf(accounts, id) + " · " + bandLabel,
              })
            );
            dispatch(
              setRecordsPanel({
                accountId: id,
                source: "owners",
                key:
                  key === "single-owner"
                    ? "single"
                    : key === "fan-in"
                      ? "fanIn3"
                      : "two",
              })
            );
            return;
          }
        }}
        onOpenStrength={function (id, band) {
          dispatch(setAccountDetail(null));
          const bandLabel =
            band === "unassessed"
              ? "Not assessed"
              : band === "weak"
                ? "Weak (RL 0–1)"
                : band === "moderate"
                  ? "Moderate (RL 2–3)"
                  : band === "strong"
                    ? "Strong (RL 4–5)"
                    : band;
          dispatch(
            drillAccountFocus({
              accountId: id,
              status: band || undefined,
              accountsMode: "strength",
              focusLabel: nameOf(accounts, id) + " · " + bandLabel,
            })
          );
        }}
      />

      <OwnerPortfolioModal
        portfolio={ownerPortfolio}
        owners={owners}
        rows={rows}
        accounts={accounts}
        ownerCapacities={ownerCapacities}
        attentionItems={attentionItems}
        meddpiccOn={meddpiccOn}
        onClose={function () {
          dispatch(setOwnerPortfolio(null));
        }}
        onSetFilter={function (key) {
          dispatch(setOwnerPortfolioFilter(key));
        }}
        onToggleFilter={function (key) {
          dispatch(toggleOwnerPortfolioFilter(key));
        }}
        onViewMap={openAccountCoverageMap}
        onTeamViews={function (owner) {
          dispatch(setOwnerPortfolio(null));
          openTeamViews(owner.name, owner.name + " owner coverage");
        }}
        onDepartureImpact={function (owner) {
          dispatch(setOwnerPortfolio(null));
          startDepartureImpact(owner.id);
        }}
        onSeeRisks={function (owner) {
          dispatch(setOwnerPortfolio(null));
          dispatch(
            openNeedsAttention({
              naView: "risks",
              ownerId: owner.id,
              focusLabel: "Owner: " + owner.name,
            })
          );
        }}
      />

      <DepartureImpactModal
        draft={departureImpact}
        owners={owners}
        rows={rows}
        accounts={accounts}
        ownerCapacities={ownerCapacities}
        onClose={function () {
          setDepartureImpact(null);
        }}
        onConfirmPlan={function () {
          setDepartureImpact(null);
          dispatch(fetchPortfolioCoverage());
        }}
      />
    </TooltipProvider>
  );
};

/* ================================================================== */
/*  Overview                                                           */
/* ================================================================== */

function OverviewPane(props: {
  rows: CoverageRow[];
  scoped: CoverageRow[];
  matching: CoverageRow[];
  filters: CoverageFilters;
  accounts: CoverageAccount[];
  ownerCapacities: OwnerCapacity[];
  readinessByAccount: Record<string, AccountReadiness>;
  meddpiccOn: boolean;
  mapFocus: {
    accountId?: string | null;
    contactId?: string | null;
    band?: string | null;
  } | null;
  lensId: string;
  linkedKey: string | null;
  onStatus: (status: string, view: "accounts" | "attention" | "gaps") => void;
  onOpenNotSetUp: () => void;
  onOpenOwnersView: () => void;
  onOpenUnassessed: () => void;
  onLens: (id: string) => void;
  onLink: (key: string | null) => void;
  onDrill: (
    lens: (typeof COMPOSITION_LENSES)[number],
    key: string,
    label: string
  ) => void;
  onOpenAccount: (accountId: string) => void;
  onOpenRelationships: (accountId: string) => void;
  onClearMapFocus: () => void;
}) {
  const {
    rows,
    scoped,
    filters,
    accounts,
    ownerCapacities,
    readinessByAccount,
    meddpiccOn,
    mapFocus,
    lensId,
    linkedKey,
    onStatus,
    onOpenNotSetUp,
    onOpenOwnersView,
    onOpenUnassessed,
    onLens,
    onLink,
    onDrill,
    onOpenAccount,
    onOpenRelationships,
    onClearMapFocus,
  } = props;

  /**
   * Health-map banner overflow: count-based only (no max-h clip — that cut pills mid-row).
   * Caps sized for ≈2 lines (Not set up) / ≈1 line (Unknown) with long account+reason labels.
   */
  const SETUP_PILL_CAP = 6;
  const UNKNOWN_PILL_CAP = 4;
  const [bannerOverflow, setBannerOverflow] = useState<null | {
    kind: "setup" | "unknown";
    ids: string[];
  }>(null);
  const [mapFindId, setMapFindId] = useState<string | null>(null);

  /** Keep a Find-account hit in the visible pill strip (not only behind +N more). */
  const visibleBannerIds = function (
    ids: string[],
    cap: number,
    foundId: string | null
  ) {
    if (!foundId || !ids.includes(foundId)) return ids.slice(0, cap);
    return [
      foundId,
      ...ids.filter(function (id) {
        return id !== foundId;
      }),
    ].slice(0, cap);
  };

  const accountIds = Array.from(
    new Set([
      ...accounts.map(function (a) {
        return a.account_id;
      }),
      ...scoped.map(function (r) {
        return r.account_id;
      }),
    ])
  ).filter(function (id) {
    return (
      filters.account === "all" ||
      id === filters.account ||
      scoped.some(function (r) {
        return r.account_id === id;
      })
    );
  });

  const kpis = coverageKpis(scoped, {
    readinessByAccount: readinessByAccount,
    ownerCapacities: ownerCapacities,
    meddpiccOn: meddpiccOn,
    accountId: filters.account !== "all" ? filters.account : null,
  });
  const ownerFiltered = filters.owner !== "all";
  const singleAccountFiltered = filters.account !== "all";
  const setupSummary = setupSummaryText(readinessByAccount);
  const totalAccounts = accountIds.length;

  const metrics: Record<string, ReturnType<typeof accountMetrics>> = {};
  accountIds.forEach(function (id) {
    const account = accounts.find(function (a) {
      return a.account_id === id;
    });
    metrics[id] = accountMetrics(id, rows, {
      teamSize: account?.team_size ?? 0,
      ownerCapacities: ownerCapacities,
    });
  });

  const setupAccounts = accountIds.filter(function (id) {
    return readinessByAccount[id]?.setup;
  });

  const known = accountIds.filter(function (id) {
    return metrics[id].assessedCount > 0;
  });
  const unknown = accountIds.filter(function (id) {
    return metrics[id].assessedCount === 0 && !readinessByAccount[id]?.setup;
  });

  const mapPoints = useMemo(
    function () {
      const usedCodes = new Set<string>();
      return known.map(function (id) {
        const m = metrics[id];
        const name =
          scoped.find(function (r) {
            return r.account_id === id;
          })?.account_name || id;
        return {
          id: id,
          name: name,
          code: accountMapCode(id, name, usedCodes),
          color: colorForAccount(id),
          x: healthMapX(m.complete),
          y: healthMapY(m.strong),
          quadrant: quadrantLabel(m.complete, m.strong),
          gaps: m.gaps,
          pending: m.pending,
          m: m,
        };
      });
    },
    [known, metrics, scoped]
  );

  const lensIndex = Math.max(
    0,
    COMPOSITION_LENSES.findIndex(function (l) {
      return l.id === lensId;
    })
  );
  const lens = COMPOSITION_LENSES[lensIndex];
  const values = compositionValues(
    lens.id,
    lens.defs,
    scoped,
    lens.id === "readiness" ? readinessByAccount : undefined
  );
  const total =
    values.reduce(function (s, v) {
      return s + v;
    }, 0) || 1;
  const lensRecordTotal = values.reduce(function (s, v) {
    return s + v;
  }, 0);

  const funnel =
    filters.account !== "all" ? alignmentFunnel(filters.account, rows) : null;
  const accountName =
    scoped.find(function (r) {
      return r.account_id === filters.account;
    })?.account_name || "Account";

  const tileHelpContent = [
    {
      title: "Accounts not set up",
      question: "Can we measure it?",
      body: "Accounts with no contacts, no team or no mappings are left out of every figure here and sit under Unknown quality on the health map, so a high coverage % can hide them.",
    },
    {
      title: ownerFiltered ? "Contacts owned" : "Contact coverage",
      question: "How much is covered?",
      body: "Breadth: the health map’s horizontal axis for the whole scope. “Never mapped” are contacts that have never had an owner.",
    },
    {
      title: "True orphans",
      question: "What have we lost?",
      body: "Coverage already lost when an owner left, and why an account has a ring on the map. Pending handoffs are the same departure, one step later.",
    },
    {
      title: "Single-threaded contacts",
      question: "What could we lose next?",
      body: "Every contact with one owner is one departure from becoming an orphan. The map can’t show this: a dot looks the same with one owner or three.",
    },
    {
      title: "Owners over capacity",
      question: "Who is holding too much?",
      body: "Coverage held by a stretched owner is fragile. Limits are soft, shared settings by grade level.",
    },
    {
      title: "Assessment visibility",
      question: "How much can we vouch for?",
      body: "Each owner–contact relationship needs its own assessment, so this counts relationships, not contacts. It is about evidence, not strength (strength belongs to the Alignment KPIs). On the map’s vertical axis a contact with no assessed relationship counts as not proven, so “Broad but weak” can mean “broad but unknown”.",
    },
  ];

  /* Setup sub-text for tile 1 */
  const setupSubText = singleAccountFiltered
    ? (() => {
        const rd = readinessByAccount[filters.account];
        if (!rd) return "Set up";
        return rd.setup ? STATUS_LABELS[rd.category] || "Not set up" : "Set up";
      })()
    : kpis.accountsNotSetUp === 0
      ? "All " + totalAccounts + " set up"
      : setupSummary ||
        kpis.accountsNotSetUp +
          " " +
          pluralUnit(kpis.accountsNotSetUp, "account") +
          " not set up";

  return (
    <div className="flex flex-col gap-4">
      {/* KPI row — 3 tiles over composition, 3 tiles + help over the map */}
      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
          {/* 1. Accounts not set up */}
          <MetricCard
            title="Accounts not set up"
            value={
              singleAccountFiltered
                ? readinessByAccount[filters.account]?.setup
                  ? "Not set up"
                  : "Set up"
                : kpis.accountsNotSetUp
            }
            subtitle={setupSubText}
            borderType="red"
            className="h-full"
            onClick={function () {
              onOpenNotSetUp();
            }}
            info={
              <MetricInfo
                label="Accounts not set up"
                text={
                  "Accounts with no contacts, no account team, or no mapped owner–contact pairs. Currently " +
                  kpis.accountsNotSetUp +
                  " of " +
                  totalAccounts +
                  ". Click to open Needs Attention › Gaps › Not set up."
                }
              />
            }
          />
          {/* 2. Contact coverage (or Contacts owned when owner filter) */}
          <MetricCard
            title={ownerFiltered ? "Contacts owned" : "Contact coverage"}
            value={ownerFiltered ? kpis.owned : kpis.coverageRate + "%"}
            subtitle={
              ownerFiltered
                ? "Contacts this owner actively covers in scope"
                : kpis.baseline + " never mapped (baseline)"
            }
            borderType="green"
            className="h-full"
            onClick={function () {
              onStatus("coverage", "accounts");
            }}
            info={
              <MetricInfo
                label={ownerFiltered ? "Contacts owned" : "Contact coverage"}
                text={
                  ownerFiltered
                    ? "Contacts this owner actively covers in scope (" +
                      kpis.owned +
                      ")."
                    : "Contacts with at least one current or pending owner ÷ all contacts in scope. Each contact counted once. Currently " +
                      kpis.coverageRate +
                      "%. Opens Accounts sorted lowest coverage first."
                }
              />
            }
          />
          {/* 3. True orphans */}
          <MetricCard
            title="True orphans"
            value={kpis.gaps}
            subtitle={kpis.pending + " pending handoffs"}
            borderType="red"
            className="h-full"
            onClick={function () {
              onStatus("open-orphaned", "gaps");
            }}
            info={
              <MetricInfo
                label="True orphans"
                text={
                  "Contacts that lost coverage and have no remaining owner. " +
                  kpis.gaps +
                  " orphan records across " +
                  kpis.affectedAccounts +
                  " accounts. " +
                  kpis.pending +
                  " pending handoffs. Opens Needs Attention › Gaps for orphaned lifecycle gaps and pending handoffs."
                }
              />
            }
          />
        </div>
        <div className="flex min-w-0 items-stretch gap-3">
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
            {/* 4. Single-threaded contacts */}
            <MetricCard
              title="Single-threaded contacts"
              value={kpis.single}
              subtitle={
                (meddpiccOn && kpis.singleKeyRoleCount
                  ? kpis.singleKeyRoleCount + " in key roles · "
                  : "") +
                kpis.singleAccounts +
                " " +
                pluralUnit(kpis.singleAccounts, "account")
              }
              borderType="yellow"
              className="h-full"
              onClick={function () {
                onStatus("single-owner", "gaps");
              }}
              info={
                <MetricInfo
                  label="Single-threaded contacts"
                  text={
                    "ALL contacts where fan-in is 1. Currently " +
                    kpis.single +
                    " contacts are single-threaded" +
                    (meddpiccOn && kpis.singleKeyRoleCount
                      ? " (" + kpis.singleKeyRoleCount + " in key roles)"
                      : "") +
                    " across " +
                    kpis.singleAccounts +
                    " " +
                    pluralUnit(kpis.singleAccounts, "account") +
                    ". Opens Accounts sorted by Single owner. A Single owner cell links to Risks."
                  }
                />
              }
            />
            {/* 5. Owners over capacity */}
            <MetricCard
              title="Owners over capacity"
              value={kpis.ownersOverCapacity}
              subtitle={
                kpis.ownerCount +
                " " +
                pluralUnit(kpis.ownerCount, "owner") +
                " in scope"
              }
              borderType={kpis.ownersOverCapacity ? "red" : "green"}
              className="h-full"
              onClick={function () {
                onOpenOwnersView();
              }}
              info={
                <MetricInfo
                  label="Owners over capacity"
                  text={
                    "Owners whose portfolio or any single-account load exceeds the recommended capacity limit. Currently " +
                    kpis.ownersOverCapacity +
                    " of " +
                    kpis.ownerCount +
                    " owners. Opens Owners grouped by over, near, and within limit."
                  }
                />
              }
            />
            {/* 6. Assessment visibility */}
            <MetricCard
              title="Assessment visibility"
              value={
                kpis.assessmentVisibility != null
                  ? kpis.assessmentVisibility + "%"
                  : "\u2014"
              }
              subtitle={
                kpis.unassessedRel +
                " " +
                pluralUnit(kpis.unassessedRel, "relationship") +
                " not yet assessed"
              }
              borderType={
                kpis.assessmentVisibility == null
                  ? "purple"
                  : kpis.assessmentVisibility >= 80
                    ? "green"
                    : kpis.assessmentVisibility >= 50
                      ? "yellow"
                      : "red"
              }
              className="h-full"
              onClick={function () {
                onOpenUnassessed();
              }}
              info={
                <MetricInfo
                  label="Assessment visibility"
                  text={
                    "Share of active relationships (pairs) with an RL assessment. Currently " +
                    (kpis.assessmentVisibility != null
                      ? kpis.assessmentVisibility + "%"
                      : "—") +
                    " (" +
                    kpis.assessedRel +
                    " of " +
                    kpis.activePairs +
                    " relationships). Click to open unassessed relationships."
                  }
                />
              }
            />
          </div>
          {/* How to read these tiles — help popover */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full border border-gray-200 bg-white text-gray-400 transition hover:text-purple-600"
                aria-label="How to read these tiles"
              >
                <Info className="h-3.5 w-3.5" strokeWidth={2.25} />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              sideOffset={6}
              className={metricInfoClass + " !max-w-[380px] !p-3"}
            >
              <strong className="mb-1.5 block text-[12px] text-gray-900">
                How to read these tiles
              </strong>
              <p className="mb-2 text-[11px] leading-snug text-gray-600">
                Left to right they tell one coverage-health story: can we
                measure it, how much is covered, what is lost, what could be
                lost next, and how much we can vouch for. The health map shows
                where; these tiles show how much.
              </p>
              <ul className="space-y-2 text-[11px] leading-snug text-gray-600">
                {tileHelpContent.map(function (h) {
                  return (
                    <li key={h.title}>
                      <strong className="text-gray-900">{h.title}</strong>
                      <em className="ml-1.5 text-gray-500">{h.question}</em>
                      <span className="mt-0.5 block text-gray-600">
                        {h.body}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 border-t border-gray-100 pt-2 text-[10px] leading-snug text-gray-500">
                Units differ by tile (accounts, contacts, relationships,
                owners), so compare tiles by story, not by number. With one
                account selected, the first tile shows its setup and capacity
                uses the per-account limit. Click any tile to open where it is
                explained.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Alignment funnel (account-scoped) */}
      {funnel ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <strong className="text-sm">
              Alignment funnel &middot; {accountName}
            </strong>
          </div>
          <FunnelRow funnel={funnel} />
          <p className="mt-2 text-xs text-gray-500">
            Funnel: Contacts/{funnel.total} Mapped/{funnel.mapped} Assessed/
            {funnel.assessed} Goaled/{funnel.goaled} Progressing/
            {funnel.progressing} Achieved/
            {funnel.achieved} at contact grain.
          </p>
        </div>
      ) : (
        <></>
      )}

      <div className="grid items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Account health map — right column */}
        <div className="order-2 flex h-full min-w-0 w-full flex-col rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-1 flex shrink-0 flex-wrap items-center justify-between gap-2">
            <strong className="text-sm">Coverage health map</strong>
            <div className="w-[240px] shrink-0">
              <DropdownSelector
                label="Find account…"
                selected={mapFindId}
                hideLabel
                compact
                fullWidth
                searchable
                searchPlaceholder="Find account…"
                triggerStyle="field"
                enableScroll
                maxHeight="240px"
                options={(mapFindId
                  ? [{ value: "", label: "Clear highlight" }]
                  : []
                ).concat(
                  accountIds
                    .slice()
                    .sort(function (a, b) {
                      return nameOf(accounts, a).localeCompare(
                        nameOf(accounts, b)
                      );
                    })
                    .map(function (id) {
                      const plotted = mapPoints.some(function (p) {
                        return p.id === id;
                      });
                      const place = readinessByAccount[id]?.setup
                        ? "Not set up"
                        : plotted
                          ? mapPoints.find(function (p) {
                              return p.id === id;
                            })?.quadrant || "On the map"
                          : "Unknown quality";
                      return {
                        value: id,
                        label: nameOf(accounts, id) + " · " + place,
                      };
                    })
                )}
                onSelect={function (value) {
                  setMapFindId(value || null);
                }}
              />
            </div>
          </div>
          <p className="mb-2 shrink-0 text-[11px] text-gray-500">
            Each point is one account with assessed relationship evidence; ring
            = lifecycle gap or pending handoff. Not mapped / not set up accounts
            appear in the bands below — not as plot points.
          </p>
          <div
            className="grid min-h-0 flex-1 gap-x-2"
            style={{
              gridTemplateColumns: "38px minmax(0,1fr)",
              gridTemplateRows:
                "minmax(" + HEALTH_MAP_PLOT_MIN_PX + "px, 1fr) 32px",
            }}
          >
            {/* Y axis */}
            <div className="flex flex-col items-center justify-center gap-1 text-center text-gray-700">
              <span
                className="text-base font-extrabold leading-none"
                aria-hidden
              >
                ↑
              </span>
              <span
                className="text-[10px] font-bold leading-tight"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                Assessed relationship strength
              </span>
            </div>

            {/* Colored quadrant plot with cluster fan-out */}
            <HealthMapPlot
              points={mapPoints}
              onOpenAccount={onOpenAccount}
              focusAccountId={mapFocus?.accountId}
              foundAccountId={mapFindId}
            />

            {/* spacer under Y axis */}
            <div />

            {/* X axis */}
            <div className="flex items-center justify-between px-1 text-[9px] font-bold text-gray-600">
              <span>Incomplete</span>
              <strong className="text-[10px] text-gray-700">
                Contact coverage completeness →
              </strong>
              <span>Complete</span>
            </div>
          </div>
          <div className="mt-2 shrink-0 rounded-md border border-gray-100 bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
            <strong className="mb-1 block text-gray-800">
              How accounts are placed
            </strong>
            <ul className="list-disc space-y-0.5 pl-4">
              <li>
                Quadrant eligible: at least one active covered contact has an RL
                assessment. Accounts with contacts and a team but no mapped
                pairs stay in Not set up as Not mapped — they are not plotted.
              </li>
              <li>
                X = contacts with an active owner ÷ all account contacts. Y =
                contacts at RL4–5 ÷ all covered contacts (unassessed covered =
                not yet proven).
              </li>
              <li>
                Center lines = 50%. Hover an account for its quadrant and exact
                counts.
              </li>
              <li>
                Nearby accounts fan out from a hub so every code stays visible;
                hover or click the hub for the shared-position list.
              </li>
            </ul>
          </div>
          {setupAccounts.length ? (
            <div
              className={
                "mt-2 shrink-0 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-red-200 bg-red-50 px-3 py-2 text-xs text-gray-600" +
                (mapFindId && setupAccounts.includes(mapFindId)
                  ? " ring-1 ring-amber-300"
                  : "")
              }
            >
              <strong className="text-red-800">Not set up</strong>
              <span>
                No contacts, no team, or not yet mapped — cannot plot health:
              </span>
              {visibleBannerIds(setupAccounts, SETUP_PILL_CAP, mapFindId).map(
                function (id) {
                  const rd = readinessByAccount[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      title={(rd?.reasons || []).join("; ")}
                      className={
                        "cursor-pointer rounded-full border border-red-200 bg-white px-2.5 py-0.5 text-[11px] font-bold text-red-700 hover:bg-red-100" +
                        (id === mapFindId ? " ring-2 ring-amber-400" : "") +
                        (mapFindId && id !== mapFindId ? " opacity-40" : "")
                      }
                      onClick={function () {
                        onOpenRelationships(id);
                      }}
                    >
                      {nameOf(accounts, id)}
                      <span className="ml-1 font-medium text-red-500">
                        {STATUS_LABELS[rd?.category || ""] || rd?.category}
                      </span>
                    </button>
                  );
                }
              )}
              {setupAccounts.length > SETUP_PILL_CAP ? (
                <button
                  type="button"
                  className="rounded-full border border-red-300 bg-white px-2.5 py-0.5 text-[11px] font-bold text-red-800 hover:bg-red-100"
                  onClick={function () {
                    setBannerOverflow({
                      kind: "setup",
                      ids: setupAccounts,
                    });
                  }}
                >
                  +{setupAccounts.length - SETUP_PILL_CAP} more
                </button>
              ) : null}
              {mapFocus?.accountId ? (
                <button
                  type="button"
                  className="ml-auto text-[11px] font-medium text-[#974ae4] hover:underline"
                  onClick={onClearMapFocus}
                >
                  Clear map focus
                </button>
              ) : null}
            </div>
          ) : null}
          <div
            className={
              "mt-2 shrink-0 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-600" +
              (mapFindId && unknown.includes(mapFindId)
                ? " ring-1 ring-amber-300"
                : "")
            }
          >
            <strong className="text-gray-800">Unknown quality</strong>
            <span>Set up, but insufficient assessment evidence:</span>
            {unknown.length ? (
              <>
                {visibleBannerIds(unknown, UNKNOWN_PILL_CAP, mapFindId).map(
                  function (id) {
                    const n =
                      scoped.find(function (r) {
                        return r.account_id === id;
                      })?.account_name || nameOf(accounts, id);
                    const m = metrics[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        title={
                          formatMetricPct(m.complete) +
                          " contact coverage; no assessed relationship evidence"
                        }
                        className={
                          "cursor-pointer rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-[11px] font-bold text-[#974ae4] hover:bg-gray-100" +
                          (id === mapFindId ? " ring-2 ring-amber-400" : "") +
                          (mapFindId && id !== mapFindId ? " opacity-40" : "")
                        }
                        onClick={function () {
                          onOpenRelationships(id);
                        }}
                      >
                        {n}
                      </button>
                    );
                  }
                )}
                {unknown.length > UNKNOWN_PILL_CAP ? (
                  <button
                    type="button"
                    className="rounded-full border border-gray-400 bg-white px-2.5 py-0.5 text-[11px] font-bold text-gray-800 hover:bg-gray-100"
                    onClick={function () {
                      setBannerOverflow({ kind: "unknown", ids: unknown });
                    }}
                  >
                    +{unknown.length - UNKNOWN_PILL_CAP} more
                  </button>
                ) : null}
              </>
            ) : (
              <span>
                None — every set-up account in scope has assessed relationship
                evidence.
              </span>
            )}
          </div>
          {!setupAccounts.length && mapFocus?.accountId ? (
            <div className="mt-2 text-right">
              <button
                type="button"
                className="text-[11px] font-medium text-[#974ae4] hover:underline"
                onClick={onClearMapFocus}
              >
                Clear map focus
              </button>
            </div>
          ) : null}
          <CommonModal
            isOpen={!!bannerOverflow}
            toggle={function () {
              setBannerOverflow(null);
            }}
            usePortal
            size="xl"
            title={
              bannerOverflow?.kind === "setup"
                ? "Not set up accounts"
                : "Unknown quality accounts"
            }
            subtitle={
              bannerOverflow
                ? String(bannerOverflow.ids.length) +
                  " account" +
                  (bannerOverflow.ids.length === 1 ? "" : "s")
                : undefined
            }
          >
            <ul className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto overflow-x-hidden p-2">
              {(bannerOverflow?.ids || []).map(function (id) {
                const rd = readinessByAccount[id];
                const n =
                  scoped.find(function (r) {
                    return r.account_id === id;
                  })?.account_name || nameOf(accounts, id);
                const reasonBadges =
                  bannerOverflow?.kind === "setup"
                    ? (rd?.reasons || []).length
                      ? rd!.reasons
                      : [
                          STATUS_LABELS[rd?.category || ""] ||
                            rd?.category ||
                            "Not set up",
                        ]
                    : [];
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={
                        "w-full rounded-md border bg-white px-3 py-2.5 text-left hover:bg-gray-50" +
                        (id === mapFindId
                          ? " border-amber-400 ring-2 ring-amber-400"
                          : " border-gray-100")
                      }
                      onClick={function () {
                        setBannerOverflow(null);
                        onOpenRelationships(id);
                      }}
                    >
                      <div className="text-sm font-semibold text-gray-900">
                        {n}
                      </div>
                      {reasonBadges.length ? (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {reasonBadges.map(function (reason, i) {
                            return (
                              <span
                                key={i}
                                className="inline-flex max-w-full rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] leading-snug text-red-700"
                              >
                                {reason}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </CommonModal>
        </div>

        {/* Portfolio composition — left column */}
        <div className="order-1 flex h-full min-w-0 w-full flex-col rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 shrink-0">
            <strong className="text-sm">Portfolio composition</strong>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
              One lens at a time. Each answers a different question and uses its
              own denominator.
            </p>
          </div>
          <TabsComponent
            tabs={COMPOSITION_LENSES.map(function (item) {
              return { id: item.id, label: item.label };
            })}
            activeTab={lens.id}
            onTabChange={function (id: string) {
              onLens(id);
            }}
            variant="secondary-segmented"
            segmentedLayout="fit"
            className="mb-3 shrink-0"
          />
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
              <strong className="text-sm text-gray-900">{lens.label}</strong>
              <Badge
                type="primary-border"
                label={lensRecordTotal + " " + lens.displayUnit}
                title={compositionUnitTooltip(lens.id, meddpiccOn)}
              />
            </div>
            <p className="shrink-0 text-sm font-medium text-gray-900">
              {lens.question}
            </p>
            <p className="mt-1 shrink-0 text-sm text-gray-600">
              {lens.meaning}
            </p>

            {/* Stacked bar with hover highlight */}
            <div className="mt-3 flex h-4 shrink-0 overflow-hidden rounded-full bg-gray-100">
              {lens.defs.map(function (def, idx) {
                if (values[idx] === 0) return null;
                const linked =
                  linkedKey === def[0] || filters.status === def[0];
                const dimmed = Boolean(linkedKey) && linkedKey !== def[0];
                return (
                  <div
                    key={def[0]}
                    role="button"
                    tabIndex={0}
                    className={
                      def[2] +
                      " cursor-pointer transition-all duration-200 ease-out" +
                      (linked
                        ? " brightness-110 ring-2 ring-inset ring-gray-900"
                        : "") +
                      (dimmed ? " opacity-35" : " opacity-100")
                    }
                    style={{
                      flexGrow: values[idx],
                      flexBasis: 0,
                      minWidth: values[idx] > 0 ? 4 : 0,
                    }}
                    aria-label={
                      lens.label +
                      " · " +
                      def[1] +
                      ": " +
                      values[idx] +
                      " (" +
                      Math.round((values[idx] / total) * 100) +
                      "%)"
                    }
                    title={
                      def[1] +
                      " · " +
                      values[idx] +
                      " (" +
                      Math.round((values[idx] / total) * 100) +
                      "%) — " +
                      def[3]
                    }
                    onMouseEnter={function () {
                      onLink(def[0]);
                    }}
                    onMouseLeave={function () {
                      onLink(null);
                    }}
                    onFocus={function () {
                      onLink(def[0]);
                    }}
                    onBlur={function () {
                      onLink(null);
                    }}
                    onClick={function () {
                      onDrill(lens, def[0], def[1]);
                    }}
                    onKeyDown={function (e) {
                      if (e.key === "Enter") onDrill(lens, def[0], def[1]);
                    }}
                  />
                );
              })}
            </div>

            {/* Breakdown — single-column rows (prototype weight); fixed row band = tallest lens */}
            <div
              className="mt-3 shrink-0 space-y-1.5"
              style={{
                minHeight:
                  Math.max(
                    ...COMPOSITION_LENSES.map(function (item) {
                      return item.defs.length;
                    })
                  ) *
                    2.625 +
                  "rem",
              }}
            >
              {lens.defs.map(function (def, idx) {
                const pct = Math.round((values[idx] / total) * 100);
                const linked =
                  linkedKey === def[0] || filters.status === def[0];
                const dimmed = Boolean(linkedKey) && linkedKey !== def[0];
                return (
                  <div
                    key={def[0]}
                    role="button"
                    tabIndex={0}
                    className={
                      "flex cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2 text-left transition-all duration-200 hover:bg-gray-50" +
                      (linked
                        ? " border-purple-200 bg-purple-50/50 ring-2 ring-purple-400"
                        : "") +
                      (dimmed ? " opacity-45" : "")
                    }
                    onMouseEnter={function () {
                      onLink(def[0]);
                    }}
                    onMouseLeave={function () {
                      onLink(null);
                    }}
                    onClick={function () {
                      onDrill(lens, def[0], def[1]);
                    }}
                    onKeyDown={function (e) {
                      if (e.key === "Enter") onDrill(lens, def[0], def[1]);
                    }}
                  >
                    <span className="flex min-w-0 items-start gap-2">
                      <span
                        className={
                          "mt-1 h-2.5 w-2.5 shrink-0 rounded-full " + def[2]
                        }
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900">
                          {def[1]}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {def[3]}
                        </span>
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-sm tabular-nums text-gray-900">
                      {values[idx]}
                      <em className="ml-2 not-italic text-gray-500">
                        ({pct}%)
                      </em>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 shrink-0 space-y-1 text-xs leading-relaxed text-gray-600">
              <p>
                <strong className="text-gray-800">Why it matters</strong>{" "}
                {lens.decision}
              </p>
              <p>
                <strong className="text-gray-800">Next step</strong>{" "}
                {lens.action}
              </p>
            </div>

            <div className="mt-auto shrink-0 pt-3">
              <CarouselNavigation
                activeIndex={lensIndex}
                total={COMPOSITION_LENSES.length}
                disablePrev={lensIndex === 0}
                disableNext={lensIndex === COMPOSITION_LENSES.length - 1}
                onPrev={function () {
                  onLens(
                    COMPOSITION_LENSES[
                      (lensIndex + COMPOSITION_LENSES.length - 1) %
                        COMPOSITION_LENSES.length
                    ].id
                  );
                }}
                onNext={function () {
                  onLens(
                    COMPOSITION_LENSES[
                      (lensIndex + 1) % COMPOSITION_LENSES.length
                    ].id
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Account coverage modal                                             */
/* ================================================================== */

/** Non-clickable status label — matches prototype filled `.cw-status` pills. */
function CoverageStatusPill(props: { status: string }) {
  const status = props.status;
  return (
    <span
      className={
        "inline-flex shrink-0 rounded-[10px] px-1.5 py-0.5 text-[9.5px] font-bold " +
        coverageStatusPillClass(status)
      }
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/**
 * Prototype cwPanelContactCard — one contact with owner rows (matched band emphasized).
 * Shared by band side panel, Strength list, and account modal strength slide.
 */
function ContactRelationshipCard(props: {
  contactName: string;
  contactMeta?: string;
  relationships: CoverageRow[];
  isMatch?: (row: CoverageRow) => boolean;
  why?: string;
  onViewMap?: () => void;
  onOpenMatrix?: () => void;
}) {
  const {
    contactName,
    contactMeta,
    relationships,
    isMatch,
    why,
    onViewMap,
    onOpenMatrix,
  } = props;

  const rels = relationships
    .filter(function (r) {
      if (r.lifecycle_state !== "dormant") return true;
      const st = effectiveStatus(r);
      return st !== "transferred" && st !== "dismissed";
    })
    .slice()
    .sort(function (a, b) {
      const aMatch = isMatch && isMatch(a) ? 1 : 0;
      const bMatch = isMatch && isMatch(b) ? 1 : 0;
      if (bMatch !== aMatch) return bMatch - aMatch;
      return (b.rl ?? -1) - (a.rl ?? -1);
    });

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-1 min-w-0">
        <div className="truncate text-sm font-semibold text-gray-900">
          {contactName}
        </div>
        {contactMeta ? (
          <div className="truncate text-[11px] text-gray-500">
            {contactMeta}
          </div>
        ) : null}
      </div>
      {why ? (
        <p className="mb-2 text-[11px] leading-snug text-gray-500">{why}</p>
      ) : null}
      <div className="mb-2 flex flex-col gap-1">
        {rels.length ? (
          rels.map(function (r) {
            const matched = !isMatch || isMatch(r);
            return (
              <div
                key={r.id}
                className={
                  "flex items-center justify-between gap-2 rounded px-2 py-1 text-xs " +
                  (matched ? "bg-gray-50 text-gray-900" : "text-gray-400")
                }
              >
                <span className="min-w-0 truncate">
                  {(r.lifecycle_state === "dormant" ? "Former owner " : "") +
                    (r.owner_name || "No owner")}
                  {r.owner_id
                    ? r.rl != null
                      ? " · RL " + r.rl
                      : " · Not assessed"
                    : ""}
                  {r.goal_target_rl != null
                    ? " · goal RL " + r.goal_target_rl
                    : ""}
                </span>
                <CoverageStatusPill status={effectiveStatus(r)} />
              </div>
            );
          })
        ) : (
          <div className="px-2 py-1 text-xs text-gray-400">
            No relationships
          </div>
        )}
      </div>
      {onViewMap || onOpenMatrix ? (
        <div className="mt-auto flex flex-wrap gap-2">
          {onViewMap ? (
            <button
              type="button"
              className="text-[11px] font-medium text-[#974ae4] hover:underline"
              onClick={onViewMap}
            >
              View on Map →
            </button>
          ) : null}
          {onOpenMatrix ? (
            <Button
              variant="outline-primary"
              className="!text-xs"
              onClick={onOpenMatrix}
            >
              Open matrix
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function AccountCoverageModal(props: {
  accountId: string | null;
  accounts: CoverageAccount[];
  rows: CoverageRow[];
  attentionItems: AttentionItem[];
  ownerCapacities: OwnerCapacity[];
  readinessByAccount: Record<string, AccountReadiness>;
  meddpiccOn: boolean;
  onClose: () => void;
  onMatrix: (id: string) => void;
  onViewCoverageMap: (accountId: string) => void;
  onOpenGaps: (accountId: string) => void;
  onOpenRisks: (accountId: string) => void;
  onOpenOwnerBand: (accountId: string, key: string) => void;
  onOpenStrength: (accountId: string, band?: string) => void;
}) {
  const {
    accountId,
    accounts,
    rows,
    attentionItems,
    ownerCapacities,
    readinessByAccount,
    meddpiccOn,
    onClose,
    onMatrix,
    onViewCoverageMap,
    onOpenGaps,
    onOpenRisks,
    onOpenOwnerBand,
    onOpenStrength,
  } = props;

  if (!accountId) return null;
  const name = nameOf(accounts, accountId);
  const account = accounts.find(function (a) {
    return a.account_id === accountId;
  });
  const m = accountMetrics(accountId, rows, {
    teamSize: account?.team_size ?? 0,
    ownerCapacities: ownerCapacities,
  });
  const readiness = readinessByAccount[accountId];
  const overload = overloadCount(accountId, rows, ownerCapacities);
  const accountRows = rows.filter(function (r) {
    return r.account_id === accountId;
  });
  const gapCount = buildGapItems(attentionItems, {
    meddpiccOn: meddpiccOn,
  }).filter(function (item) {
    return item.account_id === accountId;
  }).length;
  const riskCount = buildRiskItems(attentionItems, {
    meddpiccOn: meddpiccOn,
  }).filter(function (item) {
    return item.account_id === accountId;
  }).length;
  const ownerBandCounts = ownerBands(accountRows);
  const ownerBandMeta = [
    { key: "baseline-unmapped", label: "No owner", tone: "border-red-300" },
    { key: "single-owner", label: "Single owner", tone: "border-amber-300" },
    { key: "two-owners", label: "Two owners", tone: "border-green-300" },
    { key: "fan-in", label: "3+ owners", tone: "border-amber-300" },
  ];
  const roleLabel = meddpiccOn
    ? "Strategic-role coverage"
    : "Executive coverage";
  const roleValue = meddpiccOn ? m.roles : m.executive;
  const roleDetail = meddpiccOn
    ? m.classifiedCoveredCount +
      " of " +
      m.classifiedTotal +
      " contacts with a role"
    : m.execTotal
      ? m.execCoveredCount + " of " + m.execTotal + " Executive contacts"
      : "No Executive contacts tagged";
  const bands = accountStrengthBands(accountId, rows);
  const funnel = alignmentFunnel(accountId, rows);
  const strengthMeta: { band: EvidenceBandKey; label: string; tone: string }[] =
    [
      { band: "unassessed", label: "Not assessed", tone: "border-slate-300" },
      { band: "weak", label: "Weak (RL 0–1)", tone: "border-red-300" },
      {
        band: "moderate",
        label: "Moderate (RL 2–3)",
        tone: "border-amber-300",
      },
      { band: "strong", label: "Strong (RL 4–5)", tone: "border-green-300" },
    ];
  const readinessTone =
    readiness?.category === "ready"
      ? "border-emerald-200 bg-emerald-50"
      : readiness?.category === "other-unowned" ||
          readiness?.category === "strain" ||
          readiness?.category === "single-key"
        ? "border-amber-200 bg-amber-50"
        : "border-red-200 bg-red-50";
  const setupAction =
    readiness?.category === "no-contacts"
      ? "Add contacts →"
      : readiness?.category === "no-team"
        ? "Set up account team →"
        : "Map relationships →";
  const pctTiles = [
    {
      key: "complete",
      label: "Contact coverage",
      value: formatMetricPct(m.complete),
      detail: m.coveredCount + " of " + m.total + " contacts",
      tone: heatClass("complete", m.complete),
    },
    {
      key: meddpiccOn ? "roles" : "executive",
      label: roleLabel,
      value: formatMetricPct(roleValue),
      detail: roleDetail,
      tone: heatClass(meddpiccOn ? "roles" : "executive", roleValue),
    },
    {
      key: "assessed",
      label: "Assessment visibility",
      value: formatMetricPct(m.assessed),
      detail: m.relAssessedCount + " of " + m.relTotal + " relationships",
      tone: heatClass("assessed", m.assessed),
    },
  ];

  return (
    <CommonModal
      isOpen={Boolean(accountId)}
      toggle={onClose}
      usePortal
      size="4xl"
      title={name + " · Account coverage"}
      subtitle={[account?.tier].filter(Boolean).join(" · ") || undefined}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {readiness?.setup ? (
            <Button
              variant="primary"
              onClick={function () {
                onMatrix(accountId);
              }}
              title={
                "Open " +
                name +
                " in Account Relationships to add contacts, the account team and mappings"
              }
            >
              {setupAction}
            </Button>
          ) : (
            <>
              {gapCount || riskCount ? (
                <span className="mr-1 text-[11px] font-semibold text-gray-500">
                  Needs Attention
                </span>
              ) : null}
              {gapCount ? (
                <Button
                  variant="outline-primary"
                  onClick={function () {
                    onOpenGaps(accountId);
                  }}
                  title={
                    "Open Needs Attention › Gaps for " +
                    name +
                    ": coverage missing now"
                  }
                >
                  {gapCount + (gapCount === 1 ? " gap →" : " gaps →")}
                </Button>
              ) : null}
              {riskCount ? (
                <Button
                  variant="outline-primary"
                  onClick={function () {
                    onOpenRisks(accountId);
                  }}
                  title={
                    "Open Needs Attention › Risks for " +
                    name +
                    ": coverage that exists but is fragile"
                  }
                >
                  {riskCount + (riskCount === 1 ? " risk →" : " risks →")}
                </Button>
              ) : null}
              <Button
                variant="outline-primary"
                onClick={function () {
                  onMatrix(accountId);
                }}
                title={"Open " + name + " in Account Relationships"}
              >
                Account Relationships →
              </Button>
              <Button
                variant="outline-primary"
                onClick={function () {
                  onViewCoverageMap(accountId);
                }}
                title={"Open the Map flow view for " + name}
              >
                Coverage Map →
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-2 p-1">
        {readiness ? (
          <div
            className={
              "flex flex-wrap items-baseline gap-2 rounded-md border px-2.5 py-1.5 text-[11px] text-gray-700 " +
              readinessTone
            }
            title={
              readiness.reasons.join(" · ") ||
              "Set up; no key-contact, strain or single-thread gaps"
            }
          >
            <strong>
              {"Account readiness · " +
                (STATUS_LABELS[readiness.category] || readiness.category)}
            </strong>
            <span>
              {readiness.reasons
                .map(function (reason) {
                  return reason.split(": ")[0];
                })
                .join(" · ") ||
                "Set up; no key-contact, strain or single-thread gaps"}
            </span>
          </div>
        ) : null}
        {readiness?.setup ? (
          <p className="rounded-md border border-orange-200 bg-orange-50 px-2.5 py-2 text-[11px] leading-snug text-orange-900">
            Coverage cannot be measured for {name} yet:{" "}
            {(readiness.reasons.join("; ") || "it is not set up").toLowerCase()}
            . {readiness.contacts} contact
            {readiness.contacts === 1 ? "" : "s"} · {readiness.teamSize} team
            member
            {readiness.teamSize === 1 ? "" : "s"}. Set the account up in Account
            Relationships, then return here.
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          {pctTiles.map(function (tile) {
            return (
              <div
                key={tile.key}
                title={tile.detail}
                className={
                  "flex flex-col gap-0.5 rounded-lg border border-gray-200 px-2.5 py-2 " +
                  tile.tone
                }
              >
                <b className="text-xl text-gray-800">{tile.value}</b>
                <span className="text-[11px] font-semibold text-gray-700">
                  {tile.label}
                </span>
                <small className="text-[10px] text-gray-500">
                  {tile.detail}
                </small>
              </div>
            );
          })}
          <div
            className={
              "flex flex-col gap-0.5 rounded-lg border border-gray-200 px-2.5 py-2 " +
              (readiness?.setup
                ? "bg-slate-50 text-slate-500"
                : heatClass("single", overload))
            }
            title="Owners whose current relationships in this account exceed their recommended account capacity."
          >
            <b className="text-xl">{readiness?.setup ? "—" : overload}</b>
            <span className="text-[11px] font-semibold">
              Over account limit
            </span>
            <small className="text-[10px] text-gray-500">
              {readiness?.setup
                ? "no owners yet"
                : overload +
                  " of " +
                  new Set(
                    accountRows
                      .filter(function (row) {
                        return row.lifecycle_state === "active" && row.owner_id;
                      })
                      .map(function (row) {
                        return row.owner_id;
                      })
                  ).size +
                  " owners"}
            </small>
          </div>
          <div
            className={
              "flex flex-col justify-center gap-1 rounded-lg border border-gray-200 px-2.5 py-2 " +
              (readiness?.setup
                ? "bg-slate-50 text-slate-500"
                : m.orphanedContacts
                  ? heatClass("single", 3)
                  : m.pending
                    ? heatClass("single", 1)
                    : heatClass("single", 0))
            }
            title="Orphaned contacts have no owner after a departure. Pending handoffs are waiting for the new owner to confirm."
          >
            <span className="text-[11px]">
              <b className="text-[15px]">
                {readiness?.setup ? "—" : m.orphanedContacts}
              </b>{" "}
              orphaned contact{m.orphanedContacts === 1 ? "" : "s"}
            </span>
            <span className="text-[11px]">
              <b className="text-[15px]">
                {readiness?.setup ? "—" : m.pending}
              </b>{" "}
              pending handoff{m.pending === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <div
            className="self-center text-[11px] font-bold text-gray-700"
            title="Each contact counted once, by its number of current owners."
          >
            Owners per contact
            <small className="block text-[10px] font-medium text-gray-400">
              contacts
            </small>
          </div>
          {ownerBandMeta.map(function (band, idx) {
            const n = ownerBandCounts[idx] || 0;
            const empty = Boolean(readiness?.setup) || !n;
            return (
              <button
                key={band.key}
                type="button"
                disabled={empty}
                className={
                  "flex items-baseline justify-between gap-2 rounded-lg border bg-white px-3 py-1.5 text-left text-[11px] text-gray-700 " +
                  band.tone +
                  (empty ? " cursor-default" : " hover:border-violet-500")
                }
                onClick={function () {
                  if (!empty) onOpenOwnerBand(accountId, band.key);
                }}
              >
                <span>{band.label}</span>
                <b className={empty ? "text-gray-400" : "text-gray-800"}>
                  {readiness?.setup ? "—" : n}
                </b>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <div
            className="self-center text-[11px] font-bold text-gray-700"
            title="Each current relationship counted once by its RL."
          >
            Relationship strength
            <small className="block text-[10px] font-medium text-gray-400">
              relationships
            </small>
          </div>
          {strengthMeta.map(function (item) {
            const n = readiness?.setup ? 0 : bands[item.band];
            const empty = !n;
            return (
              <button
                key={item.band}
                type="button"
                disabled={empty}
                className={
                  "flex items-baseline justify-between gap-2 rounded-lg border bg-white px-3 py-1.5 text-left text-[11px] text-gray-700 " +
                  item.tone +
                  (empty ? " cursor-default" : " hover:border-violet-500")
                }
                onClick={function () {
                  if (!empty) onOpenStrength(accountId, item.band);
                }}
              >
                <span>{item.label}</span>
                <b className={empty ? "text-gray-400" : "text-gray-800"}>
                  {readiness?.setup ? "—" : n}
                </b>
              </button>
            );
          })}
        </div>
        <div className="mt-2 border-t border-dashed border-gray-200 pt-3">
          <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold text-gray-700">
            Alignment progression
            <span className="text-[10px] font-normal text-gray-400">
              From Alignment; shown for context, not part of coverage
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {(
              [
                ["Contacts", funnel.total],
                ["Mapped", funnel.mapped],
                ["Assessed", funnel.assessed],
                ["Goaled", funnel.goaled],
                ["Progressing", funnel.progressing],
                ["Achieved", funnel.achieved],
              ] as [string, number][]
            ).map(function (stage) {
              return (
                <span key={stage[0]} className="text-[11px] text-gray-600">
                  <b className="mr-1 text-sm text-gray-900">{stage[1]}</b>
                  {stage[0]}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </CommonModal>
  );
}

const OWNER_RL_MIX_BANDS: {
  key: EvidenceBandKey;
  label: string;
  bar: string;
  className: string;
}[] = [
  {
    key: "unassessed",
    label: "Not assessed",
    bar: "#cbd5e1",
    className: "bg-gray-100 text-gray-700",
  },
  {
    key: "weak",
    label: "RL 0–1",
    bar: "#ef4444",
    className: "bg-red-50 text-red-800",
  },
  {
    key: "moderate",
    label: "RL 2–3",
    bar: "#f59e0b",
    className: "bg-amber-50 text-amber-900",
  },
  {
    key: "strong",
    label: "RL 4–5",
    bar: "#10b981",
    className: "bg-emerald-50 text-emerald-900",
  },
];

function fanInLabelForRow(row: CoverageRow): string {
  if (row.fan_in <= 1) return "only owner";
  return (
    "with " +
    String(row.fan_in - 1) +
    " other" +
    (row.fan_in - 1 === 1 ? "" : "s")
  );
}

function resolveOwnerPerson(
  ownerId: string,
  owners: OwnerMatrixPerson[],
  rows: CoverageRow[],
  ownerCapacities: OwnerCapacity[]
): OwnerMatrixPerson | null {
  const fromMatrix = owners.find(function (p) {
    return p.id === ownerId;
  });
  if (fromMatrix) {
    const capacity = ownerCapacities.find(function (c) {
      return c.owner_id === ownerId;
    });
    if (capacity?.on_roster && !fromMatrix.onRoster) {
      return { ...fromMatrix, onRoster: true };
    }
    return fromMatrix;
  }
  const sample = rows.find(function (row) {
    return row.owner_id === ownerId;
  });
  const capacity = ownerCapacities.find(function (c) {
    return c.owner_id === ownerId;
  });
  if (!sample && !capacity) return null;
  return {
    id: ownerId,
    name: sample?.owner_name || capacity?.name || "Unnamed owner",
    role: sample?.owner_role || capacity?.role || "",
    onRoster: Boolean(sample?.on_roster || capacity?.on_roster),
    reactive: sample ? isOpenDormant(sample) : false,
    localCap: capacity?.local_capacity ?? null,
    globalCap: capacity?.global_capacity ?? null,
  };
}

/** Shared column track for portfolio header + each account summary row. */
const OWNER_PORTFOLIO_GRID =
  "12px minmax(120px,1.5fr) 88px 100px 88px 118px 112px";

function OwnerPortfolioModal(props: {
  portfolio: {
    ownerId: string;
    focusAccountId?: string | null;
    filter?: OwnersPortfolioFilter | null;
  } | null;
  owners: OwnerMatrixPerson[];
  rows: CoverageRow[];
  accounts: CoverageAccount[];
  ownerCapacities: OwnerCapacity[];
  attentionItems: AttentionItem[];
  meddpiccOn: boolean;
  onClose: () => void;
  onSetFilter: (key: OwnersPortfolioFilter | "all" | null) => void;
  onToggleFilter: (key: OwnersPortfolioFilter) => void;
  onViewMap: (
    accountId: string,
    ownerId: string,
    accountName: string,
    ownerName: string,
    portfolioFilter?: string | null
  ) => void;
  onTeamViews: (owner: OwnerMatrixPerson) => void;
  onDepartureImpact: (owner: OwnerMatrixPerson) => void;
  onSeeRisks: (owner: OwnerMatrixPerson) => void;
}) {
  const {
    portfolio,
    owners,
    rows,
    accounts,
    ownerCapacities,
    attentionItems,
    meddpiccOn,
    onClose,
    onSetFilter,
    onToggleFilter,
    onViewMap,
    onTeamViews,
    onDepartureImpact,
    onSeeRisks,
  } = props;
  const focusRef = useRef<HTMLDetailsElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const canManageDepartureImpact = useTeamViewsPrivilegedAccess();
  const [sort, setSort] = useState<OwnerPortfolioSortKey>("worst");
  const [showAllContacts, setShowAllContacts] = useState<Set<string>>(
    function () {
      return new Set();
    }
  );
  const [openIds, setOpenIds] = useState<Set<string> | null>(null);
  const orgGrades = useAppSelector(function (state) {
    const raw = state.vendorAdmin.grades;
    if (Array.isArray(raw)) return raw;
    return raw?.items as { grade?: number; grade_name?: string }[] | undefined;
  });

  const ownerId = portfolio?.ownerId;
  const focusAccountId = portfolio?.focusAccountId;
  const rawFilter = portfolio?.filter ?? null;
  const owner = ownerId
    ? resolveOwnerPerson(ownerId, owners, rows, ownerCapacities)
    : null;

  const capacity = ownerId
    ? ownerCapacities.find(function (c) {
        return c.owner_id === ownerId;
      })
    : null;

  const stats = useMemo(
    function () {
      if (!ownerId || !owner) return null;
      return buildOwnerStats(ownerId, rows, capacity, {
        name: owner.name,
        role: owner.role,
        onRoster: owner.onRoster,
      });
    },
    [ownerId, owner, rows, capacity]
  );

  const departed = stats?.departed ?? false;
  // Departed owners cannot use over/only filters.
  const filter: OwnersPortfolioFilter | null =
    departed &&
    (rawFilter === "over" ||
      rawFilter === "only" ||
      rawFilter === "overCovered")
      ? null
      : rawFilter;

  const allAccounts = useMemo(
    function () {
      if (!stats) return [] as OwnerPortfolioAccountStats[];
      return buildOwnerPortfolioAccounts(stats);
    },
    [stats]
  );

  const accountNameOf = function (id: string) {
    return (
      accounts.find(function (a) {
        return a.account_id === id;
      })?.name ||
      allAccounts.find(function (a) {
        return a.id === id;
      })?.rows[0]?.account_name ||
      id
    );
  };

  const visible = useMemo(
    function () {
      const matched = allAccounts.filter(function (a) {
        return ownerPortfolioMatch(a, filter);
      });
      return sortOwnerPortfolioAccounts(matched, sort, accountNameOf);
    },
    // accountNameOf closes over accounts/allAccounts — recompute with those deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allAccounts, filter, sort, accounts]
  );

  // Reset local UI state when opening a different portfolio / filter.
  useEffect(
    function () {
      if (!portfolio) return;
      setShowAllContacts(new Set());
      setOpenIds(null);
      setSort("worst");
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    },
    [portfolio?.ownerId, portfolio?.filter, portfolio?.focusAccountId]
  );

  const effectiveOpen = useMemo(
    function () {
      if (openIds) return openIds;
      return ownerPortfolioDefaultOpenIds(visible, filter, focusAccountId);
    },
    [openIds, visible, filter, focusAccountId]
  );

  useEffect(
    function () {
      if (!portfolio || !focusAccountId) return;
      const el = focusRef.current;
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    },
    [portfolio, focusAccountId, visible.length]
  );

  if (!portfolio || !ownerId || !owner || !stats) return null;
  if (!stats.active.length && !stats.dormantOpen.length) return null;

  const list = departed ? stats.dormantOpen : stats.active;
  const showTeamViews = !departed || owner.onRoster;
  const overAccountCount = allAccounts.filter(function (a) {
    return a.over;
  }).length;
  const onlyAccountCount = allAccounts.filter(function (a) {
    return a.only > 0;
  }).length;
  const overCoveredAccountCount = allAccounts.filter(function (a) {
    return a.overCovered > 0;
  }).length;
  const unAssessedTotal = allAccounts.reduce(function (n, a) {
    return n + a.un;
  }, 0);
  const unAssessedAccounts = allAccounts.filter(function (a) {
    return ownerPortfolioMatch(a, "unassessed");
  }).length;
  const hitCol = filter ? OWNERS_PORTFOLIO_HIT_COL[filter] : null;
  const bandIdx =
    filter === "unassessed"
      ? 0
      : filter === "weak"
        ? 1
        : filter === "moderate"
          ? 2
          : filter === "strong"
            ? 3
            : -1;
  const bandFilter =
    filter === "weak" || filter === "moderate" || filter === "strong";
  const rowFilter = ownerPortfolioUsesRowFilter(filter);

  const gradeName = (orgGrades || []).find(function (item) {
    return Number(item.grade) === Number(capacity?.grade);
  })?.grade_name;
  const subtitle = [
    formatOwnerGradeLine(owner.role, capacity?.grade, gradeName),
    departed ? "Departed owner" : "",
    stats.accountIds.length +
      " account" +
      (stats.accountIds.length === 1 ? "" : "s"),
  ]
    .filter(Boolean)
    .join(" · ");

  const riskTypeCount = departed
    ? 0
    : new Set(
        buildRiskItems(attentionItems, { meddpiccOn: meddpiccOn })
          .filter(function (item) {
            return naOwnerMatch(item, owner.id);
          })
          .map(function (item) {
            return item.riskType;
          })
      ).size;

  const impactLabel = departed
    ? "Review departure impact →"
    : "Plan departure impact →";

  const tileOn = function (key: OwnersPortfolioFilter) {
    return filter === key;
  };
  const tileCls = function (key: OwnersPortfolioFilter, n: number) {
    if (!n) return "opacity-60";
    return (
      "cursor-pointer transition hover:ring-2 hover:ring-violet-200 " +
      (tileOn(key) ? "ring-2 ring-violet-400" : "")
    );
  };

  const expandAll = function (open: boolean) {
    setOpenIds(
      open
        ? new Set(
            visible.map(function (a) {
              return a.id;
            })
          )
        : new Set()
    );
  };

  const toggleShowAll = function (id: string) {
    setShowAllContacts(function (prev) {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Preserve currently open accounts across re-render.
    setOpenIds(new Set(effectiveOpen));
  };

  const pill = function (
    key: "all" | OwnersPortfolioFilter,
    label: string,
    n: number,
    sub: string,
    title: string
  ) {
    const active = (filter || "all") === key;
    const disabled = key !== "all" && !n;
    return (
      <button
        key={key}
        type="button"
        disabled={disabled}
        aria-pressed={active}
        title={title}
        className={
          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition " +
          (active
            ? "border-violet-400 bg-violet-50 text-violet-900"
            : disabled
              ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
              : "border-gray-200 bg-white text-gray-700 hover:border-violet-200")
        }
        onClick={function () {
          onSetFilter(key);
        }}
      >
        {label} <b>{n}</b>
        {sub ? (
          <small className="font-normal text-gray-500">{sub}</small>
        ) : null}
      </button>
    );
  };

  return (
    <CommonModal
      isOpen
      toggle={onClose}
      usePortal
      size="5xl"
      title={owner.name + " · Owner portfolio"}
      subtitle={subtitle}
      headerActions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {riskTypeCount ? (
            <Button
              variant="outline-primary"
              onClick={function () {
                onSeeRisks(owner);
              }}
            >
              See {owner.name}&apos;s risks →
            </Button>
          ) : null}
          {showTeamViews ? (
            <Button
              variant="outline-primary"
              onClick={function () {
                onTeamViews(owner);
              }}
            >
              Manage account roles →
            </Button>
          ) : null}
          {canManageDepartureImpact ? (
            <Button
              variant="primary"
              onClick={function () {
                onDepartureImpact(owner);
              }}
            >
              {impactLabel}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col gap-3 p-1">
        {departed ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This owner has left the account team. The relationships below are
            open Dormant records that still need a successor or a dismissal.
          </p>
        ) : null}

        {/* Summary tiles — clickable filters (synced with pills) */}
        <div
          className={
            "grid gap-3 " +
            (departed
              ? "md:grid-cols-[minmax(0,140px)_1fr]"
              : "md:grid-cols-[minmax(0,140px)_minmax(0,140px)_1fr]")
          }
        >
          <div
            role={overAccountCount && !departed ? "button" : undefined}
            tabIndex={overAccountCount && !departed ? 0 : undefined}
            aria-pressed={
              !departed && overAccountCount ? tileOn("over") : undefined
            }
            title={
              departed
                ? "Open Dormant relationships across all accounts"
                : overAccountCount
                  ? "Click to show accounts over the per-account limit" +
                    (tileOn("over") ? " — click again to clear" : "")
                  : "Current relationships across all accounts ÷ recommended global capacity"
            }
            className={
              "rounded-lg border border-gray-200 bg-white px-3 py-2 text-center " +
              (!departed ? tileCls("over", overAccountCount) : "")
            }
            onClick={
              !departed && overAccountCount
                ? function () {
                    onToggleFilter("over");
                  }
                : undefined
            }
            onKeyDown={
              !departed && overAccountCount
                ? function (e) {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onToggleFilter("over");
                    }
                  }
                : undefined
            }
          >
            <div
              className={
                "text-2xl font-bold " +
                (stats.overGlobal ? "text-red-700" : "text-gray-900")
              }
            >
              {departed
                ? stats.global
                : stats.global + " / " + (stats.globalCap ?? "—")}
            </div>
            <div className="text-xs font-semibold text-gray-800">
              {departed ? "Open relationships" : "Global load"}
            </div>
            <div className="text-[10px] text-gray-500">
              {departed
                ? "across all accounts"
                : "current relationships ÷ global capacity" +
                  (overAccountCount
                    ? " · " +
                      overAccountCount +
                      " " +
                      pluralUnit(overAccountCount, "account") +
                      " over per-account limit"
                    : "")}
            </div>
          </div>

          {!departed ? (
            <div
              role={stats.only ? "button" : undefined}
              tabIndex={stats.only ? 0 : undefined}
              aria-pressed={stats.only ? tileOn("only") : undefined}
              title={
                stats.only
                  ? "Contacts where " +
                    owner.name +
                    " is the only current owner" +
                    (tileOn("only") ? " — click again to clear" : "")
                  : "No single-owner contacts"
              }
              className={
                "rounded-lg border border-gray-200 bg-white px-3 py-2 text-center " +
                tileCls("only", stats.only)
              }
              onClick={
                stats.only
                  ? function () {
                      onToggleFilter("only");
                    }
                  : undefined
              }
              onKeyDown={
                stats.only
                  ? function (e) {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggleFilter("only");
                      }
                    }
                  : undefined
              }
            >
              <div className="text-2xl font-bold text-gray-900">
                {stats.only}
              </div>
              <div className="text-xs font-semibold text-gray-800">
                Only owner of {stats.only} contact
                {stats.only === 1 ? "" : "s"}
              </div>
              <div className="text-[10px] text-gray-500">
                across {onlyAccountCount}{" "}
                {pluralUnit(onlyAccountCount, "account")}
              </div>
            </div>
          ) : null}

          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="mb-2 text-xs font-semibold text-gray-800">
              Relationship strength mix · pairwise RL
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {OWNER_RL_MIX_BANDS.map(function (band) {
                const n = list.filter(function (r) {
                  return ownerPortfolioRowMatch(r, band.key);
                }).length;
                // unassessed uses rl==null which rowMatch handles
                const count =
                  band.key === "unassessed"
                    ? list.filter(function (r) {
                        return r.rl == null;
                      }).length
                    : n;
                return (
                  <div
                    key={band.key}
                    role={count ? "button" : undefined}
                    tabIndex={count ? 0 : undefined}
                    aria-pressed={count ? tileOn(band.key) : undefined}
                    title={
                      count
                        ? "Show the " +
                          count +
                          " " +
                          band.label +
                          " relationship" +
                          (count === 1 ? "" : "s") +
                          (tileOn(band.key) ? " — click again to clear" : "")
                        : band.label
                    }
                    className={
                      "rounded-md px-2 py-1.5 text-center " +
                      band.className +
                      " " +
                      tileCls(band.key, count)
                    }
                    onClick={
                      count
                        ? function () {
                            onToggleFilter(band.key);
                          }
                        : undefined
                    }
                    onKeyDown={
                      count
                        ? function (e) {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onToggleFilter(band.key);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="text-lg font-bold">{count}</div>
                    <div className="text-[10px] font-medium">{band.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pills — same filter key as tiles */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Show
          </span>
          {pill(
            "all",
            "All accounts",
            allAccounts.length,
            "",
            "Show every account in this portfolio"
          )}
          {!departed
            ? pill(
                "over",
                "Over limit",
                overAccountCount,
                overAccountCount === 1 ? "account" : "accounts",
                "Accounts where relationships exceed the per-account capacity (" +
                  String(stats.localCap ?? "—") +
                  ")"
              )
            : null}
          {!departed
            ? pill(
                "only",
                "Only owner",
                stats.only,
                "contacts in " +
                  onlyAccountCount +
                  " " +
                  pluralUnit(onlyAccountCount, "account"),
                "Contacts with no other current owner — single-owner exposure if " +
                  owner.name +
                  " leaves"
              )
            : null}
          {!departed
            ? pill(
                "overCovered",
                "Over-covered",
                stats.overCovered,
                "contacts in " +
                  overCoveredAccountCount +
                  " " +
                  pluralUnit(overCoveredAccountCount, "account"),
                "Contacts where this owner is one of " +
                  FAN_IN_THRESHOLD +
                  " or more current owners"
              )
            : null}
          {pill(
            "unassessed",
            "Not assessed",
            unAssessedTotal,
            "relationships in " +
              unAssessedAccounts +
              " " +
              pluralUnit(unAssessedAccounts, "account"),
            "Relationships with no pairwise RL yet"
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-600"
                aria-label="How these filters work"
              >
                ?
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={metricInfoClass}>
              <strong className="block text-gray-900">
                How these filters work
              </strong>
              <p className="mt-1">
                Each filter narrows the account list. The number is the same
                count as the tiles above, followed by how many accounts it
                spans.
              </p>
              <p className="mt-1">
                <b>Over limit</b> — accounts where this owner&apos;s
                relationships exceed the per-account capacity for their grade.
              </p>
              <p className="mt-1">
                <b>Only owner</b> — contacts with no other current owner.
              </p>
              <p className="mt-1">
                <b>Over-covered</b> — contacts where this owner is one of{" "}
                {FAN_IN_THRESHOLD} or more current owners.
              </p>
              <p className="mt-1">
                <b>Not assessed</b> — relationships with no pairwise RL yet.
              </p>
              <p className="mt-1">
                The tiles above are filters too: click one to apply it, click it
                again to clear.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Column labels + sort + expand — shared grid with each account summary */}
        <div
          className="grid items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
          style={{ gridTemplateColumns: OWNER_PORTFOLIO_GRID }}
        >
          <span aria-hidden />
          <span className="normal-case">
            Account
            <label className="ml-2 font-normal text-gray-400">
              · sort{" "}
              <select
                className="rounded border border-gray-200 bg-white px-1 py-0.5 text-[11px] text-gray-700"
                value={sort}
                onChange={function (e) {
                  setOpenIds(new Set(effectiveOpen));
                  setSort(e.target.value as OwnerPortfolioSortKey);
                }}
              >
                <option value="worst">Worst first</option>
                <option value="name">Account name</option>
                <option value="load">Load</option>
              </select>
            </label>
          </span>
          <span
            className={
              "text-center " +
              (hitCol === "load" ? "font-extrabold text-blue-700" : "")
            }
          >
            {departed ? "Open" : "Load / limit"}
          </span>
          <span
            className={
              "text-center " +
              (hitCol === "only" ? "font-extrabold text-blue-700" : "")
            }
          >
            Only owner of
          </span>
          <span
            className={
              "text-center " +
              (hitCol === "unassessed" ? "font-extrabold text-blue-700" : "")
            }
          >
            Not assessed
          </span>
          <span
            className={
              hitCol === "mix" ? "font-extrabold text-blue-700" : undefined
            }
          >
            Strength mix{" "}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-[9px] font-bold normal-case text-gray-600"
                  aria-label="How to read the strength mix bar"
                >
                  ?
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className={metricInfoClass}>
                <strong className="block text-gray-900">
                  Strength mix bar
                </strong>
                <p className="mt-1">
                  Each bar splits this owner&apos;s relationships in that
                  account by pairwise RL, left to right: Not assessed, RL 0–1,
                  RL 2–3, RL 4–5. Segment width is the share. Hover a bar for
                  the exact counts.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="justify-self-end normal-case font-medium text-[#974ae4]">
            <button
              type="button"
              className="hover:underline"
              onClick={function () {
                expandAll(true);
              }}
            >
              Expand
            </button>
            {" · "}
            <button
              type="button"
              className="hover:underline"
              onClick={function () {
                expandAll(false);
              }}
            >
              Collapse
            </button>
          </span>
        </div>

        <div
          ref={bodyRef}
          className="flex max-h-[min(52vh,420px)] flex-col gap-2 overflow-y-auto pr-1"
        >
          {!visible.length ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No accounts match this filter.
            </p>
          ) : (
            visible.map(function (a) {
              const accountName = accountNameOf(a.id);
              const isFocus = focusAccountId === a.id;
              const isOpen = effectiveOpen.has(a.id);
              const showAll = !rowFilter || showAllContacts.has(a.id);
              const shown = showAll
                ? a.rows
                : a.rows.filter(function (r) {
                    return ownerPortfolioRowMatch(r, filter);
                  });
              const matchCount = a.rows.filter(function (r) {
                return ownerPortfolioRowMatch(r, filter);
              }).length;
              const total = a.rows.length || 1;
              return (
                <details
                  key={a.id}
                  ref={isFocus ? focusRef : undefined}
                  open={isOpen}
                  className="rounded-lg border border-gray-200 bg-gray-50"
                  onToggle={function (e) {
                    const el = e.currentTarget;
                    setOpenIds(function (prev) {
                      const base = new Set(prev || effectiveOpen);
                      if (el.open) base.add(a.id);
                      else base.delete(a.id);
                      return base;
                    });
                  }}
                >
                  <summary
                    className="grid cursor-pointer list-none items-center gap-2 bg-gray-50 px-2.5 py-2 text-sm font-semibold text-gray-900 [&::-webkit-details-marker]:hidden"
                    style={{ gridTemplateColumns: OWNER_PORTFOLIO_GRID }}
                  >
                    <span className="text-[9px] text-gray-500" aria-hidden>
                      {isOpen ? "▼" : "▶"}
                    </span>
                    <span className="truncate">{accountName}</span>
                    <span
                      className={
                        "justify-self-center rounded px-1.5 py-0.5 text-center text-[11px] font-medium " +
                        (a.over
                          ? "bg-red-100 font-bold text-red-800"
                          : "text-gray-600") +
                        (hitCol === "load" ? " bg-blue-100 text-blue-700" : "")
                      }
                      title={
                        departed
                          ? "Open Dormant relationships left by this former owner"
                          : "Current relationships in this account ÷ recommended account capacity"
                      }
                    >
                      {departed
                        ? a.load + " open"
                        : a.load + " / " + (stats.localCap ?? "—")}
                    </span>
                    <span
                      className={
                        "justify-self-center text-center text-[11px] font-medium " +
                        (a.only ? "text-gray-800" : "text-gray-300") +
                        (hitCol === "only"
                          ? " rounded bg-blue-100 px-1.5 font-bold text-blue-700"
                          : "")
                      }
                      title="Contacts in this account with no other owner"
                    >
                      {departed ? "—" : a.only}
                    </span>
                    <span
                      className={
                        "justify-self-center text-center text-[11px] font-medium " +
                        (a.un ? "text-gray-800" : "text-gray-300") +
                        (hitCol === "unassessed"
                          ? " rounded bg-blue-100 px-1.5 font-bold text-blue-700"
                          : "")
                      }
                      title="Relationships in this account without a pairwise RL"
                    >
                      {a.un}
                    </span>
                    <span
                      className={
                        "flex min-w-0 items-center gap-1 " +
                        (bandFilter ? "font-bold text-blue-700" : "")
                      }
                    >
                      {bandFilter && bandIdx >= 0 ? (
                        <b className="shrink-0 rounded bg-blue-100 px-1.5 text-[10px] text-blue-700">
                          {a.bands[bandIdx]}
                        </b>
                      ) : null}
                      <span
                        className="flex h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-200"
                        title={
                          accountName +
                          " · " +
                          a.rows.length +
                          " relationship" +
                          (a.rows.length === 1 ? "" : "s") +
                          ", left to right: " +
                          OWNER_RL_MIX_BANDS.map(function (b, i) {
                            return b.label + " " + a.bands[i];
                          }).join(" · ")
                        }
                      >
                        {OWNER_RL_MIX_BANDS.map(function (b, i) {
                          if (!a.bands[i]) return null;
                          return (
                            <i
                              key={b.key}
                              className="block h-full"
                              style={{
                                width: (a.bands[i] / total) * 100 + "%",
                                background: b.bar,
                                opacity:
                                  bandIdx >= 0 && i !== bandIdx ? 0.25 : 1,
                              }}
                            />
                          );
                        })}
                      </span>
                    </span>
                    <Button
                      variant="outline-primary"
                      className="justify-self-end !px-2 !py-1 text-xs"
                      onClick={function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                        onViewMap(
                          a.id,
                          ownerId,
                          accountName,
                          owner.name,
                          rawFilter
                        );
                      }}
                    >
                      View on Map →
                    </Button>
                  </summary>
                  <div className="overflow-x-auto border-t border-gray-200 bg-white">
                    <table
                      className="w-full text-left text-xs"
                      style={{ tableLayout: "fixed" }}
                    >
                      <colgroup>
                        {departed ? (
                          <>
                            <col style={{ width: "32%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "32%" }} />
                          </>
                        ) : (
                          <>
                            <col style={{ width: "28%" }} />
                            <col style={{ width: "16%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "28%" }} />
                          </>
                        )}
                      </colgroup>
                      <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {departed ? (
                          <tr>
                            <th className="px-3 py-2">Contact / role</th>
                            <th className="px-3 py-2">Last RL</th>
                            <th className="min-w-[7.5rem] px-3 py-2">Status</th>
                            <th className="px-3 py-2">Current owners</th>
                          </tr>
                        ) : (
                          <tr>
                            <th className="px-3 py-2">Contact / role</th>
                            <th className="px-3 py-2">Pairwise RL</th>
                            <th className="px-3 py-2">Goal</th>
                            <th className="px-3 py-2">Fan-in</th>
                            <th className="min-w-[7.5rem] px-3 py-2">Status</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {shown.map(function (row) {
                          const matched =
                            rowFilter && ownerPortfolioRowMatch(row, filter);
                          const ownersLabel = row.current_owner_names?.length
                            ? row.current_owner_names.join(", ")
                            : "None";
                          return (
                            <tr
                              key={row.id}
                              className={
                                "border-t border-gray-100 " +
                                (matched ? "bg-violet-50/60" : "")
                              }
                            >
                              <td className="px-3 py-2">
                                {row.contact_name}
                                <div className="text-[10px] text-gray-500">
                                  {row.contact_role_label}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {row.rl != null
                                  ? "RL " + row.rl
                                  : "Not assessed"}
                              </td>
                              {departed ? (
                                <>
                                  <td className="min-w-[7.5rem] px-3 py-2">
                                    <CoverageStatusPill
                                      status={effectiveStatus(row)}
                                    />
                                  </td>
                                  <td className="px-3 py-2">{ownersLabel}</td>
                                </>
                              ) : (
                                <>
                                  <td className="px-3 py-2">
                                    {row.goal_target_rl != null
                                      ? "RL " + row.goal_target_rl
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-2">
                                    {fanInLabelForRow(row)}
                                  </td>
                                  <td className="min-w-[7.5rem] whitespace-nowrap px-3 py-2">
                                    {row.status === "pending" ? (
                                      <CoverageStatusPill
                                        status={effectiveStatus(row)}
                                      />
                                    ) : (
                                      "Current"
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {rowFilter &&
                    a.rows.some(function (r) {
                      return !ownerPortfolioRowMatch(r, filter);
                    }) ? (
                      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-[11px] text-gray-500">
                        <button
                          type="button"
                          className="font-medium text-[#974ae4] hover:underline"
                          onClick={function () {
                            toggleShowAll(a.id);
                          }}
                        >
                          {showAll
                            ? "Show matching contacts only"
                            : "Show all " + a.rows.length + " contacts"}
                        </button>
                        <span>
                          {matchCount} of {a.rows.length} match
                        </span>
                      </div>
                    ) : null}
                  </div>
                </details>
              );
            })
          )}
        </div>
      </div>
    </CommonModal>
  );
}

/* ================================================================== */
/*  Accounts                                                           */
/* ================================================================== */

function AccountsPane(props: {
  accounts: CoverageAccount[];
  rows: CoverageRow[];
  scoped: CoverageRow[];
  matching: CoverageRow[];
  filters: CoverageFilters;
  ownerCapacities: OwnerCapacity[];
  readinessByAccount: Record<string, AccountReadiness>;
  readinessSegment: string;
  meddpiccOn: boolean;
  recordsPanel: {
    accountId: string;
    source: string;
    key?: string | null;
  } | null;
  /** Click-through label, kept after the side list closes. */
  scopeFocusLabel: string | null;
  accountsMode: "heatmap" | "strength";
  accountsSort: { key: string; dir: "asc" | "desc" } | null;
  onAccountsMode: (mode: "heatmap" | "strength") => void;
  onSelect: (id: string | null, slide?: "coverage" | "strength") => void;
  /**
   * Setup CTAs in the Not set up heatmap group.
   * `category` is readiness category (`no-contacts`, `no-team`, …).
   */
  onSetupCta: (accountId: string, category: string) => void;
  onClearStatus: () => void;
  onStrengthBand: (band: EvidenceBandKey) => void;
  onSortColumn: (key: string) => void;
  onOpenAccountRisks: (accountId: string, riskType: string) => void;
  onOpenPanel: (panel: {
    accountId: string;
    source: string;
    key?: string | null;
  }) => void;
  onClosePanel: () => void;
  /** Open Relationships Account Relationships for an account. */
  onOpenRelationships: (accountId: string, contactId?: string) => void;
  /** Open Relationships Map view for an account (optional contact highlight). */
  onViewCoverageMap: (accountId: string, contactId?: string) => void;
}) {
  const {
    accounts,
    rows,
    scoped,
    matching,
    filters,
    ownerCapacities,
    readinessByAccount,
    readinessSegment,
    meddpiccOn,
    recordsPanel,
    scopeFocusLabel,
    onSelect,
    onSetupCta,
    onClearStatus,
    onStrengthBand,
    onSortColumn,
    onOpenAccountRisks,
    onOpenPanel,
    accountsMode,
    accountsSort,
    onAccountsMode,
    onClosePanel,
    onOpenRelationships,
    onViewCoverageMap,
  } = props;

  const evidenceBand = isEvidenceBandKey(filters.status)
    ? filters.status
    : null;

  const setupOnlySegment =
    readinessSegment === "setup" || readinessSegment === "not-set-up";

  /** Prototype coverageHeatGroups: Not set up collapsed, Set up open by default. */
  const [heatGroups, setHeatGroups] = useState({
    setup: false,
    ready: true,
  });

  useEffect(
    function () {
      if (setupOnlySegment) {
        setHeatGroups({ setup: true, ready: false });
      }
    },
    [setupOnlySegment]
  );

  const scopedIds = Array.from(
    new Set([
      ...accounts.map(function (a) {
        return a.account_id;
      }),
      ...scoped.map(function (r) {
        return r.account_id;
      }),
    ])
  ).filter(function (id) {
    if (filters.account !== "all" && id !== filters.account) return false;
    if (filters.role !== "all" || filters.owner !== "all") {
      return scoped.some(function (r) {
        return r.account_id === id;
      });
    }
    return true;
  });

  const matchingAccountIds = new Set(
    matching.map(function (r) {
      return r.account_id;
    })
  );

  // When drilling a non-setup readiness band (e.g. ready), hide the Not set up group.
  const hideSetupGroup =
    readinessSegment !== "all" &&
    !setupOnlySegment &&
    readinessSegment !== "setup";

  const setupIds = hideSetupGroup
    ? []
    : scopedIds.filter(function (id) {
        const setup = readinessByAccount[id]?.setup;
        if (!setup) return false;
        if (filters.status !== "all" && !matchingAccountIds.has(id))
          return false;
        return true;
      });
  const heatmapIds = scopedIds.filter(function (id) {
    return !readinessByAccount[id]?.setup;
  });

  let ids =
    filters.status !== "all"
      ? heatmapIds.filter(function (id) {
          return matchingAccountIds.has(id);
        })
      : heatmapIds;

  if (readinessSegment && readinessSegment !== "all") {
    if (setupOnlySegment) {
      ids = [];
    } else {
      ids = ids.filter(function (id) {
        return readinessByAccount[id]?.category === readinessSegment;
      });
    }
  }

  const setupOpen = heatGroups.setup || (!ids.length && setupIds.length > 0);
  const readyOpen = heatGroups.ready || (!setupIds.length && ids.length > 0);
  const setupSummaryNote = setupSummaryText(
    Object.fromEntries(
      setupIds
        .filter(function (id) {
          return readinessByAccount[id];
        })
        .map(function (id) {
          return [id, readinessByAccount[id]];
        })
    )
  );

  const panelRows = recordsPanel
    ? (recordsPanel.source === "records" ? matching : rows).filter(
        function (r) {
          return r.account_id === recordsPanel.accountId;
        }
      )
    : [];
  const panelAccount = recordsPanel
    ? nameOf(accounts, recordsPanel.accountId)
    : "";
  const panelReadiness = recordsPanel
    ? readinessByAccount[recordsPanel.accountId]
    : null;
  const panelBandRows =
    recordsPanel &&
    recordsPanel.source === "band" &&
    isEvidenceBandKey(recordsPanel.key || "")
      ? accountContactsByStrongestBand(
          recordsPanel.accountId,
          rows,
          recordsPanel.key as EvidenceBandKey
        )
      : [];
  const ownerPanelKey =
    recordsPanel?.source === "owners" &&
    (recordsPanel.key === "single" ||
      recordsPanel.key === "two" ||
      recordsPanel.key === "fanIn3")
      ? recordsPanel.key
      : null;
  const panelOwnerContactIds =
    recordsPanel && ownerPanelKey
      ? accountContactIdsByOwnerBand(
          recordsPanel.accountId,
          rows,
          ownerPanelKey
        )
      : [];
  const panelRecordContactIds = Array.from(
    new Set(
      panelRows.map(function (r) {
        return r.contact_id;
      })
    )
  );
  const panelRecordIds = new Set(
    panelRows.map(function (r) {
      return r.id;
    })
  );

  useEffect(
    function () {
      if (!recordsPanel) return;
      const onKey = function (e: KeyboardEvent) {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClosePanel();
        }
      };
      document.addEventListener("keydown", onKey, true);
      return function () {
        document.removeEventListener("keydown", onKey, true);
      };
    },
    [recordsPanel, onClosePanel]
  );

  if (!scopedIds.length) {
    return (
      <p className="text-sm text-gray-500">
        No accounts match the account, contact-role, and owner scope.
      </p>
    );
  }

  if (
    filters.status !== "all" &&
    !ids.length &&
    !setupIds.length &&
    !setupOnlySegment
  ) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-gray-500">
          No accounts in scope have contacts in{" "}
          <strong>
            {STATUS_LABELS[filters.status] || evidenceBand || filters.status}
          </strong>
          .
        </p>
        <Button
          variant="clear"
          className="!w-fit !text-xs !underline"
          onClick={onClearStatus}
        >
          Clear status
        </Button>
      </div>
    );
  }

  const heatTip = function (key: string, rec: any): string {
    const listContacts = " Click to list these contacts in a side panel.";
    const listOwners =
      " Click to list these contacts and their owners in a side panel.";
    if (key === "complete") {
      if (!rec.total) return "No contacts added";
      return (
        rec.coveredCount +
        " of " +
        rec.total +
        " contacts have a current or pending owner" +
        (rec.orphanedContacts
          ? "; " + rec.orphanedContacts + " orphaned by a departed owner"
          : "")
      );
    }
    if (key === "roster") {
      const noun = rec.roster === 1 ? "contact" : "contacts";
      if (!meddpiccOn) {
        return (
          rec.roster + " " + noun + " · " + rec.execTotal + " tagged Executive"
        );
      }
      const missing = ["EB", "DM", "CH"].filter(function (role) {
        return !rec.rolesPresent.includes(role);
      });
      return (
        rec.roster +
        " " +
        noun +
        " · key roles present: " +
        (rec.rolesPresent.join(", ") || "none") +
        " · " +
        (missing.length
          ? "Missing MEDDPICC roles: " + missing.join(", ")
          : "All MEDDPICC roles present")
      );
    }
    if (key === "roles") {
      if (!rec.classifiedTotal) return "No contacts with a MEDDPICC role";
      return (
        rec.classifiedCoveredCount +
        " of " +
        rec.classifiedTotal +
        " contacts with a role are covered"
      );
    }
    if (key === "executive") {
      if (!rec.execTotal) return "No Executive contacts tagged";
      return (
        rec.execCoveredCount +
        " of " +
        rec.execTotal +
        " Executive contacts have a current or pending owner"
      );
    }
    if (key === "assessed") {
      if (!rec.relTotal) return "No current relationships";
      return (
        rec.relAssessedCount +
        " of " +
        rec.relTotal +
        " current relationships are assessed"
      );
    }
    if (key === "strong" || key === "moderate" || key === "weak") {
      if (!rec.coveredCount) return "No covered contacts to measure";
      const range =
        key === "strong" ? "RL 4–5" : key === "moderate" ? "RL 2–3" : "RL 0–1";
      const count =
        key === "strong"
          ? rec.strongCount
          : key === "moderate"
            ? rec.moderateCount
            : rec.weakCount;
      return (
        count +
        " of " +
        rec.coveredCount +
        " covered contacts have a strongest relationship of " +
        range +
        "." +
        listContacts
      );
    }
    if (key === "single" || key === "two" || key === "fanIn3") {
      const phrase =
        key === "single"
          ? "one owner"
          : key === "two"
            ? "two owners"
            : FAN_IN_THRESHOLD + " or more owners";
      const count = rec[key];
      const base =
        count + " contact" + (count === 1 ? "" : "s") + " covered by " + phrase;
      return count > 0 ? base + "." + listOwners : base;
    }
    if (key === "concentration") {
      if (!rec.activePairs) return "No current relationships";
      return (
        (rec.concentrationOwner || "The top owner") +
        " holds " +
        rec.concentrationCount +
        " of " +
        rec.activePairs +
        " current relationships"
      );
    }
    return "";
  };

  const fillCell = function (
    key: string,
    value: number | null,
    primary: React.ReactNode,
    secondary: React.ReactNode,
    rec: any,
    onClick: () => void,
    tone?: string
  ) {
    return (
      <button
        type="button"
        title={heatTip(key, rec)}
        className={"cw-heat-fill " + (tone || heatClass(key, value))}
        onClick={onClick}
      >
        <span className="text-sm font-semibold">{primary}</span>
        {secondary}
      </button>
    );
  };

  const pctCell = function (
    key: string,
    value: number | null,
    countLabel: string,
    rec: any,
    onClick: () => void,
    extra?: React.ReactNode
  ) {
    return fillCell(
      key,
      value,
      formatMetricPct(value),
      <>
        {value == null ? null : <small>{countLabel}</small>}
        {extra}
      </>,
      rec,
      onClick
    );
  };

  const openRow = function (id: string) {
    onSelect(id);
  };

  type HeatmapColumn = {
    key: string;
    title: React.ReactNode;
    width?: string;
    className?: string;
    render: (_v: any, rec: any) => React.ReactNode;
  };

  const roleColTitle = meddpiccOn
    ? "Strategic-role coverage"
    : "Executive coverage";

  const recordFilterActive = coverageRecordFilterActive(
    filters,
    readinessSegment
  );

  const columnTitle = function (key: string, label: string, sub: string) {
    const active = accountsSort?.key === key;
    const headerTips: Record<string, string> = {
      complete:
        "Contacts with at least one current or pending owner ÷ all contacts in the account. Each contact counted once. Red subline: contacts whose owner departed and who now have no owner." +
        (meddpiccOn
          ? ""
          : " With MEDDPICC off this is the main breadth signal: the share of contacts with at least one owner."),
      roster:
        "Contacts added to the account. With MEDDPICC on, the Buying Committee roles present (EB, DM, CH); the tooltip lists missing MEDDPICC roles. With MEDDPICC off, the number of Executive contacts.",
      roles:
        "Covered contacts that have a MEDDPICC role ÷ all contacts with a role. Each contact counted once.",
      executive:
        "Executive contacts with at least one current or pending owner ÷ all Executive contacts. Shown when MEDDPICC is off. Shows — until contacts are tagged with the Executive role.",
      assessed:
        "Current owner–contact relationships with an RL assessment ÷ all current relationships in the account. Each pairwise relationship counted once, so a contact with two owners needs two assessments.",
      strong:
        "Covered contacts whose strongest relationship is RL 4–5 ÷ covered contacts. Each contact counted once, using its highest pairwise RL.",
      moderate:
        "Covered contacts whose strongest relationship is RL 2–3 ÷ covered contacts. Each contact counted once, using its highest pairwise RL. Strong + Moderate + Weak = covered contacts with at least one assessed relationship.",
      weak: "Covered contacts whose strongest relationship is RL 0–1 ÷ covered contacts. Each contact counted once, using its highest pairwise RL; contacts not yet assessed are not counted as weak.",
      single: "Count of contacts covered by exactly one current owner.",
      two:
        FAN_IN_THRESHOLD === 3
          ? "Count of contacts covered by exactly two current owners: the resilience target."
          : "Count of contacts covered by 2 to " +
            (FAN_IN_THRESHOLD - 1) +
            " current owners: the resilience target, below the fan-in threshold (" +
            FAN_IN_THRESHOLD +
            ").",
      fanIn3:
        "Count of contacts covered by " +
        FAN_IN_THRESHOLD +
        " or more current owners (fan-in threshold in Coverage settings). Flagged for review, not as a failure.",
      concentration:
        "The most-loaded owner's current relationships in this account ÷ all current owner–contact relationships in the account.",
    };
    const listsContacts = [
      "strong",
      "moderate",
      "weak",
      "single",
      "two",
      "fanIn3",
    ].includes(key);
    const tip =
      (headerTips[key] || "") +
      (listsContacts
        ? " Click a cell to list these contacts in a side panel."
        : "") +
      " Click to sort; click again to reverse.";
    return (
      <button
        type="button"
        className="flex flex-col items-start text-left"
        title={tip}
        onClick={function () {
          onSortColumn(key);
        }}
      >
        <span>
          {label}
          {active ? (accountsSort?.dir === "asc" ? " ▲" : " ▼") : ""}
        </span>
        <span className="text-[10px] font-normal text-gray-500">{sub}</span>
      </button>
    );
  };

  const ownerCell = function (
    key: "single" | "two" | "fanIn3",
    value: number,
    rec: { account_id: string }
  ) {
    return fillCell(key, value, value, null, rec, function () {
      if (value > 0) {
        onOpenPanel({
          accountId: rec.account_id,
          source: "owners",
          key: key,
        });
        return;
      }
      openRow(rec.account_id);
    });
  };

  const heatmapColumns: HeatmapColumn[] = [
    {
      key: "name",
      title: "Account",
      width: "220px",
      render: function (_v: any, rec: any) {
        const showFilterBadge = recordFilterActive && rec.recordCount > 0;
        const showWhyBadge =
          !recordFilterActive &&
          PANEL_READINESS_CATEGORIES.includes(readinessSegment) &&
          rec.readiness?.category === readinessSegment;
        const badgeLabel = heatRecordBadgeLabel(
          filters.status,
          rec.matchingRows
        );
        return (
          <div className="flex flex-col gap-0.5">
            <div
              role="link"
              tabIndex={0}
              className="cursor-pointer truncate text-left font-medium text-gray-900 hover:underline"
              title={rec.name}
              onClick={function () {
                openRow(rec.account_id);
              }}
              onKeyDown={function (e: React.KeyboardEvent) {
                if (e.key === "Enter") openRow(rec.account_id);
              }}
            >
              {rec.name}
            </div>
            {showFilterBadge ? (
              <button
                type="button"
                className="mt-0.5 inline-flex w-fit items-center rounded-full border border-violet-300 bg-white px-2 py-px text-[10px] font-bold text-violet-700 hover:bg-violet-50"
                title={"Show the " + badgeLabel + " in a side panel"}
                onClick={function () {
                  onOpenPanel({
                    accountId: rec.account_id,
                    source: "records",
                  });
                }}
              >
                {badgeLabel} ▸
              </button>
            ) : null}
            {showWhyBadge ? (
              <button
                type="button"
                className="w-fit text-left text-[11px] font-medium text-[#974ae4] hover:underline"
                title={
                  "Show why " + rec.name + " is in this readiness category"
                }
                onClick={function () {
                  onOpenPanel({
                    accountId: rec.account_id,
                    source: "why",
                    key: rec.readiness.category,
                  });
                }}
              >
                Why →
              </button>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "complete",
      title: columnTitle(
        "complete",
        "Contact coverage",
        "covered ÷ all contacts"
      ),
      width: "120px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "complete",
          rec.complete,
          rec.coveredCount + "/" + rec.total,
          rec,
          function () {
            openRow(rec.account_id);
          },
          rec.orphanedContacts ? (
            <small className="font-semibold text-red-600">
              {rec.orphanedContacts} orphaned
            </small>
          ) : null
        );
      },
    },
    {
      key: "roster",
      title: columnTitle("roster", "Roster", "contacts · key roles present"),
      width: "120px",
      render: function (_v: any, rec: any) {
        const rosterTone = !meddpiccOn
          ? "bg-white text-slate-600"
          : rec.rolesPresent.length >= 3
            ? "bg-emerald-50 text-emerald-800"
            : "bg-amber-50 text-amber-800";
        return fillCell(
          "roster",
          rec.roster,
          rec.roster,
          <small>
            {meddpiccOn
              ? rec.rolesPresent.length
                ? rec.rolesPresent.join(" ")
                : "no key roles"
              : rec.execTotal + " Executive"}
          </small>,
          rec,
          function () {
            openRow(rec.account_id);
          },
          rosterTone
        );
      },
    },
    {
      key: meddpiccOn ? "roles" : "executive",
      title: columnTitle(
        meddpiccOn ? "roles" : "executive",
        roleColTitle,
        meddpiccOn
          ? "covered ÷ classified contacts"
          : "covered ÷ Executive contacts"
      ),
      width: "140px",
      render: function (_v: any, rec: any) {
        return pctCell(
          meddpiccOn ? "roles" : "executive",
          meddpiccOn ? rec.roles : rec.executive,
          meddpiccOn
            ? rec.classifiedCoveredCount + "/" + rec.classifiedTotal
            : rec.execCoveredCount + "/" + rec.execTotal,
          rec,
          function () {
            openRow(rec.account_id);
          }
        );
      },
    },
    {
      key: "assessed",
      title: columnTitle(
        "assessed",
        "Assessment visibility",
        "assessed ÷ current relationships"
      ),
      width: "130px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "assessed",
          rec.assessed,
          rec.relAssessedCount + "/" + rec.relTotal,
          rec,
          function () {
            openRow(rec.account_id);
          }
        );
      },
    },
    {
      key: "strong",
      title: columnTitle(
        "strong",
        "Strong RL 4–5",
        "strong ÷ covered contacts"
      ),
      width: "110px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "strong",
          rec.strong,
          rec.strongCount + "/" + rec.coveredCount,
          rec,
          function () {
            onOpenPanel({
              accountId: rec.account_id,
              source: "band",
              key: "strong",
            });
          }
        );
      },
    },
    {
      key: "moderate",
      title: columnTitle(
        "moderate",
        "Moderate RL 2–3",
        "moderate ÷ covered contacts"
      ),
      width: "120px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "moderate",
          rec.moderate,
          rec.moderateCount + "/" + rec.coveredCount,
          rec,
          function () {
            onOpenPanel({
              accountId: rec.account_id,
              source: "band",
              key: "moderate",
            });
          }
        );
      },
    },
    {
      key: "weak",
      title: columnTitle("weak", "Weak RL 0–1", "weak ÷ covered contacts"),
      width: "110px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "weak",
          rec.weak,
          rec.weakCount + "/" + rec.coveredCount,
          rec,
          function () {
            onOpenPanel({
              accountId: rec.account_id,
              source: "band",
              key: "weak",
            });
          }
        );
      },
    },
    {
      key: "single",
      title: columnTitle("single", "Single owner", "contacts with one owner"),
      width: "110px",
      render: function (_v: any, rec: any) {
        return ownerCell("single", rec.single, rec);
      },
    },
    {
      key: "two",
      title: columnTitle("two", "Two owners", "contacts with two owners"),
      width: "110px",
      render: function (_v: any, rec: any) {
        return ownerCell("two", rec.two, rec);
      },
    },
    {
      key: "fanIn3",
      title: columnTitle("fanIn3", "3+ owners", "contacts with 3+ owners"),
      width: "110px",
      render: function (_v: any, rec: any) {
        return ownerCell("fanIn3", rec.fanIn3, rec);
      },
    },
    {
      key: "concentration",
      title: columnTitle(
        "concentration",
        "Owner concentration",
        "top owner ÷ all relationships"
      ),
      width: "130px",
      render: function (_v: any, rec: any) {
        return pctCell(
          "concentration",
          rec.concentration,
          rec.concentrationCount + "/" + rec.activePairs,
          rec,
          function () {
            openRow(rec.account_id);
          },
          rec.concentrationOwner ? (
            <small className="font-semibold">{rec.concentrationOwner}</small>
          ) : null
        );
      },
    },
  ];

  heatmapColumns.forEach(function (column) {
    if (column.key !== "name") column.className = "cw-heat-td";
  });

  const heatmapData = ids
    .map(function (id) {
      const account = accounts.find(function (a) {
        return a.account_id === id;
      });
      const m = accountMetrics(id, rows, {
        teamSize: account?.team_size ?? 0,
        ownerCapacities: ownerCapacities,
      });
      const n = nameOf(accounts, id);
      // Filter badge counts matching scope rows; otherwise unused (Why badge instead).
      const matchingRows = matching.filter(function (r) {
        return r.account_id === id;
      });
      const contactCount = new Set(
        matchingRows.map(function (r) {
          return r.contact_id;
        })
      ).size;
      const roleSet = new Set<string>();
      rows.forEach(function (row) {
        if (row.account_id !== id) return;
        const code = (row.contact_role || "").toUpperCase();
        if ((KEY_MEDDPICC_ROLES as readonly string[]).includes(code)) {
          roleSet.add(code);
        }
      });
      const concentrationOwner =
        rows.find(function (row) {
          return (
            row.account_id === id && row.owner_id === m.concentrationOwnerId
          );
        })?.owner_name || "";
      return {
        account_id: id,
        name: n,
        roster: m.total,
        rolesPresent: KEY_MEDDPICC_ROLES.filter(function (role) {
          return roleSet.has(role);
        }),
        concentrationOwner: concentrationOwner,
        complete: m.complete,
        roles: m.roles,
        executive: m.executive,
        assessed: m.assessed,
        strong: m.strong,
        moderate: m.moderate,
        weak: m.weak,
        concentration: m.concentration,
        coveredCount: m.coveredCount,
        total: m.total,
        classifiedCoveredCount: m.classifiedCoveredCount,
        classifiedTotal: m.classifiedTotal,
        execCoveredCount: m.execCoveredCount,
        execTotal: m.execTotal,
        assessedCount: m.assessedCount,
        relAssessedCount: m.relAssessedCount,
        relTotal: m.relTotal,
        strongCount: m.strongCount,
        moderateCount: m.moderateCount,
        weakCount: m.weakCount,
        single: m.single,
        two: m.two,
        fanIn3: m.fanIn3,
        orphanedContacts: m.orphanedContacts,
        concentrationCount: m.concentrationCount,
        activePairs: m.activePairs,
        teamSize: m.teamSize,
        recordCount: matchingRows.length,
        matchingRows: matchingRows,
        contactCount: contactCount,
        readiness: readinessByAccount[id] || null,
      };
    })
    .sort(function (a, b) {
      const activeSort =
        accountsSort ||
        (recordFilterActive
          ? { key: "recordCount", dir: "desc" as const }
          : null);
      if (activeSort) {
        const key = activeSort.key as keyof typeof a;
        const av = Number(a[key] ?? 0);
        const bv = Number(b[key] ?? 0);
        const diff = (av - bv) * (activeSort.dir === "asc" ? 1 : -1);
        if (diff !== 0) return diff;
        if (activeSort.key === "complete") {
          const orphans = b.orphanedContacts - a.orphanedContacts;
          if (orphans !== 0) return orphans;
        }
      } else if (filters.status === "two-owners") {
        const diff = b.two - a.two;
        if (diff !== 0) return diff;
      } else if (evidenceBand) {
        const ae = accountEvidenceBands(a.account_id, rows);
        const be = accountEvidenceBands(b.account_id, rows);
        const diff = be[evidenceBand] - ae[evidenceBand];
        if (diff !== 0) return diff;
      }
      return a.name.localeCompare(b.name);
    });

  const heatmapCaption = recordFilterActive
    ? "Figures are whole-account; click the count under an account's name to see its matching contacts"
    : accountsSort?.key === "complete"
      ? "Sorted lowest contact coverage first. Figures are whole-account."
      : accountsSort?.key === "single" || filters.status === "single-owner"
        ? "Sorted by Single owner. Click a Single owner cell to see those contacts. Figures are whole-account."
        : filters.status === "two-owners"
          ? "Sorted by the Two owners column. Click a Two owners cell to see those contacts. Figures are whole-account."
          : filters.status === "active"
            ? "Current counts active relationships, not a resilience band. Figures are whole-account."
            : evidenceBand
              ? "Figures are whole-account; click an RL cell to list matching contacts"
              : readinessSegment !== "all" && !setupOnlySegment
                ? "Figures are whole-account; open Why on an account for readiness detail"
                : "Whole-account metrics; click a header to sort, a name for account coverage, or an RL or owner-count cell to list its contacts";

  const bandCounts: Record<EvidenceBandKey, number> = {
    unassessed: 0,
    weak: 0,
    moderate: 0,
    strong: 0,
  };
  scoped.forEach(function (row) {
    STRENGTH_BAND_ORDER.forEach(function (band) {
      if (rowMatchesEvidenceBand(row, band)) bandCounts[band] += 1;
    });
  });
  const selectedStrengthBand: EvidenceBandKey | null =
    evidenceBand ||
    (accountsMode === "strength"
      ? STRENGTH_BAND_ORDER.find(function (band) {
          return bandCounts[band] > 0;
        }) || null
      : null);

  const strengthPills = (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label="Relationship strength bands"
    >
      {STRENGTH_BAND_STRIP_ORDER.map(function (band) {
        const active = selectedStrengthBand === band;
        const cnt = bandCounts[band];
        return (
          <button
            key={band}
            type="button"
            disabled={!cnt}
            aria-pressed={active}
            title={
              band === "unassessed"
                ? "Mapped, RL not yet assessed"
                : band === "weak"
                  ? "Assessed weak relationship"
                  : band === "moderate"
                    ? "Assessed moderate relationship"
                    : "Assessed strong relationship"
            }
            className={
              "inline-flex items-baseline gap-1.5 rounded-full border bg-white px-3 py-1 text-[11px] text-gray-700 " +
              (active
                ? "border-[#974ae4] shadow-[0_0_0_2px_#974ae4] "
                : STRENGTH_BAND_CHIP_BORDER[band] + " ") +
              (cnt ? "hover:brightness-95" : "cursor-default opacity-45")
            }
            onClick={function () {
              if (cnt) onStrengthBand(band);
            }}
          >
            <span>{evidenceBandLabel(band)}</span>
            <b className="text-xs text-gray-900">{cnt}</b>
          </button>
        );
      })}
    </div>
  );

  const modeSwitch = (
    <TabsComponent
      tabs={[
        { id: "heatmap", label: "Heatmap" },
        { id: "strength", label: "Relationship strength" },
      ]}
      activeTab={accountsMode}
      onTabChange={function (id: "heatmap" | "strength") {
        onAccountsMode(id);
      }}
      variant="tab-underline"
      className="mb-0"
    />
  );

  return (
    <div className="relative flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div>
            <strong className="text-sm text-gray-900">
              {accountsMode === "heatmap"
                ? "Account coverage heatmap"
                : "Relationship strength"}
            </strong>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {accountsMode === "heatmap"
                ? heatmapCaption
                : "Current relationships in scope, each counted once by its RL. Select a band to list its relationships by account."}
              {evidenceBand
                ? " · " + STATUS_LABELS[evidenceBand]
                : readinessSegment !== "all"
                  ? " · " +
                    (STATUS_LABELS[readinessSegment] || readinessSegment)
                  : ""}
            </p>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {modeSwitch}
          {accountsMode === "strength" ? strengthPills : null}
        </div>

        {accountsMode === "heatmap" ? (
          <div className="flex flex-col gap-0">
            {setupIds.length ? (
              <>
                <button
                  type="button"
                  className={
                    "flex w-full items-center gap-2 rounded-t-md border px-3 py-2 text-left text-xs " +
                    (setupOpen
                      ? "border-red-200 bg-red-50 text-red-900"
                      : "border-red-100 bg-red-50/70 text-red-800")
                  }
                  aria-expanded={setupOpen}
                  title={
                    setupOpen ? "Collapse this group" : "Expand this group"
                  }
                  onClick={function () {
                    setHeatGroups(function (prev) {
                      return { ...prev, setup: !prev.setup };
                    });
                  }}
                >
                  <span className="inline-block w-3 text-[10px] text-gray-700">
                    {setupOpen ? "▼" : "▶"}
                  </span>
                  <strong>Not set up ({setupIds.length})</strong>
                  <span className="font-normal text-red-700/80">
                    {setupOpen
                      ? "Coverage cannot be measured until the account has contacts, an account team and mapped relationships."
                      : setupSummaryNote ||
                        "Needs contacts, team, and mapped relationships"}
                  </span>
                </button>
                {setupOpen ? (
                  <div className="border border-t-0 border-red-100 bg-white">
                    {setupIds.map(function (id) {
                      const rd = readinessByAccount[id];
                      const category = rd?.category || "";
                      const action =
                        category === "no-contacts"
                          ? "Add contacts →"
                          : category === "no-team"
                            ? "Set up account team →"
                            : "Map relationships →";
                      return (
                        <div
                          key={id}
                          className="flex flex-wrap items-center gap-2 border-b border-red-50 px-3 py-2 text-xs last:border-b-0"
                        >
                          <button
                            type="button"
                            className="font-semibold text-purple-700 hover:underline"
                            onClick={function () {
                              openRow(id);
                            }}
                          >
                            {nameOf(accounts, id)}
                          </button>
                          <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-800">
                            {SETUP_LABELS[category] ||
                              STATUS_LABELS[category] ||
                              category}
                          </span>
                          <span className="min-w-0 flex-1 text-gray-500">
                            {(rd?.reasons || []).join(" · ") ||
                              "Not ready for coverage health"}
                            {" · "}
                            {(rd?.contacts ?? 0) +
                              " " +
                              pluralUnit(rd?.contacts ?? 0, "contact")}
                            {" · "}
                            {(rd?.teamSize ?? 0) +
                              " team " +
                              pluralUnit(rd?.teamSize ?? 0, "member")}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 font-medium text-[#974ae4] hover:underline"
                            onClick={function () {
                              onSetupCta(id, category);
                            }}
                          >
                            {action}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </>
            ) : null}

            {!setupOnlySegment && ids.length ? (
              <>
                {setupIds.length ? (
                  <button
                    type="button"
                    className={
                      "mt-1 flex w-full items-center gap-2 border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs text-gray-800 " +
                      (setupOpen ? "" : "rounded-t-md")
                    }
                    aria-expanded={readyOpen}
                    title={
                      readyOpen ? "Collapse this group" : "Expand this group"
                    }
                    onClick={function () {
                      setHeatGroups(function (prev) {
                        return { ...prev, ready: !prev.ready };
                      });
                    }}
                  >
                    <span className="inline-block w-3 text-[10px] text-gray-700">
                      {readyOpen ? "▼" : "▶"}
                    </span>
                    <strong>Set up ({ids.length})</strong>
                    <span className="font-normal text-gray-500">
                      Whole-account coverage metrics.
                    </span>
                  </button>
                ) : null}
                {readyOpen || !setupIds.length ? (
                  <div
                    className={
                      setupIds.length ? "border border-t-0 border-gray-200" : ""
                    }
                  >
                    <Table
                      className="coverage-accounts-matrix"
                      columns={heatmapColumns}
                      data={heatmapData}
                      rowKey={function (rec: any) {
                        return rec.account_id;
                      }}
                      stickyFirstColumn
                    />
                  </div>
                ) : null}
              </>
            ) : null}

            {!setupIds.length && !ids.length ? (
              <p className="text-sm text-gray-500">
                No accounts match the current Accounts filters.
              </p>
            ) : null}

            <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
              <strong className="text-gray-800">How to read this:</strong>{" "}
              percentage columns are shares of the account&apos;s contacts,
              using each contact&apos;s best relationship for RL, except
              Assessment visibility, which is a share of the account&apos;s
              relationships because each owner–contact pair needs its own
              assessment. Single owner, Two owners, and 3+ owners are contact
              counts; click one to list those contacts. Owner concentration is
              the top owner&apos;s share of relationships. Strong + Moderate +
              Weak add up to covered contacts with an assessed relationship. —
              means there is nothing to measure yet. Green is healthy, amber
              needs review, and red needs attention.
            </div>
          </div>
        ) : (
          <StrengthListPane
            accounts={accounts}
            rows={rows}
            scoped={scoped}
            filters={filters}
            ids={ids.length ? ids : heatmapIds}
            ownerCapacities={ownerCapacities}
            readinessByAccount={readinessByAccount}
            onSelect={function (id) {
              onSelect(id, "strength");
            }}
            onClearStatus={onClearStatus}
            onOpenRelationships={onOpenRelationships}
            selectedBand={selectedStrengthBand}
          />
        )}
      </div>

      {recordsPanel ? (
        <div
          className="fixed bottom-0 right-0 top-0 z-40 flex w-full max-w-[440px] flex-col border-l border-gray-200 bg-white shadow-xl"
          style={{ animation: "cwPanelIn 180ms ease-out" }}
        >
          <style>
            {
              "@keyframes cwPanelIn{from{transform:translateX(24px);opacity:.72}to{transform:none;opacity:1}}"
            }
          </style>
          <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div>
              <strong className="text-sm text-gray-900">
                {panelAccount}
                {recordsPanel.source === "why" && panelReadiness
                  ? " · " +
                    (STATUS_LABELS[panelReadiness.category] ||
                      panelReadiness.category)
                  : recordsPanel.source === "band"
                    ? " · " +
                      (STATUS_LABELS[recordsPanel.key || ""] ||
                        recordsPanel.key) +
                      " contacts"
                    : recordsPanel.source === "owners"
                      ? " · Contacts with " +
                        (recordsPanel.key === "single"
                          ? "a single owner"
                          : recordsPanel.key === "two"
                            ? "two owners"
                            : "3+ owners")
                      : recordsPanel.source === "records"
                        ? " · " +
                          (STATUS_LABELS[filters.status] || filters.status)
                        : ""}
              </strong>
              <div className="mt-0.5 text-xs leading-snug text-gray-500">
                {recordsPanel.source === "why"
                  ? "Why " +
                    panelAccount +
                    " is in this readiness category. Each account is counted once under its worst condition."
                  : recordsPanel.source === "band"
                    ? panelBandRows.length +
                      " of the account's covered contacts, counted once by their strongest RL."
                    : recordsPanel.source === "owners"
                      ? panelOwnerContactIds.length +
                        " " +
                        pluralUnit(panelOwnerContactIds.length, "contact") +
                        ". Each card lists the contact's current owners."
                      : panelRows.length +
                        " relationships · " +
                        panelRecordContactIds.length +
                        " " +
                        pluralUnit(panelRecordContactIds.length, "contact") +
                        ". Highlighted rows match the filter; other relationships are shown faded."}
                {scopeFocusLabel
                  ? " Closing this list keeps Accounts on " +
                    scopeFocusLabel +
                    ". Clear Focus above the heatmap to show every account again."
                  : ""}
              </div>
            </div>
            <button
              type="button"
              className="text-xs font-medium text-gray-500 hover:text-gray-800"
              onClick={onClosePanel}
            >
              Close
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 text-xs">
            {recordsPanel.source === "why" && panelReadiness ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-1.5">
                  {panelReadiness.reasons.map(function (reason, i) {
                    return (
                      <span
                        key={i}
                        className="inline-flex max-w-full rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] leading-snug text-amber-900"
                      >
                        {reason}
                      </span>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                  <Button
                    variant="outline-primary"
                    className="!text-xs"
                    onClick={function () {
                      onOpenRelationships(recordsPanel.accountId);
                    }}
                  >
                    Open Account Relationships →
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="!text-xs"
                    onClick={function () {
                      onSelect(recordsPanel.accountId);
                      onClosePanel();
                    }}
                  >
                    Open {panelAccount} coverage
                  </Button>
                </div>
              </div>
            ) : null}
            {recordsPanel.source === "band" ? (
              <div className="flex flex-col gap-2">
                {!panelBandRows.length ? (
                  <p className="text-gray-500">
                    No contacts in this band for {panelAccount}.
                  </p>
                ) : (
                  panelBandRows.map(function (sample) {
                    const contactId = sample.contact_id;
                    const contactRels = rows.filter(function (r) {
                      return (
                        r.account_id === recordsPanel.accountId &&
                        r.contact_id === contactId
                      );
                    });
                    const bandKey = recordsPanel.key as EvidenceBandKey;
                    const bestRl = sample.rl;
                    const why =
                      bandKey === "unassessed"
                        ? "Counted as Not assessed because they have no assessed relationship."
                        : bestRl != null
                          ? "Counted as " +
                            evidenceBandLabel(bandKey) +
                            " because their best relationship is RL " +
                            bestRl +
                            "."
                          : undefined;
                    return (
                      <ContactRelationshipCard
                        key={contactId}
                        contactName={sample.contact_name}
                        contactMeta={
                          sample.contact_role_label || sample.contact_role
                        }
                        relationships={contactRels}
                        isMatch={function (r) {
                          return rowMatchesEvidenceBand(r, bandKey);
                        }}
                        onViewMap={function () {
                          onViewCoverageMap(recordsPanel.accountId, contactId);
                        }}
                        onOpenMatrix={function () {
                          onOpenRelationships(
                            recordsPanel.accountId,
                            contactId
                          );
                        }}
                        why={why}
                      />
                    );
                  })
                )}
              </div>
            ) : null}
            {recordsPanel.source === "owners" ? (
              <div className="flex flex-col gap-2">
                {!panelOwnerContactIds.length ? (
                  <p className="text-gray-500">
                    No contacts in {panelAccount} match this owner count.
                  </p>
                ) : (
                  panelOwnerContactIds.map(function (contactId) {
                    const contactRels = rows.filter(function (r) {
                      return (
                        r.account_id === recordsPanel.accountId &&
                        r.contact_id === contactId &&
                        r.lifecycle_state === "active"
                      );
                    });
                    const sample = contactRels[0];
                    return (
                      <ContactRelationshipCard
                        key={contactId}
                        contactName={sample?.contact_name || contactId}
                        contactMeta={
                          sample?.contact_role_label || sample?.contact_role
                        }
                        relationships={contactRels}
                        isMatch={function () {
                          return true;
                        }}
                        onOpenMatrix={function () {
                          onOpenRelationships(
                            recordsPanel.accountId,
                            contactId
                          );
                        }}
                      />
                    );
                  })
                )}
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                  {recordsPanel.key === "single" ||
                  recordsPanel.key === "fanIn3" ? (
                    <Button
                      variant="outline-primary"
                      className="!text-xs"
                      onClick={function () {
                        onOpenAccountRisks(
                          recordsPanel.accountId,
                          recordsPanel.key === "fanIn3"
                            ? "fan-in"
                            : "single-owner"
                        );
                      }}
                    >
                      {recordsPanel.key === "fanIn3"
                        ? "See in Risks › Fan-in →"
                        : "See in Risks › Single-owner →"}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline-primary"
                    className="!text-xs"
                    onClick={function () {
                      onSelect(recordsPanel.accountId);
                      onClosePanel();
                    }}
                  >
                    Open {panelAccount} coverage
                  </Button>
                </div>
              </div>
            ) : null}
            {recordsPanel.source === "records" ? (
              <div className="flex flex-col gap-2">
                {!panelRecordContactIds.length ? (
                  <p className="text-gray-500">
                    No matching records in this account.
                  </p>
                ) : (
                  panelRecordContactIds.map(function (contactId) {
                    const contactRels = rows.filter(function (r) {
                      return (
                        r.account_id === recordsPanel.accountId &&
                        r.contact_id === contactId
                      );
                    });
                    const sample =
                      contactRels[0] ||
                      panelRows.find(function (r) {
                        return r.contact_id === contactId;
                      });
                    return (
                      <ContactRelationshipCard
                        key={contactId}
                        contactName={sample?.contact_name || contactId}
                        contactMeta={
                          sample?.contact_role_label || sample?.contact_role
                        }
                        relationships={contactRels}
                        isMatch={function (r) {
                          return panelRecordIds.has(r.id);
                        }}
                        onViewMap={function () {
                          onViewCoverageMap(recordsPanel.accountId, contactId);
                        }}
                        onOpenMatrix={function () {
                          onOpenRelationships(
                            recordsPanel.accountId,
                            contactId
                          );
                        }}
                      />
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Strength list pane (inside AccountsPane)                           */
/* ================================================================== */

function StrengthListPane(props: {
  accounts: CoverageAccount[];
  rows: CoverageRow[];
  scoped: CoverageRow[];
  filters: CoverageFilters;
  ids: string[];
  ownerCapacities: OwnerCapacity[];
  readinessByAccount: Record<string, AccountReadiness>;
  onSelect: (id: string) => void;
  onClearStatus: () => void;
  onOpenRelationships: (accountId: string, contactId?: string) => void;
  selectedBand: EvidenceBandKey | null;
}) {
  const {
    accounts,
    rows,
    filters,
    ids,
    onSelect,
    onOpenRelationships,
    selectedBand,
  } = props;

  /** Prototype: strength account groups open by default (collapsed set empty). */
  const [collapsedStrengthIds, setCollapsedStrengthIds] = useState<
    Record<string, boolean>
  >({});

  const activeBand = isEvidenceBandKey(filters.status) ? filters.status : null;

  /* Accounts in ids that have pairs in the selected band */
  const accountsForBand = selectedBand
    ? ids.filter(function (id) {
        return accountPairsByStrengthBand(id, rows, selectedBand).length > 0;
      })
    : ids;

  return (
    <div className="flex flex-col gap-3">
      {selectedBand && !activeBand ? (
        <p className="text-[11px] text-gray-500">
          Showing {evidenceBandLabel(selectedBand)}, the weakest band with
          relationships. Select another band above.
        </p>
      ) : null}

      {/* Account groups with contacts in band */}
      {!accountsForBand.length ? (
        <p className="text-sm text-gray-500">
          No accounts have relationships in this strength band.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {accountsForBand.map(function (id) {
            const name = nameOf(accounts, id);
            const bandRows = selectedBand
              ? accountPairsByStrengthBand(id, rows, selectedBand)
              : [];
            const isOpen = !collapsedStrengthIds[id];
            return (
              <details
                key={id}
                open={isOpen}
                className="rounded-lg border border-gray-200 bg-gray-50"
                onToggle={function (e) {
                  const nextOpen = (e.currentTarget as HTMLDetailsElement).open;
                  setCollapsedStrengthIds(function (prev) {
                    if (!nextOpen === Boolean(prev[id])) return prev;
                    return { ...prev, [id]: !nextOpen };
                  });
                }}
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 text-[10px] text-gray-700"
                      aria-hidden
                    >
                      {isOpen ? "▼" : "▶"}
                    </span>
                    <button
                      type="button"
                      className="text-purple-700 hover:underline"
                      onClick={function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        onSelect(id);
                      }}
                    >
                      {name}
                    </button>
                    <span className="text-[11px] font-normal text-gray-500">
                      {bandRows.length}{" "}
                      {selectedBand
                        ? evidenceBandLabel(selectedBand) +
                          " " +
                          pluralUnit(bandRows.length, "relationship")
                        : pluralUnit(bandRows.length, "relationship")}
                      {" · "}
                      {
                        new Set(
                          bandRows.map(function (r) {
                            return r.contact_id;
                          })
                        ).size
                      }{" "}
                      {pluralUnit(
                        new Set(
                          bandRows.map(function (r) {
                            return r.contact_id;
                          })
                        ).size,
                        "contact"
                      )}
                    </span>
                  </span>
                </summary>
                <div className="border-t border-gray-200 bg-white px-2 py-2 pl-5">
                  {/* Prototype .cw-str-cards — auto-fill columns, cards stay ~300px (not full-bleed). */}
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(300px, 1fr))",
                    }}
                  >
                    {Array.from(
                      new Set(
                        bandRows.map(function (r) {
                          return r.contact_id;
                        })
                      )
                    ).map(function (contactId) {
                      const contactRels = rows.filter(function (r) {
                        return (
                          r.account_id === id && r.contact_id === contactId
                        );
                      });
                      const sample =
                        contactRels[0] ||
                        bandRows.find(function (r) {
                          return r.contact_id === contactId;
                        });
                      const bandIds = new Set(
                        bandRows.map(function (r) {
                          return r.id;
                        })
                      );
                      return (
                        <ContactRelationshipCard
                          key={contactId}
                          contactName={sample?.contact_name || contactId}
                          contactMeta={
                            sample?.contact_role_label || sample?.contact_role
                          }
                          relationships={contactRels}
                          isMatch={function (r) {
                            return bandIds.has(r.id);
                          }}
                          onOpenMatrix={function () {
                            onOpenRelationships(id, contactId);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Departure Impact                                                   */
/* ================================================================== */

type ImpactSuccessorCandidate = {
  id: string;
  name: string;
  role: string;
  /** True when this person already has an active pair to the contact. */
  alreadyCovers: boolean;
};

type ImpactDraftItem = {
  id: string;
  relationshipId: string;
  accountId: string;
  accountName: string;
  contactId: string;
  contactName: string;
  contactRoleLabel: string;
  rl: number | null;
  goalTargetRl: number | null;
  fanIn: number;
  successorId: string;
  candidates: ImpactSuccessorCandidate[];
};

function impactSourceRows(
  ownerId: string,
  mode: "prospective" | "reactive",
  rows: CoverageRow[]
): CoverageRow[] {
  return rows.filter(function (row) {
    if (row.owner_id !== ownerId) return false;
    if (mode === "reactive") return isOpenDormant(row);
    return row.lifecycle_state === "active";
  });
}

function successorCandidatesFor(
  accountId: string,
  ownerId: string,
  contactId: string,
  rows: CoverageRow[],
  owners: OwnerMatrixPerson[]
): ImpactSuccessorCandidate[] {
  const byId = new Map<string, ImpactSuccessorCandidate>();
  rows.forEach(function (row) {
    if (
      row.account_id !== accountId ||
      row.lifecycle_state !== "active" ||
      !row.owner_id ||
      row.owner_id === ownerId
    ) {
      return;
    }
    const existing = byId.get(row.owner_id);
    const coversThisContact = row.contact_id === contactId;
    if (existing) {
      if (coversThisContact) {
        existing.alreadyCovers = true;
      }
      return;
    }
    const person = owners.find(function (o) {
      return o.id === row.owner_id;
    });
    byId.set(row.owner_id, {
      id: row.owner_id,
      name: person?.name || row.owner_name || "Owner",
      role: formatCoverageOwnerRole(person?.role || row.owner_role || ""),
      alreadyCovers: coversThisContact,
    });
  });
  return Array.from(byId.values()).sort(function (a, b) {
    if (a.alreadyCovers !== b.alreadyCovers) {
      return a.alreadyCovers ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
}

function DepartureImpactModal(props: {
  draft: { ownerId: string; mode: "prospective" | "reactive" } | null;
  owners: OwnerMatrixPerson[];
  rows: CoverageRow[];
  accounts: CoverageAccount[];
  ownerCapacities: OwnerCapacity[];
  onClose: () => void;
  onConfirmPlan: (summary: {
    ownerName: string;
    accountCount: number;
    relationshipCount: number;
  }) => void;
}) {
  const {
    draft,
    owners,
    rows,
    accounts,
    ownerCapacities,
    onClose,
    onConfirmPlan,
  } = props;
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<"scope" | "assign" | "preview">("scope");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [items, setItems] = useState<ImpactDraftItem[]>([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(
    function () {
      if (!draft) {
        setStep("scope");
        setSelectedAccountIds([]);
        setItems([]);
        return;
      }
      const source = impactSourceRows(draft.ownerId, draft.mode, rows);
      const nextItems = source
        .filter(function (row) {
          return Boolean(row.relationship_id);
        })
        .map(function (row) {
          return {
            id: row.id,
            relationshipId: String(row.relationship_id),
            accountId: row.account_id,
            accountName: row.account_name,
            contactId: row.contact_id,
            contactName: row.contact_name,
            contactRoleLabel: row.contact_role_label,
            rl: row.rl ?? null,
            goalTargetRl: row.goal_target_rl ?? null,
            fanIn: row.fan_in,
            successorId: "",
            candidates: successorCandidatesFor(
              row.account_id,
              draft.ownerId,
              row.contact_id,
              rows,
              owners
            ),
          };
        });
      setItems(nextItems);
      setSelectedAccountIds(
        Array.from(
          new Set(
            nextItems.map(function (item) {
              return item.accountId;
            })
          )
        )
      );
      setStep("scope");
    },
    [draft, rows, owners]
  );

  if (!draft) return null;

  const owner = resolveOwnerPerson(
    draft.ownerId,
    owners,
    rows,
    ownerCapacities
  );
  if (!owner) return null;

  const allAccountIds = Array.from(
    new Set(
      items.map(function (item) {
        return item.accountId;
      })
    )
  );
  const selectedItems = items.filter(function (item) {
    return selectedAccountIds.indexOf(item.accountId) >= 0;
  });
  const selectedAccounts = Array.from(
    new Set(
      selectedItems.map(function (item) {
        return item.accountId;
      })
    )
  );

  const beforeGaps = rows.filter(function (row) {
    return row.status === "open-orphaned";
  }).length;
  const beforeSingle = new Set(
    rows
      .filter(function (row) {
        return row.lifecycle_state === "active" && row.single_owner;
      })
      .map(function (row) {
        return row.account_id + ":" + row.contact_id;
      })
  ).size;

  // Prospective: departing pairs leave coverage unless a successor is chosen
  // and no other active owner remains on that contact.
  let afterGaps = beforeGaps;
  let afterSingle = beforeSingle;
  if (draft.mode === "prospective") {
    const orphanKeys = new Set<string>();
    selectedItems.forEach(function (item) {
      if (item.successorId) return;
      const others = rows.some(function (row) {
        return (
          row.account_id === item.accountId &&
          row.contact_id === item.contactId &&
          row.lifecycle_state === "active" &&
          row.owner_id &&
          row.owner_id !== draft.ownerId
        );
      });
      if (!others) {
        orphanKeys.add(item.accountId + ":" + item.contactId);
      }
    });
    afterGaps = beforeGaps + orphanKeys.size;
    afterSingle = Math.max(0, beforeSingle - selectedItems.length);
  } else {
    const resolvedOrphans = selectedItems.filter(function (item) {
      return item.successorId;
    }).length;
    afterGaps = Math.max(0, beforeGaps - resolvedOrphans);
  }

  const pendingConfirm = selectedItems.filter(function (item) {
    return item.successorId && (item.rl != null || item.goalTargetRl != null);
  }).length;

  const capacityWarnings: string[] = [];
  const successors = Array.from(
    new Set(
      selectedItems
        .filter(function (item) {
          return item.successorId;
        })
        .map(function (item) {
          return item.successorId;
        })
    )
  );
  successors.forEach(function (successorId) {
    const person = resolveOwnerPerson(
      successorId,
      owners,
      rows,
      ownerCapacities
    );
    const added = selectedItems.filter(function (item) {
      return item.successorId === successorId;
    }).length;
    const global = globalFanOutFor(successorId, rows) + added;
    if (person?.globalCap != null && global > person.globalCap) {
      capacityWarnings.push(
        (person.name || "Successor") +
          ": global " +
          global +
          "/" +
          person.globalCap
      );
    }
  });

  const stepIndex = step === "scope" ? 0 : step === "assign" ? 1 : 2;
  const impactSteps = [
    {
      key: "scope",
      title: "Scope accounts",
      description:
        "Select the accounts to include in this coordinated plan. Deselected accounts remain unchanged.",
    },
    {
      key: "assign",
      title: "Assign relationships",
      description:
        "Choose a successor for each relationship, or leave it Dormant for follow-up.",
    },
    {
      key: "preview",
      title: "Preview impact",
      description:
        "Review projected orphans, single-owner exposure, and capacity flags, then confirm the plan.",
    },
  ];
  const displayStepNumber = stepIndex + 1;
  const activeStepMeta = impactSteps[stepIndex];
  const modeLabel = draft.mode === "prospective" ? "Prospective" : "Reactive";

  const handleConfirm = async function () {
    if (!selectedAccounts.length) {
      toast.warning("Select at least one affected account before confirming.");
      return;
    }
    const missingRel = selectedItems.some(function (item) {
      return !item.relationshipId;
    });
    if (missingRel) {
      toast.error(
        "Some relationships are missing ids and cannot be transferred. Refresh Coverage and try again."
      );
      return;
    }

    setConfirming(true);
    const accountCount = selectedAccounts.length;
    const relationshipCount = selectedItems.length;

    try {
      const decisions = selectedItems.map(function (item) {
        const chosen = item.candidates.find(function (c) {
          return c.id === item.successorId;
        });
        // Collisions are not transferable — treat as Leave Dormant.
        const successorId =
          item.successorId && !chosen?.alreadyCovers ? item.successorId : null;
        return {
          relationship_id: item.relationshipId,
          account_id: item.accountId,
          successor_user_id: successorId,
        };
      });

      await dispatch(
        applyDepartureImpact({
          departingOwnerId: draft.ownerId,
          decisions: decisions,
          handoffNote:
            "Departure Impact plan for " + owner.name + " (" + draft.mode + ")",
        })
      ).unwrap();

      selectedAccounts.forEach(function (accountId) {
        dispatch(invalidateRelationshipMatrixCache(accountId));
      });

      toast.success(
        "Coverage plan confirmed across " +
          accountCount +
          " account" +
          (accountCount === 1 ? "" : "s") +
          ". Dormant history preserved; inherited context is pending confirmation where transferred."
      );
      onConfirmPlan({
        ownerName: owner.name,
        accountCount: accountCount,
        relationshipCount: relationshipCount,
      });
    } catch (err: any) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || "Could not apply the coordinated plan.";
      toast.error(message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <CommonModal
      isOpen
      toggle={onClose}
      usePortal
      size="5xl"
      contentClassName="overflow-y-auto"
      title={"Departure Impact — " + owner.name}
      subtitle={
        modeLabel +
        " · " +
        selectedAccounts.length +
        "/" +
        allAccountIds.length +
        " accounts · " +
        selectedItems.length +
        "/" +
        items.length +
        " relationships"
      }
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="secondary"
            disabled={step === "scope" || confirming}
            icon={<ChevronLeft className="h-4 w-4" />}
            onClick={function () {
              if (step === "preview") setStep("assign");
              else if (step === "assign") setStep("scope");
            }}
          >
            Back
          </Button>
          <div className="flex flex-wrap justify-end gap-2">
            {step === "scope" ? (
              <>
                <Button variant="outline-primary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={!selectedAccounts.length}
                  icon={<ChevronRight className="h-4 w-4" />}
                  onClick={function () {
                    setStep("assign");
                  }}
                >
                  {"Continue (" + selectedAccounts.length + " accounts)"}
                </Button>
              </>
            ) : null}
            {step === "assign" ? (
              <Button
                variant="primary"
                icon={<ChevronRight className="h-4 w-4" />}
                onClick={function () {
                  setStep("preview");
                }}
              >
                Preview impact
              </Button>
            ) : null}
            {step === "preview" ? (
              <Button
                variant="primary"
                loading={confirming}
                disabled={confirming}
                icon={<CheckCircle2 className="h-4 w-4" />}
                onClick={handleConfirm}
              >
                Confirm coordinated plan
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Match OnboardingWizard: circles + connector only; labels in a second equal row */}
        <div>
          <div className="relative flex items-center justify-between px-1">
            <div className="pointer-events-none absolute left-[18px] right-[18px] top-1/2 flex h-0.5 -translate-y-1/2">
              {Array.from({ length: impactSteps.length - 1 }).map(
                function (_, segIdx) {
                  const n = segIdx + 1;
                  const segmentDone = displayStepNumber > n;
                  return (
                    <div
                      key={"seg-" + n}
                      className={
                        "min-w-0 flex-1 rounded " +
                        (segmentDone ? "bg-emerald-400" : "bg-gray-200")
                      }
                    />
                  );
                }
              )}
            </div>
            {impactSteps.map(function (s, idx) {
              const n = idx + 1;
              const active = displayStepNumber === n;
              const done = displayStepNumber > n;
              return (
                <div
                  key={s.key}
                  className={
                    "relative z-10 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums transition-colors " +
                    (done
                      ? "bg-emerald-500 text-white"
                      : active
                        ? "bg-purple-600 text-white ring-4 ring-purple-100 ring-offset-2"
                        : "bg-gray-100 text-gray-500")
                  }
                >
                  {done ? "✓" : n}
                </div>
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 px-1">
            {impactSteps.map(function (s, idx) {
              const n = idx + 1;
              const active = displayStepNumber === n;
              const done = displayStepNumber > n;
              return (
                <div
                  key={s.key + "-label"}
                  className={
                    "text-center text-[11px] font-semibold " +
                    (active
                      ? "text-purple-700"
                      : done
                        ? "text-emerald-700"
                        : "text-gray-400")
                  }
                >
                  {s.title}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {activeStepMeta.title}
          </h3>
          <p className="mt-0.5 text-sm leading-relaxed text-gray-500">
            {activeStepMeta.description}
          </p>
        </div>

        {!items.length ? (
          <p className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
            No affected relationships for this owner and mode.
          </p>
        ) : null}

        {items.length && step === "scope" ? (
          <div className="flex flex-col gap-2">
            {allAccountIds.map(function (accountId) {
              const accountItems = items.filter(function (item) {
                return item.accountId === accountId;
              });
              const checked = selectedAccountIds.indexOf(accountId) >= 0;
              const accountName =
                accounts.find(function (a) {
                  return a.account_id === accountId;
                })?.name ||
                accountItems[0]?.accountName ||
                accountId;
              const atRisk = accountItems.filter(function (item) {
                return (
                  (item.rl != null && item.rl >= 4) || item.goalTargetRl != null
                );
              }).length;
              const m = accountMetrics(accountId, rows, {
                teamSize:
                  accounts.find(function (a) {
                    return a.account_id === accountId;
                  })?.team_size ?? 0,
                ownerCapacities: ownerCapacities,
              });
              return (
                <label
                  key={accountId}
                  className={
                    "flex cursor-pointer gap-3 rounded-xl border bg-white px-3 py-3 shadow-sm transition " +
                    (checked
                      ? "border-purple-300 ring-2 ring-purple-100"
                      : "border-gray-200 hover:border-gray-300")
                  }
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-purple-600"
                    checked={checked}
                    onChange={function (e) {
                      const on = e.target.checked;
                      setSelectedAccountIds(function (prev) {
                        if (on) {
                          return prev.indexOf(accountId) >= 0
                            ? prev
                            : prev.concat([accountId]);
                        }
                        return prev.filter(function (id) {
                          return id !== accountId;
                        });
                      });
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {accountName}
                      </span>
                      <Badge
                        type="primary-border"
                        label={
                          accountItems.length +
                          " relationship" +
                          (accountItems.length === 1 ? "" : "s")
                        }
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatMetricPct(m.complete)} complete · {m.single}{" "}
                      single-owner · {m.gaps} open gap
                      {m.gaps === 1 ? "" : "s"}
                    </p>
                    {atRisk ? (
                      <p className="mt-1 text-xs font-medium text-amber-800">
                        {atRisk} high-RL / goal-context relationship
                        {atRisk === 1 ? "" : "s"} at risk
                      </p>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>
        ) : null}

        {items.length && (step === "assign" || step === "preview") ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                title="True orphans"
                value={beforeGaps + " → " + afterGaps}
                subtitle="Before → after"
                borderType={afterGaps > beforeGaps ? "red" : "green"}
                className="h-full"
              />
              <MetricCard
                title="Single-owner exposure"
                value={beforeSingle + " → " + afterSingle}
                subtitle="Before → after"
                borderType="orange"
                className="h-full"
              />
              <MetricCard
                title="Pending confirmations"
                value={pendingConfirm}
                subtitle="RL / goal inherit"
                borderType="purple"
                className="h-full"
              />
              <MetricCard
                title="Capacity flags"
                value={capacityWarnings.length}
                subtitle="Soft warnings"
                borderType={capacityWarnings.length ? "yellow" : "blue"}
                className="h-full"
              />
            </div>

            {step === "preview" ? (
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
                <strong>Review locked choices.</strong> Confirm applies warm
                handoffs for chosen successors and leaves the rest Dormant on
                this account.
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              {selectedAccounts.map(function (accountId) {
                const accountItems = items
                  .map(function (item, index) {
                    return { item: item, index: index };
                  })
                  .filter(function (entry) {
                    return entry.item.accountId === accountId;
                  });
                const accountName =
                  accounts.find(function (a) {
                    return a.account_id === accountId;
                  })?.name ||
                  accountItems[0]?.item.accountName ||
                  accountId;
                return (
                  <div
                    key={accountId}
                    className="rounded-xl border border-gray-200 bg-white shadow-sm"
                  >
                    <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {accountName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {accountItems.length} relationships
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 p-3">
                      {accountItems.map(function (entry) {
                        const item = entry.item;
                        const eligibleCandidates = item.candidates.filter(
                          function (c) {
                            return !c.alreadyCovers;
                          }
                        );
                        const otherOwnerCoversContact = item.candidates.some(
                          function (c) {
                            return c.alreadyCovers;
                          }
                        );
                        const successorOptions = [
                          {
                            value: "",
                            label: eligibleCandidates.length
                              ? "Leave Dormant"
                              : "No eligible in-account successor",
                          },
                        ].concat(
                          item.candidates.map(function (c) {
                            const base =
                              c.name + (c.role ? " — " + c.role : "");
                            return {
                              value: c.id,
                              label: c.alreadyCovers
                                ? base + " (already covers this contact)"
                                : base,
                              disabled: c.alreadyCovers,
                            };
                          })
                        );
                        const selectedLabel =
                          successorOptions.find(function (o) {
                            return o.value === item.successorId;
                          })?.label || "Leave Dormant";
                        const helperText = item.successorId
                          ? "Successor staged · fan-in " +
                            item.fanIn +
                            " · RL/goal may need confirm"
                          : otherOwnerCoversContact
                            ? "Leave Dormant — another owner already covers this contact"
                            : draft.mode === "prospective"
                              ? "Leave Dormant — may become a true orphan"
                              : "Leave open Dormant — still needs a successor";
                        return (
                          <div
                            key={item.id}
                            className="grid gap-3 rounded-lg border border-gray-100 bg-slate-50/80 px-3 py-2.5 md:grid-cols-[minmax(0,1.1fr)_minmax(200px,240px)_minmax(0,1fr)] md:items-center"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                {item.contactName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {item.contactRoleLabel}
                                {" · "}
                                {item.rl != null
                                  ? "RL " + item.rl
                                  : "Unassessed"}
                                {item.goalTargetRl != null
                                  ? " · goal RL" + item.goalTargetRl
                                  : ""}
                              </div>
                            </div>
                            {step === "preview" ? (
                              <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs font-medium text-gray-800">
                                {selectedLabel}
                              </div>
                            ) : (
                              <DropdownSelector
                                label="Successor"
                                hideLabel
                                compact
                                fullWidth
                                triggerStyle="field"
                                selected={item.successorId}
                                options={successorOptions}
                                onSelect={function (value) {
                                  const chosen = item.candidates.find(
                                    function (c) {
                                      return c.id === value;
                                    }
                                  );
                                  if (chosen?.alreadyCovers) return;
                                  setItems(function (prev) {
                                    return prev.map(function (row, idx) {
                                      if (idx !== entry.index) return row;
                                      return {
                                        ...row,
                                        successorId: value || "",
                                      };
                                    });
                                  });
                                }}
                              />
                            )}
                            <p
                              className={
                                "text-xs leading-snug " +
                                (item.successorId
                                  ? "text-amber-900"
                                  : otherOwnerCoversContact
                                    ? "text-gray-600"
                                    : "text-amber-900")
                              }
                            >
                              {helperText}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {capacityWarnings.length ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Non-blocking follow-ups</strong>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {capacityWarnings.map(function (w) {
                    return <li key={w}>Capacity recalibration · {w}</li>;
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
}

/* ================================================================== */
/*  Owners                                                             */
/* ================================================================== */

const OWNER_SUM_COLS: {
  key: OwnersSortKey;
  label: string;
  sub: string;
}[] = [
  { key: "name", label: "Owner", sub: "" },
  { key: "accounts", label: "Accounts", sub: "with relationships" },
  { key: "load", label: "Portfolio load", sub: "active / global capacity" },
  { key: "over", label: "Accounts over limit", sub: "above account capacity" },
  {
    key: "highest",
    label: "Highest per-account load",
    sub: "active / account capacity",
  },
  { key: "only", label: "Only owner of", sub: "contacts" },
  {
    key: "overCovered",
    label: "On over-covered contacts",
    sub: "relationships at fan-in",
  },
  { key: "unassessed", label: "Not assessed", sub: "relationships" },
];

function OwnersPane(props: {
  owners: OwnerMatrixPerson[];
  accountColumns: { id: string; name: string }[];
  rows: CoverageRow[];
  allRows: CoverageRow[];
  ownerCapacities: OwnerCapacity[];
  accounts: CoverageAccount[];
  scopeAccountId?: string | null;
  ownersMode: OwnersMode;
  ownersFocus: "capacity" | null;
  ownersSort: OwnersSortState;
  ownersGridSort: string | null;
  ownersHighlight: string | null;
  capacityConfigured: boolean;
  capacityByGrade: GradeCapacity[];
  capacitySettingsOpen: boolean;
  onOpenCapacitySettings: () => void;
  onCloseCapacitySettings: () => void;
  onSetMode: (mode: OwnersMode) => void;
  onSort: (key: OwnersSortKey) => void;
  onGridSort: (key: string) => void;
  onViewInGrid: (ownerId: string) => void;
  onOpenPortfolio: (
    ownerId: string,
    focusAccountId?: string | null,
    filter?: OwnersPortfolioFilter | null
  ) => void;
  onOpenTeamViews: (owner: { id: string; name: string }) => void;
  onDepartureImpact: (ownerId: string) => void;
}) {
  const {
    owners,
    accountColumns,
    rows,
    allRows,
    ownerCapacities,
    accounts,
    scopeAccountId,
    ownersMode,
    ownersFocus,
    ownersSort,
    ownersGridSort,
    ownersHighlight,
    capacityConfigured,
    capacityByGrade,
    capacitySettingsOpen,
    onOpenCapacitySettings,
    onCloseCapacitySettings,
    onSetMode,
    onSort,
    onGridSort,
    onViewInGrid,
    onOpenPortfolio,
    onOpenTeamViews,
    onDepartureImpact,
  } = props;

  const canManageDepartureImpact = useTeamViewsPrivilegedAccess();
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const gradeDispatch = useAppDispatch();
  const orgGrades = useAppSelector(function (state) {
    const raw = state.vendorAdmin.grades;
    if (Array.isArray(raw)) return raw;
    return raw?.items as { grade?: number; grade_name?: string }[] | undefined;
  });

  useEffect(
    function () {
      if (!orgGrades) gradeDispatch(fetchVendorAdminGrades());
    },
    [gradeDispatch, orgGrades]
  );

  const gradeLevelLabel = function (grade: number) {
    const match = (orgGrades || []).find(function (item) {
      return Number(item.grade) === Number(grade);
    });
    const name = String(match?.grade_name || "").trim();
    return name ? "GL " + grade + " (" + name + ")" : "GL " + grade;
  };

  const ownerLine = function (ownerId: string, role: string) {
    const cap = capacityById.get(ownerId);
    const gradeName = (orgGrades || []).find(function (item) {
      return Number(item.grade) === Number(cap?.grade);
    })?.grade_name;
    return formatOwnerGradeLine(role, cap?.grade, gradeName);
  };

  const capacityById = useMemo(
    function () {
      return new Map(
        ownerCapacities.map(function (c) {
          return [c.owner_id, c] as [string, OwnerCapacity];
        })
      );
    },
    [ownerCapacities]
  );

  const stats = useMemo(
    function () {
      const built = owners
        .map(function (owner) {
          return buildOwnerStats(
            owner.id,
            allRows,
            capacityById.get(owner.id),
            {
              name: owner.name,
              role: owner.role,
              onRoster: owner.onRoster,
            }
          );
        })
        .filter(function (s) {
          return s.active.length > 0 || s.dormantOpen.length > 0;
        });
      return sortOwnerStats(built, ownersSort);
    },
    [owners, allRows, capacityById, ownersSort]
  );

  const accountNameOf = function (id: string) {
    return (
      accounts.find(function (a) {
        return a.account_id === id;
      })?.name || id
    );
  };
  const scopeName = scopeAccountId ? accountNameOf(scopeAccountId) : "";
  const summaryCols = OWNER_SUM_COLS.map(function (col) {
    if (scopeAccountId && col.key === "highest") {
      return {
        ...col,
        label: "Load in " + scopeName,
        sub: "active / account capacity",
      };
    }
    return col;
  });
  const groupOwners = Boolean(scopeAccountId) || ownersFocus === "capacity";
  const ownerCapacityBand = function (s: {
    departed: boolean;
    localCap: number | null;
    global: number;
    globalCap: number | null;
    overGlobal: boolean;
    overCount: number;
    perAccount: { id: string; load: number }[];
  }) {
    if (!groupOwners) return "all";
    if (s.departed) return "departed";
    if (scopeAccountId) {
      const load =
        s.perAccount.find(function (p) {
          return p.id === scopeAccountId;
        })?.load || 0;
      if (s.localCap != null && load > s.localCap) return "over";
      if (s.localCap != null && s.localCap > 0 && load >= s.localCap * 0.8)
        return "near";
      return "within";
    }
    if (s.overGlobal || s.overCount > 0) return "over";
    const nearGlobal =
      s.globalCap != null && s.globalCap > 0 && s.global >= s.globalCap * 0.8;
    const nearLocal = s.perAccount.some(function (p) {
      return (
        s.localCap != null &&
        s.localCap > 0 &&
        p.load > 0 &&
        p.load >= s.localCap * 0.8 &&
        p.load <= s.localCap
      );
    });
    if (nearGlobal || nearLocal) return "near";
    return "within";
  };

  // Busiest-first account columns for full grid.
  const gridAccountIds = useMemo(
    function () {
      const scopeAccounts = new Set(
        rows
          .filter(function (r) {
            return r.owner_id;
          })
          .map(function (r) {
            return r.account_id;
          })
      );
      return accountColumns
        .filter(function (a) {
          return scopeAccounts.has(a.id);
        })
        .map(function (a, i) {
          let over = 0;
          let load = 0;
          stats.forEach(function (s) {
            if (s.departed) return;
            const n = fanOutFor(s.ownerId, a.id, allRows);
            load += n;
            if (s.localCap != null && n > s.localCap) over += 1;
          });
          return { id: a.id, name: a.name, i: i, over: over, load: load };
        })
        .sort(function (a, b) {
          return b.over - a.over || b.load - a.load || a.i - b.i;
        });
    },
    [accountColumns, rows, stats, allRows]
  );

  const gridOwners = useMemo(
    function () {
      if (!ownersGridSort) return stats;
      const key = ownersGridSort;
      return stats.slice().sort(function (a, b) {
        if (a.departed !== b.departed)
          return Number(a.departed) - Number(b.departed);
        if (key === "total") return b.ratio - a.ratio;
        const ba = fanOutFor(b.ownerId, key, allRows);
        const aa = fanOutFor(a.ownerId, key, allRows);
        return ba - aa;
      });
    },
    [stats, ownersGridSort, allRows]
  );

  useEffect(
    function () {
      if (ownersMode !== "grid" || !ownersHighlight) return;
      const el = highlightRef.current;
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    },
    [ownersMode, ownersHighlight, gridOwners.length]
  );

  if (!owners.length) {
    return (
      <p className="text-sm text-gray-500">
        No owners match the account, contact-role, and owner scope.
      </p>
    );
  }

  const gridTemplate =
    "minmax(180px,220px) 110px repeat(" +
    String(gridAccountIds.length) +
    ",minmax(90px,1fr))";

  const ungradedOwners = ownerCapacities.filter(function (cap) {
    return cap.grade == null || !Number.isFinite(Number(cap.grade));
  });
  const ungradedPreview = ungradedOwners
    .map(function (cap) {
      return String(cap.name || "").trim() || "Unnamed owner";
    })
    .filter(function (name, index, list) {
      return list.indexOf(name) === index;
    })
    .slice(0, 5);
  const ungradedExtra = Math.max(
    0,
    ungradedOwners.length - ungradedPreview.length
  );
  const portfolioLimit =
    (capacityByGrade[0] && capacityByGrade[0].global_capacity) ||
    (ownerCapacities[0] && ownerCapacities[0].global_capacity) ||
    20;

  const capacityModal = (
    <CommonModal
      isOpen={capacitySettingsOpen}
      toggle={onCloseCapacitySettings}
      usePortal
      size="lg"
      title="Capacity settings (by Grade Level)"
      subtitle="Read-only · shared with My Relationships"
    >
      <div className="flex flex-col gap-3 p-2">
        <p className="text-xs text-gray-600">
          Recommended limits are soft: going over shows a warning and never
          blocks an assignment. Each grade has its own per-account limit.
          Portfolio load uses one org-wide Fan-out (over-all) number for every
          grade.
        </p>
        {ungradedOwners.length ? (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950"
            role="status"
          >
            <strong>
              {ungradedOwners.length} owner
              {ungradedOwners.length === 1 ? "" : "s"} with no grade
            </strong>
            {" — "}
            per-account Fan-Out uses the default of{" "}
            <strong>{DEFAULT_FAN_OUT_CAP}</strong>. Portfolio limit still
            applies ({portfolioLimit}).
            {ungradedPreview.length ? (
              <div className="mt-1 text-amber-900/80">
                {ungradedPreview.join(", ")}
                {ungradedExtra > 0 ? " · +" + ungradedExtra + " more" : ""}
              </div>
            ) : null}
          </div>
        ) : null}
        <table className="min-w-full text-left text-xs">
          <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Grade</th>
              <th className="px-3 py-2">Per-account</th>
            </tr>
          </thead>
          <tbody>
            {(capacityByGrade.length
              ? capacityByGrade
              : [
                  { grade: 1, local_capacity: 8, global_capacity: 20 },
                  { grade: 2, local_capacity: 8, global_capacity: 20 },
                  { grade: 3, local_capacity: 8, global_capacity: 20 },
                  { grade: 4, local_capacity: 8, global_capacity: 20 },
                  { grade: 5, local_capacity: 8, global_capacity: 20 },
                ]
            ).map(function (gc) {
              return (
                <tr key={gc.grade} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {gradeLevelLabel(gc.grade)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {gc.local_capacity}
                  </td>
                </tr>
              );
            })}
            {ungradedOwners.length ? (
              <tr className="border-t border-amber-100 bg-amber-50/60">
                <td className="px-3 py-2 font-medium text-amber-950">
                  No grade (default)
                </td>
                <td className="px-3 py-2 text-amber-950">
                  {DEFAULT_FAN_OUT_CAP}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <p className="text-xs text-gray-800">
          Portfolio limit (all grades): <strong>{portfolioLimit}</strong>
        </p>
      </div>
    </CommonModal>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Owner workload by account
            </h3>
            <p className="text-xs text-gray-500">
              Active relationships compared with recommended capacity
              {" · "}
              <button
                type="button"
                className="font-medium text-[#974ae4] hover:underline"
                onClick={onOpenCapacitySettings}
              >
                Capacity settings (by GL)
              </button>
            </p>
          </div>
        </div>

        <TabsComponent
          tabs={[
            { id: "summary", label: "Summary" },
            { id: "grid", label: "Full grid" },
          ]}
          activeTab={ownersMode}
          onTabChange={function (id: OwnersMode) {
            onSetMode(id);
          }}
          variant="tab-underline"
          className="mb-3"
        />

        {!capacityConfigured ? (
          <p className="mb-3 text-sm text-amber-800">
            Owner capacity is not configured, so fan-out is shown without a
            recommended limit.
          </p>
        ) : null}

        {ownersMode === "summary" ? (
          <>
            <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <strong className="text-gray-800">How to read this:</strong> one
              row per owner. Portfolio load shows active relationships /
              recommended capacity across all accounts; highest per-account load
              is the busiest single account against its account capacity. Owners
              are sorted worst first (over portfolio capacity, then accounts
              over limit, then load); click a header to re-sort. Click any
              number to open the owner&apos;s read-only portfolio already
              filtered to it, or View in grid to see the owner across every
              account.
              {ownersSort.key !== "worst" ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="font-medium text-[#974ae4] hover:underline"
                    onClick={function () {
                      onSort("worst");
                    }}
                  >
                    Reset to worst first
                  </button>
                </>
              ) : null}
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    {summaryCols.map(function (col) {
                      const on = ownersSort.key === col.key;
                      return (
                        <th
                          key={col.key}
                          className="px-3 py-2"
                          aria-sort={
                            on
                              ? ownersSort.dir > 0
                                ? "ascending"
                                : "descending"
                              : undefined
                          }
                        >
                          <button
                            type="button"
                            className={
                              "text-left hover:text-gray-900 " +
                              (on ? "text-gray-900" : "")
                            }
                            title={"Sort by " + col.label.toLowerCase()}
                            onClick={function () {
                              onSort(col.key);
                            }}
                          >
                            {col.label}
                            {col.sub ? (
                              <div className="font-normal normal-case text-gray-400">
                                {col.sub}
                              </div>
                            ) : null}
                          </button>
                        </th>
                      );
                    })}
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(groupOwners
                    ? (["over", "near", "within", "departed"] as const)
                    : (["all"] as const)
                  ).map(function (band) {
                    const list =
                      band === "all"
                        ? stats
                        : stats.filter(function (s) {
                            return ownerCapacityBand(s) === band;
                          });
                    if (band !== "all" && !list.length && band !== "near")
                      return null;
                    return (
                      <React.Fragment key={band}>
                        {band !== "all" ? (
                          <tr className="border-t border-gray-200 bg-gray-50">
                            <td
                              colSpan={9}
                              className="px-3 py-2 text-xs font-semibold text-gray-800"
                            >
                              {band === "over"
                                ? "Over limit"
                                : band === "near"
                                  ? "Near limit"
                                  : band === "within"
                                    ? "Within limit"
                                    : "Departed"}
                              {" (" + list.length + ")"}
                              {band === "near"
                                ? " · at 80% or more of the account limit"
                                : ""}
                              {scopeName && band !== "departed"
                                ? " · capacity in " + scopeName
                                : ""}
                            </td>
                          </tr>
                        ) : null}
                        {list.length || band === "all"
                          ? list.map(function (s) {
                              if (s.departed) {
                                const n = s.dormantOpen.length;
                                return (
                                  <tr
                                    key={s.ownerId}
                                    className="border-t border-gray-100 bg-amber-50/60"
                                  >
                                    <td className="px-3 py-2">
                                      <button
                                        type="button"
                                        className="font-semibold text-gray-900 hover:text-[#974ae4] hover:underline"
                                        onClick={function () {
                                          onOpenPortfolio(s.ownerId);
                                        }}
                                      >
                                        {s.name}
                                      </button>
                                      <div className="text-[10px] text-gray-500">
                                        {ownerLine(s.ownerId, s.role)} ·
                                        Departed
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">
                                      {s.accountIds.length}
                                    </td>
                                    <td className="px-3 py-2">
                                      <button
                                        type="button"
                                        className="text-[#974ae4] hover:underline"
                                        onClick={function () {
                                          onOpenPortfolio(s.ownerId);
                                        }}
                                      >
                                        {n} open relationship
                                        {n === 1 ? "" : "s"}
                                      </button>
                                    </td>
                                    <td className="px-3 py-2" colSpan={5}>
                                      {canManageDepartureImpact ? (
                                        <button
                                          type="button"
                                          className="font-medium text-[#974ae4] hover:underline"
                                          onClick={function () {
                                            onDepartureImpact(s.ownerId);
                                          }}
                                        >
                                          Review departure impact →
                                        </button>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2">
                                      <button
                                        type="button"
                                        className="text-[#974ae4] hover:underline"
                                        onClick={function () {
                                          onViewInGrid(s.ownerId);
                                        }}
                                      >
                                        View in grid →
                                      </button>
                                    </td>
                                  </tr>
                                );
                              }
                              const scopedLoad = scopeAccountId
                                ? s.perAccount.find(function (p) {
                                    return p.id === scopeAccountId;
                                  })
                                : null;
                              const h = scopeAccountId
                                ? {
                                    id: scopeAccountId,
                                    load: scopedLoad?.load || 0,
                                  }
                                : s.highest;
                              const hName = h ? accountNameOf(h.id) : "—";
                              const numLink = function (
                                n: number,
                                filter: OwnersPortfolioFilter,
                                className?: string
                              ) {
                                if (!n) {
                                  return (
                                    <span className="text-gray-300">0</span>
                                  );
                                }
                                return (
                                  <button
                                    type="button"
                                    className={
                                      "font-medium hover:underline " +
                                      (className || "text-[#974ae4]")
                                    }
                                    onClick={function () {
                                      onOpenPortfolio(s.ownerId, null, filter);
                                    }}
                                  >
                                    {n}
                                  </button>
                                );
                              };
                              return (
                                <tr
                                  key={s.ownerId}
                                  className="border-t border-gray-100"
                                >
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      className="font-semibold text-gray-900 hover:text-[#974ae4] hover:underline"
                                      onClick={function () {
                                        onOpenPortfolio(s.ownerId);
                                      }}
                                    >
                                      {s.name}
                                    </button>
                                    <div className="text-[10px] text-gray-500">
                                      {ownerLine(s.ownerId, s.role)}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    {s.accountIds.length}
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      className={
                                        "font-medium hover:underline " +
                                        (s.overGlobal
                                          ? "text-red-700"
                                          : "text-[#974ae4]")
                                      }
                                      onClick={function () {
                                        onOpenPortfolio(s.ownerId);
                                      }}
                                    >
                                      {s.global} / {s.globalCap ?? "—"}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2">
                                    {numLink(
                                      s.overCount,
                                      "over",
                                      "text-red-700"
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {h ? (
                                      <button
                                        type="button"
                                        className={
                                          "hover:underline " +
                                          (s.localCap != null &&
                                          h.load > s.localCap
                                            ? "font-semibold text-red-700"
                                            : "text-[#974ae4]")
                                        }
                                        onClick={function () {
                                          onOpenPortfolio(s.ownerId, h.id);
                                        }}
                                      >
                                        {hName} {h.load} / {s.localCap ?? "—"}
                                      </button>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {numLink(s.only, "only")}
                                  </td>
                                  <td
                                    className="px-3 py-2"
                                    title={
                                      "Relationships on contacts with " +
                                      FAN_IN_THRESHOLD +
                                      " or more current owners"
                                    }
                                  >
                                    {s.overCovered ? (
                                      numLink(
                                        s.overCovered,
                                        "overCovered",
                                        "text-[#974ae4]"
                                      )
                                    ) : (
                                      <span className="text-gray-300">0</span>
                                    )}
                                    {s.overCovered ? (
                                      <span className="text-gray-500">
                                        {" "}
                                        of {s.global}
                                      </span>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-2">
                                    {numLink(s.unassessed, "unassessed")}
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      type="button"
                                      className="text-[#974ae4] hover:underline"
                                      onClick={function () {
                                        onViewInGrid(s.ownerId);
                                      }}
                                    >
                                      View in grid →
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <strong className="text-gray-800">How to read this:</strong>{" "}
              Portfolio total (first column) shows all active relationships /
              global capacity; each account cell shows active relationships /
              recommended capacity for that account. Accounts are ordered
              busiest first; click a column header to sort owners by it. Click a
              person or cell to open their read-only portfolio; departure impact
              planning is an action inside it, and capacity warnings do not
              block the plan.
            </p>
            <div className="overflow-x-auto table-horizontal-scroll relative isolate">
              <div
                className="grid min-w-max gap-px rounded-md border border-gray-200 bg-gray-200"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <div className="sticky left-0 z-[3] bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)]">
                  Owner
                </div>
                <button
                  type="button"
                  className={
                    "bg-gray-50 px-2 py-2 text-center text-xs font-semibold text-gray-700 hover:bg-gray-100 " +
                    (ownersGridSort === "total" ? "ring-1 ring-violet-300" : "")
                  }
                  title="Sort owners by portfolio load"
                  onClick={function () {
                    onGridSort("total");
                  }}
                >
                  Portfolio total
                  <div className="font-normal text-gray-400">
                    active / global capacity
                  </div>
                </button>
                {gridAccountIds.map(function (account) {
                  return (
                    <button
                      key={account.id}
                      type="button"
                      className={
                        "bg-gray-50 px-2 py-2 text-center text-xs font-semibold text-gray-700 hover:bg-gray-100 " +
                        (ownersGridSort === account.id
                          ? "ring-1 ring-violet-300"
                          : "")
                      }
                      title={"Sort owners by their load in " + account.name}
                      onClick={function () {
                        onGridSort(account.id);
                      }}
                    >
                      <div className="truncate" title={account.name}>
                        {account.name}
                      </div>
                      <div className="font-normal text-gray-400">
                        active / capacity
                      </div>
                    </button>
                  );
                })}

                {gridOwners.map(function (s) {
                  const hl = ownersHighlight === s.ownerId;
                  const ownerCellBg = s.departed ? "bg-amber-50" : "bg-white";
                  const hlBox = hl
                    ? "outline outline-2 outline-violet-400 -outline-offset-1"
                    : "";
                  return (
                    <React.Fragment key={s.ownerId}>
                      <div
                        ref={hl ? highlightRef : undefined}
                        className={
                          "sticky left-0 z-[2] px-3 py-2 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] " +
                          ownerCellBg +
                          " " +
                          hlBox
                        }
                      >
                        <button
                          type="button"
                          className="text-left text-sm font-semibold text-gray-900 hover:text-[#974ae4] hover:underline"
                          title={"Open " + s.name + "'s read-only portfolio"}
                          onClick={function () {
                            onOpenPortfolio(s.ownerId);
                          }}
                        >
                          {s.name}
                        </button>
                        <div className="text-xs text-gray-500">
                          {ownerLine(s.ownerId, s.role)}
                          {" \u00b7 "}
                          {s.departed
                            ? "Departed · view open relationships"
                            : "View portfolio"}
                        </div>
                        {s.onRoster && !s.departed ? (
                          <button
                            type="button"
                            className="mt-1 text-xs font-medium text-[#974ae4] hover:underline"
                            onClick={function (e) {
                              e.stopPropagation();
                              onOpenTeamViews({
                                id: s.ownerId,
                                name: s.name,
                              });
                            }}
                          >
                            Manage account roles →
                          </button>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        title="Open full owner portfolio"
                        className={
                          "flex items-center justify-center px-2 py-2 text-sm transition hover:bg-violet-50 " +
                          (s.overGlobal
                            ? "bg-red-50 font-semibold text-red-700"
                            : s.departed
                              ? "bg-amber-50 text-gray-800"
                              : "bg-white text-gray-800") +
                          " " +
                          hlBox
                        }
                        onClick={function () {
                          onOpenPortfolio(s.ownerId);
                        }}
                      >
                        {String(s.global) +
                          " / " +
                          (s.globalCap != null ? String(s.globalCap) : "—")}
                      </button>
                      {gridAccountIds.map(function (account, i) {
                        const n = fanOutFor(s.ownerId, account.id, allRows);
                        const over =
                          !s.departed && s.localCap != null && n > s.localCap;
                        const isLast = hl && i === gridAccountIds.length - 1;
                        return (
                          <button
                            key={s.ownerId + ":" + account.id}
                            type="button"
                            title="Open portfolio for this account"
                            className={
                              "flex items-center justify-center px-2 py-2 text-sm transition hover:bg-violet-50 " +
                              (over
                                ? "bg-red-50 font-semibold text-red-700"
                                : "bg-white text-gray-800") +
                              " " +
                              (hl ? hlBox : "") +
                              (isLast ? " rounded-r-md" : "")
                            }
                            onClick={function () {
                              onOpenPortfolio(s.ownerId, account.id);
                            }}
                          >
                            {n
                              ? String(n) +
                                " / " +
                                (s.localCap != null ? String(s.localCap) : "—")
                              : "—"}
                          </button>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {capacityModal}
    </div>
  );
}

/* ================================================================== */
/*  FunnelRow — shared alignment funnel display                        */
/* ================================================================== */

function FunnelRow(props: { funnel: ReturnType<typeof alignmentFunnel> }) {
  const f = props.funnel;
  const steps: {
    title: string;
    value: number;
    subtitle: string;
    borderType: "blue" | "purple" | "orange" | "yellow" | "green" | "red";
    tip: string;
  }[] = [
    {
      title: "Contacts",
      value: f.total,
      subtitle: "Known contacts on this account",
      borderType: "blue",
      tip:
        "Distinct contacts on this account in the coverage portfolio (contact grain). Currently " +
        f.total +
        ". This is the funnel denominator — every later stage is a subset of these contacts.",
    },
    {
      title: "Mapped",
      value: f.mapped,
      subtitle: "With an active owner",
      borderType: "purple",
      tip:
        "Contacts with at least one active owner relationship mapping. Currently " +
        f.mapped +
        " of " +
        f.total +
        ". Unmapped / dormant-only contacts are not counted here.",
    },
    {
      title: "Assessed",
      value: f.assessed,
      subtitle: "Active mapping with RL",
      borderType: "orange",
      tip:
        "Mapped contacts that have an RL assessment on an active mapping (rl is set). Currently " +
        f.assessed +
        " of " +
        f.mapped +
        " mapped. Covered but not yet assessed stays out of this stage.",
    },
    {
      title: "Goaled",
      value: f.goaled,
      subtitle: "Target RL set",
      borderType: "yellow",
      tip:
        "Mapped contacts with a goal target RL on an active relationship (goal_target_rl is set). Currently " +
        f.goaled +
        ". Comes from Relationship goal data, not a separate Alignment screen.",
    },
    {
      title: "Progressing",
      value: f.progressing,
      subtitle: "Goal in progress",
      borderType: "green",
      tip:
        "Mapped contacts whose goal status is in-progress. Currently " +
        f.progressing +
        ". Requires an active mapping with goal_status = in-progress.",
    },
    {
      title: "Achieved",
      value: f.achieved,
      subtitle: "Goal achieved",
      borderType: "green",
      tip:
        "Mapped contacts whose goal status is achieved. Currently " +
        f.achieved +
        ". Requires an active mapping with goal_status = achieved.",
    },
  ];
  return (
    <div className="grid grid-cols-2 items-stretch gap-2 sm:grid-cols-3 xl:grid-cols-6">
      {steps.map(function (step) {
        return (
          <div key={step.title} className="h-full min-w-0">
            <MetricCard
              title={step.title}
              value={step.value}
              subtitle={step.subtitle}
              borderType={step.borderType}
              className="h-full"
              info={<MetricInfo label={step.title} text={step.tip} />}
            />
          </div>
        );
      })}
    </div>
  );
}

export default CoverageWorkspace;
